/**
 * English (en) translation dictionary for KinShield.
 *
 * Keys map to the translated string. Keep keys stable and human-readable.
 * For new translatable strings, add the key here AND in es.js.
 *
 * Pattern: prefer dotted namespaces (e.g. "settings.title") to keep things
 * organized. The translation function `t("settings.title")` will fall back to
 * the key itself if a translation is missing, so partial coverage is safe.
 */
const en = {
  // ── Common / shared ────────────────────────────────────────────────
  "common.backTo": "Back to",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.loading": "Loading...",
  "common.refresh": "Refresh",
  "common.refreshing": "Refreshing...",
  "common.enabled": "Enabled",
  "common.disabled": "Disabled",
  "common.configured": "Configured",
  "common.notAvailableDemo": "Not Available in Demo Mode",
  "common.demoMode": "Demo Mode",
  "common.comingSoon": "Coming Soon",
  "common.signUp": "Sign Up",
  "common.logIn": "Sign In",
  "common.signOut": "Sign Out",

  // ── Edition (consumer vs corporate) terminology ────────────────────
  // Resolved via EditionContext.term(key) as edition.<consumer|corporate>.<key>
  "edition.consumer.account": "Family Account",
  "edition.consumer.member": "Family Member",
  "edition.consumer.members": "Family Members",
  "edition.consumer.owner": "Account Owner",
  "edition.consumer.group": "Group",
  "edition.consumer.groups": "Groups",
  "edition.consumer.addMember": "Add Family Member",
  "edition.consumer.plansTitle": "Choose your plan",
  "edition.corporate.account": "Company Account",
  "edition.corporate.member": "Team Member",
  "edition.corporate.members": "Team Members",
  "edition.corporate.owner": "Administrator",
  "edition.corporate.group": "Team",
  "edition.corporate.groups": "Teams",
  "edition.corporate.addMember": "Add Team Member",
  "edition.corporate.plansTitle": "Choose your business plan",

  // ── Registration / account-type selection ──────────────────────────
  "register.step": "Step {current} of {total}",
  "register.accountType.title": "How will you use KinShield?",
  "register.accountType.subtitle": "Choose the option that best fits you.",
  "register.accountType.selected": "Account type:",
  "register.accountType.change": "Change",
  "register.accountType.individual": "Individual and Family",
  "register.accountType.individualDesc":
    "Protect yourself and your household from scams and fraud.",
  "register.accountType.business": "Business and Team",
  "register.accountType.businessDesc":
    "Protect your employees with security awareness training.",
  "register.companyName": "Company Name",
  "register.companyNamePlaceholder": "e.g., Acme Inc.",
  "register.companyAdminEmailHelp":
    "This is the Company Account Admin's email, who will manage your organization's KinShield account.",
  "register.companyMeta.industry": "Industry",
  "register.companyMeta.industryPlaceholder": "Select your industry",
  "register.companyMeta.employeeCount": "Number of Employees",
  "register.companyMeta.employeeCountPlaceholder": "e.g., 25",

  // ── Company profile (corporate account owner self-service) ──────────
  "company.profile.title": "Company Profile",
  "company.profile.description":
    "View and update your company's details (account owner only).",
  "company.profile.companyName": "Company Name",
  "company.profile.descriptionLabel": "Description",
  "company.profile.descriptionPlaceholder":
    "A short description of your company (optional)",
  "company.profile.industry": "Industry",
  "company.profile.industryPlaceholder": "Select your industry",
  "company.profile.employeeCount": "Number of Employees",
  "company.profile.employeeCountPlaceholder": "e.g., 25",
  "company.profile.employeeCountHelp":
    "Your company's total headcount. This describes your business and may differ from the number of KinShield seats on your plan. Used for your cyber-insurance readiness report.",
  "company.profile.companySize": "Company Size Band",
  "company.profile.disableEmailAnalysis": "Disable Inbound Email Analysis",
  "company.profile.disableEmailAnalysisHelp":
    "When enabled, emails forwarded to KinShield by members of your organization are not analyzed and are not sent to any AI provider. Turn this on to disable the email-forwarding scam-analysis feature organization-wide.",
  "company.profile.disableEmailAnalysisOn": "Disabled",
  "company.profile.disableEmailAnalysisOff": "Enabled",
  "company.profile.ownerOnlyNote":
    "Only the account owner can update the company profile.",
  "company.profile.editButton": "Edit Company Profile",
  "company.profile.saveButton": "Save Changes",
  "company.profile.saving": "Saving...",
  "company.profile.cancelButton": "Cancel",
  "company.profile.saveSuccess": "Company profile updated successfully.",
  "company.profile.errorNameRequired": "Company name is required.",
  "company.profile.errorEmployeeCount":
    "Number of employees must be a whole number of at least 1.",
  "company.profile.errorSave":
    "Failed to update company profile. Please try again.",

  // ── Account Profile page (account owner: Account + Company + User) ──
  "accountProfile.title": "Account Profile",
  "accountProfile.subtitle":
    "Manage your account, company, and personal profile in one place",
  "accountProfile.menuLabel": "Account Profile",
  "accountProfile.account.title": "Account",
  "accountProfile.account.description":
    "Your account identity, ownership, and usage at a glance",
  "accountProfile.account.accountName": "Account Name",
  "accountProfile.account.accountId": "Account ID",
  "accountProfile.account.accountIdHelp":
    "Reference this Account ID when you contact support.",
  "accountProfile.account.accountType": "Account Type",
  "accountProfile.account.typeBusiness": "Business (Corporate)",
  "accountProfile.account.typeIndividual": "Individual / Family",
  "accountProfile.account.owner": "Account Admin",
  "accountProfile.account.ownerYou": "You",
  "accountProfile.account.status": "Status",
  "accountProfile.account.statusActive": "Active",
  "accountProfile.account.statusInactive": "Inactive",
  "accountProfile.account.statusDelinquent": "Payment overdue",
  "accountProfile.account.memberSince": "Member Since",
  "accountProfile.account.seats": "Seats in Use",
  "accountProfile.account.seatsOf": "of",
  "accountProfile.account.manageMembers": "Manage members",
  "accountProfile.account.plan": "Current Plan",
  "accountProfile.account.manageBilling": "Manage in Subscription & Payment",
  "accountProfile.account.notProvided": "Not provided",
  "accountProfile.userProfile.title": "User Profile",
  "accountProfile.userProfile.description":
    "View and update your personal profile",
  "accountProfile.userMgmt.title": "User Management",
  "accountProfile.userMgmt.description":
    "View, add, and manage the members of your account",

  // ── Theme labels ───────────────────────────────────────────────────
  "theme.light": "Light Theme",
  "theme.dark": "Dark Theme",
  "theme.auto": "Auto Theme",
  "theme.switchBack": "Switch back to your account",
  "header.theme.light": "Light",
  "header.theme.dark": "Dark",
  "header.theme.auto": "Auto",
  "header.lang.english": "English",
  "header.lang.spanish": "Spanish",
  "header.lang.toggleTitle": "Toggle language",

  // ── Settings page ──────────────────────────────────────────────────
  "settings.title": "Settings",
  "settings.subtitle": "Manage your account preferences and security settings",
  "settings.stats.mfaStatus": "MFA Status",
  "settings.stats.theme": "Theme",
  "settings.stats.notifications": "Notifications",
  "settings.stats.language": "Language",
  "settings.section.security.title": "Account Security",
  "settings.section.security.subtitle":
    "Multi-factor authentication, password, and login security",
  "settings.section.notifications.title": "Notifications & Announcements",
  "settings.section.notifications.subtitle":
    "Email, SMS, in-app notifications, and announcement preferences",
  "settings.section.appearance.title": "Appearance & Display",
  "settings.section.appearance.subtitle":
    "Theme, dashboard layout, and interface preferences",
  "settings.section.localization.title": "Language & Region",
  "settings.section.localization.subtitle":
    "Language, timezone, and date format preferences",
  "settings.section.privacy.title": "Privacy & Sharing",
  "settings.section.privacy.subtitle":
    "Control data sharing, profile visibility, and account owner access",
  "settings.section.organization.title": "Organization",
  "settings.section.organization.subtitle":
    "Organization-wide controls for account owners",

  // ── Organization sub-section ─────────────────────────────────
  "organization.dataGovernance.heading": "Data Governance",
  "organization.disableEmailAnalysis.title": "Disable Inbound Email Analysis",
  "organization.disableEmailAnalysis.desc":
    "When enabled, emails forwarded to KinShield by members of your organization are not analyzed and are not sent to any AI provider. Turn this on to disable the email-forwarding scam-analysis feature organization-wide.",
  "organization.error.load":
    "Failed to load organization settings. Please try again later.",
  "organization.error.save":
    "Failed to update organization settings. Please try again.",

  // ── Localization sub-section ───────────────────────────────────────
  "localization.heading": "Language & Region",
  "localization.language": "Language",
  "localization.timezone": "Timezone",
  "localization.dateFormat": "Date Format",
  "localization.lang.enUS": "English (US)",
  "localization.lang.enGB": "English (UK)",
  "localization.lang.es": "Spanish",
  "localization.lang.fr": "French",
  "localization.lang.de": "German",
  "localization.tz.eastern": "Eastern Time (ET)",
  "localization.tz.central": "Central Time (CT)",
  "localization.tz.mountain": "Mountain Time (MT)",
  "localization.tz.pacific": "Pacific Time (PT)",

  // ── Privacy & Sharing sub-section ─────────────────────────────────
  "privacy.ownerAccess.heading": "Account Owner Access",
  "privacy.ownerAccess.desc":
    "Control whether your account owner can view your dashboard and analysis results in read-only mode.",
  "privacy.ownerAccess.error.load": "Failed to load consent settings",
  "privacy.ownerAccess.error.save":
    "Failed to save consent preference. Please try again.",
  "privacy.ownerAccess.dashboard.title": "Allow Dashboard Viewing",
  "privacy.ownerAccess.dashboard.desc":
    "Let the account owner view your dashboard and safety scores in read-only mode",
  "privacy.ownerAccess.dashboard.ariaLabel":
    "Allow account owner to view your dashboard",
  "privacy.ownerAccess.analysis.title": "Allow Analysis Results Viewing",
  "privacy.ownerAccess.analysis.desc":
    "Let the account owner view your scam detection analysis results (email, image, SMS, social media, URL) in read-only mode",
  "privacy.ownerAccess.analysis.ariaLabel":
    "Allow account owner to view your analysis results",
  "privacy.ownerAccess.reports.title": "Allow Weekly Progress Reports Viewing",
  "privacy.ownerAccess.reports.desc":
    "Let the account owner view your weekly Shield progress reports in read-only mode",
  "privacy.ownerAccess.reports.ariaLabel":
    "Allow account owner to view your weekly progress reports",
  "privacy.dataSharing.heading": "Data Sharing",
  "privacy.dataSharing.analytics.title": "Usage Analytics",
  "privacy.dataSharing.analytics.desc":
    "Help improve our service by sharing anonymous usage data",
  "privacy.dataSharing.marketing.title": "Marketing Communications",
  "privacy.dataSharing.marketing.desc":
    "Receive updates about new features and services",
  "privacy.profileVisibility.heading": "Profile Visibility",
  "privacy.profileVisibility.label": "Profile Visibility",
  "privacy.profileVisibility.private":
    "Private - Only you can see your profile",
  "privacy.profileVisibility.team": "Team - Visible to your organization",
  "privacy.profileVisibility.public": "Public - Visible to all users",

  // ── SidePanel / Navigation menu items ──────────────────────────────
  // NOTE: The keys below intentionally match the `name` strings used in
  // SidePanel.js so that `t(item.name)` works for display while routing
  // logic continues to use the original English `name` as an identifier.
  "Member User Info": "Member User Info",
  "Groups & Organizations": "Groups & Organizations",
  "Resend Verification Key": "Resend Verification Key",
  "Account & Billing": "Subscription & Payment",
  "Simulation Profile": "Simulation Profile",
  "Simulation Messages": "Simulation Messages",
  "Recent Simulations": "Recent Simulations",
  "Shield Reports": "Shield Reports",
  "Cyber-Insurance Readiness": "Cyber-Insurance Readiness",
  "Export Data": "Export Data",
  "Admin Dashboard": "Admin Dashboard",
  "Scam Reports": "Scam Reports",
  "Send Email": "Send Email",
  "Send SMS": "Send SMS",
  "Generate Blogs": "Generate Blogs",
  "Generate Simulation Data": "Generate Simulation Data",
  "Manage Announcements": "Manage Announcements",
  "Create Simulation Schedule": "Create Simulation Schedule",
  "Execute Simulation Schedule": "Execute Simulation Schedule",
  "Demo Users": "Demo Users",
  "Event Demo Seats": "Event Demo Seats",
  "Forwarded Emails": "Forwarded Emails",
  "Manual Entry": "Manual Entry",
  "Forwarded Texts": "Forwarded Texts",
  "Manual Input": "Manual Input",
  "Text Conversation": "Text Conversation",
  "Business/Person Check": "Business/Person Check",
  "Social Media Posts": "Social Media Posts",
  "Verify a Picture": "Verify a Picture",
  "Check a Phone Number": "Check a Phone Number",
  "Training Center": "Training Center",
  "Alerts Intelligence Hub": "Alerts Intelligence Hub",
  "Quick Scan": "Quick Scan",
  "Screenshot Analysis": "Screenshot Analysis",
  "Phone Number Check": "Phone Number Check",
  "QR Code Check": "QR Code Check",

  // SidePanel section group headers (used in JSX, see SidePanel.js)
  "nav.section.scamDetector": "Scam Detector",
  "nav.section.email": "Email Analysis",
  "nav.section.text": "Text Analysis",
  "nav.section.social": "Social Protection",
  "nav.section.moreTools": "More Tools",
  "nav.section.user": "User",
  "nav.section.account": "Account",
  "nav.section.simulations": "Simulations",
  "nav.section.reports": "Reports",
  "nav.section.platformAdmin": "Platform Admin",
  "nav.section.demo": "Demo",
  "nav.section.education": "Resources",

  // SidePanel literal-string keys (used as t("...") with English label as key)
  "User Management": "User Management",
  "Scam Detector": "Scam Detector",
  "Email Analysis": "Email Analysis",
  "Text/SMS Analysis": "Text/SMS Analysis",
  "Social Media": "Social Media",
  "More Tools": "More Tools",
  Simulations: "Simulations",
  "Trainings & Alerts": "Trainings & Alerts",
  Account: "Account",
  Reports: "Reports",
  "Platform Admin": "Platform Admin",
  Demo: "Demo",
  "nav.signInPrompt": "Sign in to see menus",

  // ── Header (desktop & mobile) ──────────────────────────────────────
  "header.backToMain": "Back to Main Site",
  "header.dashboard": "Dashboard",
  "header.viewingAs": "Viewing as:",
  "header.switchBack": "Switch back",
  "header.users": "User Profile",
  "header.userProfile": "User Profile",
  "header.activityMonitoring": "User Activity Monitoring",
  "header.gettingStarted": "Getting Started",

  // ── Email Analysis (Forwarded Emails) page ────────────────────────
  "email.page.title": "Check a Suspicious Email",
  "email.page.subtitle":
    "Find out if an email is a scam \u2014 forward it to us or paste it in.",
  "email.page.forwardTo": "Forward suspicious emails to",
  "email.page.forwardSuffix": "and view your automated threat analyses here",
  "email.page.manualLink": "Prefer to copy/paste it?",
  "email.page.manualLinkAction": "Analyze manually",
  "email.disclaimer":
    "The email analysis provided is for educational purposes only. For complete terms and limitations, please review our",

  // Entry panel (how to check an email)
  "email.start.recommended": "Recommended",
  "email.start.forward.title": "Forward it to us",
  "email.start.forward.why":
    "Most accurate \u2014 we can see hidden links and who really sent it.",
  "email.start.forward.step1":
    "Open the suspicious email. Don\u2019t click any links or attachments.",
  "email.start.forward.step2": "Forward it to:",
  "email.start.forward.step3": "Forward it from {email}",
  "email.start.forward.step3Fallback":
    "Forward it from the email address you use to sign in",
  "email.start.forward.step3Detail":
    "That\u2019s how we know it\u2019s yours. Emails forwarded from other addresses can\u2019t be checked.",
  "email.start.forward.helpLabel":
    "Step-by-step for Gmail, Outlook, Apple Mail & Yahoo",
  "email.start.forward.sentBtn": "I\u2019ve just forwarded it",
  "email.start.waiting.title": "Waiting for your email\u2026",
  "email.start.waiting.body":
    "This usually takes under a minute. Your result will open here automatically \u2014 if you leave, we\u2019ll notify you.",
  "email.start.waiting.cancel": "I didn\u2019t send it \u2014 cancel",
  "email.start.timeout.title": "We haven\u2019t received it yet",
  "email.start.timeout.body":
    "Make sure you sent it to {address} from {email}.",
  "email.start.timeout.bodyFallback":
    "Make sure you sent it to {address} from the email address you use to sign in.",
  "email.start.timeout.retry": "Keep waiting",
  "email.start.timeout.dismiss": "Stop waiting",
  "email.start.paste.title": "Copy and paste it in",
  "email.start.paste.why":
    "Get an answer right away. Copy the sender, subject, and message into a short form.",
  "email.start.paste.btn": "Paste an email",
  "email.start.compact.forward": "Check another email \u2014 forward it to",
  "email.start.compact.sentBtn": "I\u2019ve just forwarded one",
  "email.start.compact.or": "or",
  "email.start.compact.paste": "I want to paste it in",

  // Mobile header
  "email.mobile.title": "Email Analysis",
  "email.mobile.subtitle":
    "Forward suspicious emails for automated threat analysis",
  "email.mobile.forwardLabel": "Forward emails to",
  "email.mobile.copied": "Copied!",
  "email.mobile.copy": "Copy",
  "email.mobile.backToList": "Back to List",

  // Results list
  "email.results.heading": "Analysis History",
  "email.results.showing":
    "Showing latest {count} analyses \u2022 Click on any item to view details",
  "email.results.refreshTitle": "Refresh analysis results",

  // Empty states
  "email.empty.title": "No Analyses Yet",
  "email.empty.forwardTo": "Forward a suspicious email to",
  "email.empty.toGetStarted": "to get started.",
  "email.empty.checkResults": "Check for Results",
  "email.empty.analyzeManuallyBtn": "Analyze a message manually",
  "email.empty.noResults.title": "No Analysis Results Found",
  "email.empty.noResults.description":
    "No email analyses match your current search criteria. Forward suspicious emails to security@kinprotect.com and view your automated threat analyses here",

  // Status badges
  "email.badge.scam": "SCAM",
  "email.badge.safe": "SAFE",
  "email.badge.legitimate": "LEGITIMATE",

  // Card / list item labels
  "email.card.from": "From",
  "email.card.fromLabel": "From:",
  "email.card.subject": "Subject",
  "email.card.subjectLabel": "Subject:",
  "email.card.unknown": "Unknown",
  "email.card.noSubject": "No subject",
  "email.card.deleteTitle": "Delete analysis",
  "email.card.delete": "Delete",

  // Detail view
  "email.detail.back": "Back to Results",
  "email.detail.scamDetected": "SCAM DETECTED",
  "email.detail.noThreats": "NO THREATS DETECTED",
  "email.detail.scamSubtitle":
    "This email contains suspicious or fraudulent content",
  "email.detail.safeSubtitle": "This email appears to be legitimate",
  "email.detail.shareIndividual":
    "Available For Family or Extended Family Plans",
  "email.detail.shareFamily": "Share this alert with family members",
  "email.detail.share": "Share",

  // Share button props
  "email.share.threatType": "Email Scam",
  "email.share.threatTitle": "Email Scam Detected",

  // Section headings (desktop detail panel)
  "email.section.analysisDetails": "Analysis Details",
  "email.section.analysisDetailsSubtitle": "What we found about this email",
  "email.section.whatToDo": "What Should You Do?",
  "email.section.riskFactors": "Risk Factors",
  "email.section.explanation": "Explanation",
  "email.section.attachments": "Attachments",
  "email.section.emailAttachments": "Email Attachments",
  "email.section.noAnalysisSelected": "No Analysis Selected",
  "email.section.noAnalysisSelectedDesc":
    "Select an email from the list to view detailed analysis results here.",

  // Attachment labels
  "email.attachment.risk.high": "high",
  "email.attachment.risk.medium": "medium",
  "email.attachment.risk.low": "low",
  "email.attachment.highRisk": "High Risk File Type",
  "email.attachment.mediumRisk": "Medium Risk File Type",
  "email.attachment.lowRisk": "Generally Safe File Type",
  "email.attachment.warningLabel": "Warning:",
  "email.attachment.warningText":
    "Never open attachments from untrusted sources. Executable files (.exe, .bat, .com, .scr) are particularly dangerous and should be treated with extreme caution.",

  // Error & quota messages
  "email.error.tryAgain": "Try Again",
  "email.error.notAuthenticated": "User not authenticated",
  "email.error.loadFailed": "Failed to load email analyses. Please try again.",
  "email.error.notFound": "The requested email analysis could not be found.",
  "email.error.loadNotifFailed": "Failed to load the requested email analysis.",
  "email.error.deleteFailed": "Failed to delete analysis. Please try again.",
  "email.quota.title": "Daily Limit Reached",
  "email.quota.body":
    "You've reached your daily limit of 25 email analyses. Your quota will reset tomorrow.",
  "email.analysisDisabled.title":
    "Email analysis is turned off for your organization",
  "email.analysisDisabled.body":
    "Your account administrator has disabled inbound email-forwarding analysis. Emails you forward to security@kinprotect.com will not be analyzed and are not sent to any AI provider. Contact your administrator if you believe this should be enabled.",

  // ── Manual Email Scan page ─────────────────────────────────────────────
  "manual.page.title": "Manual Email Analysis",
  "manual.page.subtitle":
    "Copy and paste email content to analyze for potential phishing and scam attempts using AI-powered detection",
  "manual.loading.title": "Analyzing email...",
  "manual.loading.subtitle":
    "Please wait while we check for potential scams and phishing attempts",
  "manual.error.generic": "An error occurred while analyzing the email",

  // Form
  "manual.form.heading": "Email Content",
  "manual.form.senderLabel": "Sender Email Address",
  "manual.form.subjectLabel": "Email Subject",
  "manual.form.bodyLabel": "Email Body",
  "manual.form.bodyPlaceholder": "Paste the email content here...",
  "manual.form.contextLabel": "Context (optional)",
  "manual.form.contextPlaceholder": "Any other details or situations to add?",
  "manual.form.analyzing": "Analyzing...",
  "manual.form.analyzeBtn": "Analyze Email",
  "manual.form.clearBtn": "Clear",

  // Results
  "manual.results.heading": "Analysis Details",
  "manual.results.safeSubtitle": "This email appears to be safe",
  "manual.results.scamType": "Scam Type:",
  "manual.results.noAnalysis": "No Analysis Available",
  "manual.results.noAnalysisDesc":
    "Provide email content to see detailed results here.",

  // Mobile-specific
  "manual.results.viewResults": "View Analysis Results",
  "manual.results.backToForm": "Back to Form",
  "manual.results.appearsLegitimate": "APPEARS LEGITIMATE",
  "manual.results.newScan": "New Scan",
  "manual.results.noAnalysisMobile": "Submit an email to see results here.",
  "manual.results.goToForm": "Go to Form",

  // ── Dashboard Simple ───────────────────────────────────────────────
  "dashboard.welcome": "Welcome",
  "dashboard.subtitle": "Your Scam Protection Dashboard",

  // Setup Progress toggle button
  "dashboard.setupProgress.show": "Show Setup Progress",
  "dashboard.setupProgress.hide": "Hide Setup Progress",
  "dashboard.setupProgress.showAriaLabel": "Show account setup progress",
  "dashboard.setupProgress.hideAriaLabel": "Hide account setup progress",
  "dashboard.setupProgress.label": "Setup Progress",

  // Pin-as-default (shared with GettingStarted)
  "dashboard.pin.alreadyDefault": "This is already your default page",
  "dashboard.pin.setDefault":
    "Set this page as your default landing page after login",
  "dashboard.pin.confirmed": "Default page updated!",
  "dashboard.pin.isDefault": "This is your default page",
  "dashboard.pin.makeDefault": "Make this my default page",

  // Footer
  "dashboard.footer.helpText":
    "Need help? Our support team is here to assist you with any questions.",
  "dashboard.footer.contactInfo":
    "\ud83d\udcde Call Support: 1-301-237-7843 | \ud83d\udce7 Email: support@kinprotect.com",

  // ── Getting Started page ───────────────────────────────────────────
  "gs.header.title": "Getting Started with KinShield",
  "gs.header.subtitle":
    "Here's what KinShield can do for you. Choose anything below to get started \u2014 there's no wrong choice.",
  "gs.recommended": "Recommended",

  // Category headers
  "gs.category.protect.title": "Protect Yourself",
  "gs.category.protect.subtitle": "Check suspicious messages, calls, and links",
  "gs.category.protect.moreHint":
    "Phone numbers, links, screenshots, social media posts, and conversations",
  "gs.more.show": "Show {count} more ways to check",
  "gs.more.hide": "Show fewer options",
  "gs.category.learn.title": "Learn & Train",
  "gs.category.learn.subtitle": "Build your defenses with knowledge",
  "gs.learn.simulationNote.before":
    "Safe scam simulations will automatically be sent to you according to your ",
  "gs.learn.simulationNote.link": "Simulation Settings",
  "gs.learn.simulationNote.after": ".",
  "gs.category.monitor.title": "Your Security Status",
  "gs.category.monitor.subtitle": "See how you're doing",
  "gs.category.family.title": "Protect Your Family",
  "gs.category.family.subtitle":
    "Extend protection to the people you care about",
  "gs.category.account.title": "Your Account",
  "gs.category.account.subtitle": "Manage your plan and preferences",

  // Protect Yourself items
  "gs.item.email.label": "Check a suspicious email",
  "gs.item.email.description":
    "Forward any suspicious email to security@kinprotect.com, then come here to see the AI analysis.",
  "gs.item.text.label": "Check a suspicious text message",
  "gs.item.text.description":
    "Forward suspicious texts to +1-888-899-5004 and view the analysis here.",
  "gs.item.phone.label": "Look up a phone number",
  "gs.item.phone.description":
    "See if a phone number is linked to known scammers.",
  "gs.item.url.label": "Check if a link is safe",
  "gs.item.url.description":
    "Paste a URL to scan for phishing, malware, or fraud.",
  "gs.item.screenshot.label": "Upload a screenshot for analysis",
  "gs.item.screenshot.description":
    "Upload an image to detect tech support scams, fake invoices, and more.",
  "gs.item.social.label": "Check a social media post",
  "gs.item.social.description":
    "Analyze a social media post for scam indicators.",
  "gs.item.conversation.label": "Analyze a text conversation",
  "gs.item.conversation.description":
    "Paste a full conversation thread to detect manipulation or fraud patterns.",

  // Learn & Train items
  "gs.item.training.label": "Start security training",
  "gs.item.training.description":
    "Short lessons and quizzes on how to spot and avoid scams.",
  "gs.item.simulationprofile.label": "Set your Simulation Profile",
  "gs.item.simulationprofile.description":
    "Configure your simulation profile to receive personalized phishing exercises tailored to your risk level.",
  "gs.item.alerts.label": "Browse the Alerts Intelligence Hub",
  "gs.item.alerts.description":
    "See real scam alerts by category, threat level, and recent trends.",

  // Monitor items
  "gs.item.score.label": "View your safety score",
  "gs.item.score.description":
    "A personal score based on your training, simulations, and engagement.",
  "gs.item.risk.label": "Check your risk assessment",
  "gs.item.risk.description":
    "Understand your risk level based on your security behavior.",

  // Family items
  "gs.item.adduser.label": "Add a family member",
  "gs.item.adduser.description":
    "Invite a family member so they get the same protection you do.",
  "gs.item.manageusers.label": "Manage users on your account",
  "gs.item.manageusers.description":
    "View, edit, or remove users in your group.",

  // Account items
  "gs.item.plan.label": "View your subscription plan",
  "gs.item.plan.description":
    "See your current plan, or upgrade for more features.",
  "gs.item.settings.label": "Customize your settings",
  "gs.item.settings.description":
    "Notification preferences, theme, password, and two-factor authentication.",

  // Footer
  "gs.footer.title": "Looking for something else?",
  "gs.footer.chatbotTip.prefix": "Ask",
  "gs.footer.chatbotTip.scoutName": "Scout",
  "gs.footer.chatbotTip.middle": "our",
  "gs.footer.chatbotTip.dpgName": "Digital Personal Guard",
  "gs.footer.chatbotTip.suffix":
    "chatbot, for help \u2014 just click the chat bubble in the bottom-right corner.",
  "gs.footer.chatWithScout.title": "Chat with Scout",
  "gs.footer.contactSupport.title": "Contact Support",
  "gs.footer.contactSupport.description":
    "Reach our support team for more complex questions or account help.",
  "gs.footer.contactSupport.button": "Contact Support",

  // Install App Panel
  "installApp.description":
    "Install KinShield as a standalone app on this device for quick access and a native experience.",
  "installApp.alreadyInstalled":
    "KinShield is already installed as an app on your device.",
  "installApp.shortcutRemoved":
    "KinShield is already installed \u2014 looks like the shortcut was removed.",
  "installApp.restoreShortcut": "Restore Shortcut",
  "installApp.install": "Install",
  "installApp.installTitle": "Install KinShield on this device",
  "installApp.installTitleFallback":
    "Install KinShield (opens setup guide if browser prompt is unavailable)",
  "installApp.addToHomepage": "Add to Homepage",

  // ── Common additions ───────────────────────────────────────────────
  "common.retry": "Retry",
  "common.unableToLoad": "\u26a0\ufe0f Unable to load. Please try again.",

  // ── Today's Focus card ─────────────────────────────────────────────
  "dashboard.todaysFocus.label": "Today's Focus",
  "dashboard.todaysFocus.nextSafetyLesson": "Your Next Safety Lesson",
  "dashboard.todaysFocus.trainingOf": "Training {completed} of {total}",
  "dashboard.todaysFocus.startNow": "Start Now \u2192",
  "dashboard.todaysFocus.startFirstTitle": "Start Your First Safety Lesson",
  "dashboard.todaysFocus.startFirstSub":
    "Learn to spot scams before they reach you",
  "dashboard.todaysFocus.getStarted": "Get Started \u2192",
  "dashboard.todaysFocus.allCompleteTitle":
    "All Trainings Complete \u2014 Great Job!",
  "dashboard.todaysFocus.allCompleteSub":
    "You\u2019ve finished all available lessons. Check back soon for new ones.",
  "dashboard.todaysFocus.viewProgress": "View Your Progress \u2192",
  "dashboard.todaysFocus.continueTitle": "Continue Your Safety Training",
  "dashboard.todaysFocus.completedOf":
    "You\u2019ve completed {completed} of {total} lessons",
  "dashboard.todaysFocus.continueBtn": "Continue Training \u2192",
  "dashboard.todaysFocus.buildSkillsTitle": "Build Your Scam Protection Skills",
  "dashboard.todaysFocus.buildSkillsSub":
    "Complete safety lessons to stay protected online",

  // ── Safety Score card (SafetyScoreCard) ────────────────────────────
  "dashboard.safetyScore.title": "\ud83d\udee1\ufe0f How Secure Are You?",
  "dashboard.safetyScore.lastDays": "Last 30 days",
  "dashboard.safetyScore.excellent": "Excellent",
  "dashboard.safetyScore.strong": "Strong",
  "dashboard.safetyScore.good": "Good",
  "dashboard.safetyScore.buildingUp": "Building Up \ud83c\udf31",
  "dashboard.safetyScore.goodStart": "Good Start \u2014 Keep Going!",
  "dashboard.safetyScore.justStarted": "Just Getting Started",
  "dashboard.safetyScore.explain80":
    "Great job! Your safe habits are keeping you well-protected.",
  "dashboard.safetyScore.explain60":
    "Good progress! A few more safe choices will boost your protection.",
  "dashboard.safetyScore.explain40":
    "You\u2019re building good habits! Complete a lesson to raise your level.",
  "dashboard.safetyScore.explain0":
    "Let\u2019s get started! Complete a training lesson to build your protection.",
  "dashboard.safetyScore.cta60": "Review a Lesson to Stay Sharp \u2192",
  "dashboard.safetyScore.cta0": "Complete 1 Training to Improve \u2192",
  "dashboard.safetyScore.scoreComponents": "Score Components",
  "dashboard.safetyScore.comp.appParticipation": "App Participation",
  "dashboard.safetyScore.comp.emailSafety": "Email Safety",
  "dashboard.safetyScore.comp.textSafety": "Text Safety",
  "dashboard.safetyScore.comp.training": "Training",
  "dashboard.safetyScore.noDataYet": "No data yet",
  "dashboard.safetyScore.active": "Active \u2713",
  "dashboard.safetyScore.daysActive": "{success} of {total} days active",
  "dashboard.safetyScore.noTestsYet": "No tests yet",
  "dashboard.safetyScore.avoidedOf": "{success} of {total} avoided",
  "dashboard.safetyScore.noneAvailable": "None available",
  "dashboard.safetyScore.allComplete": "All complete \u2713",
  "dashboard.safetyScore.doneOf": "{success} of {total} done",

  // ── Simple Score card (ScoreCard) ────────────────────────────
  "dashboard.scoreCard.titleProtection": "How Secure Are You?",
  "dashboard.scoreCard.titleSafety": "Your Safety Score",
  "dashboard.scoreCard.excellentProtection": "Excellent Protection",
  "dashboard.scoreCard.goodProtection": "Good Protection",
  "dashboard.scoreCard.needsImprovement": "Needs Improvement",
  "dashboard.scoreCard.highRisk": "High Risk",
  "dashboard.scoreCard.someRisk": "Some Risk",
  "dashboard.scoreCard.lowRisk": "Low Risk",
  "dashboard.scoreCard.adviceShieldGood":
    "Great work! Your online safety habits are protecting you well. Keep it up!",
  "dashboard.scoreCard.adviceShieldWarning":
    "You\u2019re doing well! A bit more practice will strengthen your safety skills.",
  "dashboard.scoreCard.adviceShieldDanger":
    "You\u2019re just getting started \u2014 complete a few drills to build your protection.",
  "dashboard.scoreCard.adviceRiskGood":
    "Excellent! You\u2019re demonstrating safe online behavior. Keep following best practices.",
  "dashboard.scoreCard.adviceRiskWarning":
    "Be cautious. Some of your online habits could put you at risk. Review the safety tips.",
  "dashboard.scoreCard.adviceRiskDanger":
    "Important: Your online activities show higher risk. Please complete the training to stay safe.",
  "dashboard.scoreCard.noteLabel": "Note:",
  "dashboard.scoreCard.usingSampleData": "Using sample data -",

  // ── Training Progress card (TrainingProgress) ────────────────
  "dashboard.training.title": "\ud83d\udcda Training Progress",
  "dashboard.training.getStarted": "Get Started",
  "dashboard.training.noLessons": "No lessons assigned yet",
  "dashboard.training.allDone": "\ud83c\udfc6 All Done!",
  "dashboard.training.completedAll": "You completed all {total} lessons",
  "dashboard.training.lessonOf": "Lesson {lesson} of {total}",
  "dashboard.training.requiredTraining": "Required Training",
  "dashboard.training.nextReady": "Your next lesson is ready",
  "dashboard.training.explain0":
    "Start your first lesson to build scam awareness!",
  "dashboard.training.explain90":
    "Outstanding! You\u2019re well-prepared to spot scams.",
  "dashboard.training.explain80":
    "Great job! Your training is keeping you safe.",
  "dashboard.training.explain70":
    "Good progress! Keep going to stay protected.",
  "dashboard.training.explain60":
    "Nice work! More lessons will strengthen your skills.",
  "dashboard.training.explain50":
    "You\u2019re on your way! Each lesson makes you safer.",
  "dashboard.training.explainDefault":
    "Every expert was once a beginner \u2014 keep going!",
  "dashboard.training.continueBtn": "Continue Training \u2192",
  "dashboard.training.required": "Required",
  "dashboard.training.recommended": "Recommended",
  "dashboard.training.quizzesTaken": "Quizzes Taken",
  "dashboard.training.avgScore": "Avg Score",
  "dashboard.training.badges": "Badges",
  "dashboard.training.areasToggle": "Training Areas ({count})",

  // ── Scam Text Simulations (TextSimulations) ───────────────
  "dashboard.textSims.title": "\ud83d\udcf1 Scam Text Simulations",
  "dashboard.textSims.scamsAvoided": "Simulated Scam Texts Avoided",
  "dashboard.textSims.noTestsYet": "No Tests Yet",
  "dashboard.textSims.explain0":
    "Text safety tests coming soon to help protect you from scam messages.",
  "dashboard.textSims.explainPerfect":
    "Perfect! You avoided all scam text attempts.",
  "dashboard.textSims.explain80":
    "Great awareness! Keep staying alert to suspicious texts.",
  "dashboard.textSims.explain60":
    "Good progress! Remember to verify unknown senders.",
  "dashboard.textSims.explainLow":
    "Stay cautious! Don\u2019t click links in unexpected texts.",
  "dashboard.textSims.avoided": "Avoided",
  "dashboard.textSims.clicked": "Clicked",
  "dashboard.textSims.total": "Total",
  "dashboard.textSims.showTests": "Show {count} Tests",
  "dashboard.textSims.textTest": "Text test",

  // ── Phishing Email Simulations (EmailSimulations) ─────────────────────
  "dashboard.emailSims.title": "\u2709\ufe0f Phishing Email Simulations",
  "dashboard.emailSims.phishingAvoided": "Simulated Phishing Emails Avoided",
  "dashboard.emailSims.noTestsYet": "No Tests Yet",
  "dashboard.emailSims.explain0":
    "Email safety tests coming soon to help protect you from phishing.",
  "dashboard.emailSims.explainPerfect":
    "Perfect! You avoided all phishing email attempts.",
  "dashboard.emailSims.explain80":
    "Great awareness! Keep watching for suspicious emails.",
  "dashboard.emailSims.explain60":
    "Good progress! Check sender addresses carefully.",
  "dashboard.emailSims.explainLow":
    "Stay alert! Don\u2019t click links in unexpected emails.",
  "dashboard.emailSims.avoided": "Avoided",
  "dashboard.emailSims.clicked": "Clicked",
  "dashboard.emailSims.total": "Total",
  "dashboard.emailSims.showTests": "Show {count} Tests",
  "dashboard.emailSims.emailTest": "Email test",

  // ── Recent Activities card (RecentActivities) ────────────────
  "dashboard.recentActivities.title": "Recent Activities",
  "dashboard.recentActivities.keepItUp": "\ud83d\udcc8 Keep It Up!",
  "dashboard.recentActivities.keepItUpDesc":
    "Here\u2019s what you\u2019ve been doing to stay safe online.",
  "dashboard.recentActivities.activity1": "Completed Email Safety Training",
  "dashboard.recentActivities.activity2": "Passed Phone Call Safety Test",
  "dashboard.recentActivities.activity3":
    "Reminder: Complete Text Message Training",
  "dashboard.recentActivities.activity4": "Earned Safety Champion Badge",
  "dashboard.recentActivities.today": "Today",
  "dashboard.recentActivities.yesterday": "Yesterday",
  "dashboard.recentActivities.twoDaysAgo": "2 days ago",
  "dashboard.recentActivities.oneWeekAgo": "1 week ago",
  "dashboard.recentActivities.viewAll": "View All Activities",

  // ── Recommendations card (Recommendations) ───────────────────
  "dashboard.recommendations.title": "Recommended for You",
  "dashboard.recommendations.loading": "Loading recommendations...",
  "dashboard.recommendations.greatJob": "\ud83c\udf89 Great job!",
  "dashboard.recommendations.allComplete":
    "You\u2019ve completed all available trainings!",
  "dashboard.recommendations.browseAll": "Browse All Trainings",
  "dashboard.recommendations.personalTips": "\ud83d\udca1 Personal Tips:",
  "dashboard.recommendations.based":
    "Based on your progress, here are some helpful topics to explore.",
  "dashboard.recommendations.recommended": "Recommended",
  "dashboard.recommendations.suggested": "Suggested",
  "dashboard.recommendations.optional": "Optional",
  "dashboard.recommendations.continue": "Continue",
  "dashboard.recommendations.startLearning": "Start Learning",
  "dashboard.recommendations.seeAll": "See All Training Options",

  // ── Forwarded Text/SMS Analysis (SmsAnalysisSimple) ──────────────────────
  "sms.page.title": "Check a Suspicious Text Message",
  "sms.page.subtitle":
    "Find out if a text is a scam \u2014 forward it to us or paste it in.",
  "sms.page.subtitlePrefix": "Forward suspicious text messages to KinShield (",
  "sms.page.subtitleSuffix": ") and view your automated threat analyses here",
  "sms.page.manualLink": "Prefer to copy/paste it?",
  "sms.page.manualLinkAction": "Analyze manually",
  "sms.disclaimer":
    "The SMS analysis provided is for educational purposes only. For complete terms and limitations, please review our",

  // Entry panel (how to check a text message)
  "sms.start.recommended": "Recommended",
  "sms.start.forward.title": "Forward it to us",
  "sms.start.forward.why":
    "Most accurate \u2014 we see the exact message, links and all.",
  "sms.start.forward.step1":
    "Open the suspicious text. Don\u2019t tap any links or reply.",
  "sms.start.forward.step2": "Forward it to:",
  "sms.start.forward.step3": "Forward it from {phone}",
  "sms.start.forward.step3Fallback":
    "Forward it from the phone number on your account",
  "sms.start.forward.step3Detail":
    "That\u2019s how we know it\u2019s yours. Texts forwarded from other numbers can\u2019t be checked.",
  "sms.start.forward.helpLabel": "Step-by-step for iPhone & Android",
  "sms.start.forward.sentBtn": "I\u2019ve just forwarded it",
  "sms.start.waiting.title": "Waiting for your text\u2026",
  "sms.start.waiting.body":
    "This usually takes under a minute. Your result will open here automatically.",
  "sms.start.waiting.cancel": "I didn\u2019t send it \u2014 cancel",
  "sms.start.timeout.title": "We haven\u2019t received it yet",
  "sms.start.timeout.body": "Make sure you sent it to {number} from {phone}.",
  "sms.start.timeout.bodyFallback":
    "Make sure you sent it to {number} from the phone number on your account.",
  "sms.start.timeout.retry": "Keep waiting",
  "sms.start.timeout.dismiss": "Stop waiting",
  "sms.start.paste.title": "Copy and paste it in",
  "sms.start.paste.why":
    "Get an answer right away. Copy the message (and the sender\u2019s number, if you have it) into a short form.",
  "sms.start.paste.btn": "Paste a text",
  "sms.start.compact.forward": "Check another text \u2014 forward it to",
  "sms.start.compact.sentBtn": "I\u2019ve just forwarded one",
  "sms.start.compact.or": "or",
  "sms.start.compact.paste": "I want to paste it in",

  // Mobile header
  "sms.mobile.title": "SMS Analysis",
  "sms.mobile.subtitle":
    "Forward suspicious text messages for automated threat analysis",
  "sms.mobile.forwardLabel": "Forward texts to",
  "sms.mobile.copied": "Copied!",
  "sms.mobile.copy": "Copy",
  "sms.mobile.backToList": "Back to List",

  // Results list
  "sms.results.heading": "Analysis History",
  "sms.results.showingSingular":
    "Showing {count} SMS analysis \u2022 Click on any item to view details",
  "sms.results.showingPlural":
    "Showing {count} SMS analyses \u2022 Click on any item to view details",
  "sms.results.noSmsFound": "No SMS analyses found",
  "sms.results.refreshTitle": "Refresh analysis results",

  // Empty states
  "sms.empty.noAnalysisFound": "No SMS Analyses Found",
  "sms.empty.forwardToKinShield":
    "Forward suspicious text messages to KinShield (+1-888-899-5004) to see analyses here",
  "sms.empty.noAnalysesYet": "No Analyses Yet",
  "sms.empty.getStarted":
    "Forward a suspicious text message to +1-888-899-5004 to get started.",
  "sms.empty.checkResults": "Check for Results",
  "sms.empty.analyzeManuallyBtn": "Analyze a message manually",

  // Status badges
  "sms.badge.scam": "SCAM",
  "sms.badge.safe": "SAFE",
  "sms.badge.legitimate": "LEGITIMATE",

  // Card labels
  "sms.card.message": "Message",
  "sms.card.deleteTitle": "Delete analysis",
  "sms.card.delete": "Delete",

  // Confidence descriptions
  "sms.confidence.veryLikelyScam": "Very likely scam ({pct}%)",
  "sms.confidence.likelyScam": "Likely scam ({pct}%)",
  "sms.confidence.possiblyScam": "Possibly scam ({pct}%)",
  "sms.confidence.veryLikelySafe": "Very likely safe ({pct}%)",
  "sms.confidence.likelySafe": "Likely safe ({pct}%)",
  "sms.confidence.possiblySafe": "Possibly safe ({pct}%)",

  // Detail view
  "sms.detail.back": "Back to Results",
  "sms.detail.scamDetected": "SCAM DETECTED",
  "sms.detail.noThreats": "NO THREATS DETECTED",
  "sms.detail.scamSubtitle":
    "This message contains suspicious or fraudulent content",
  "sms.detail.safeSubtitle": "This message appears to be safe",
  "sms.detail.shareIndividual": "Available For Family or Extended Family Plans",
  "sms.detail.shareFamily": "Share this alert with family members",
  "sms.detail.share": "Share",

  // Share button props
  "sms.share.threatType": "SMS Scam",
  "sms.share.threatTitle": "SMS Scam Detected",

  // Threat flags
  "sms.flag.suspiciousLinks": "Suspicious Links",
  "sms.flag.urgentLanguage": "Urgent Language",
  "sms.flag.financialThreats": "Financial Threats",

  // Section headings (detail panel)
  "sms.section.analysisDetails": "Analysis Details",
  "sms.section.analysisDetailsSubtitle": "What we found about this SMS",
  "sms.section.whatToDo": "What Should You Do?",
  "sms.section.explanation": "Explanation",
  "sms.section.noSmsSelected": "No SMS Selected",
  "sms.section.noSmsSelectedDesc":
    "Select an SMS from the list to view detailed analysis results here.",

  // Original message
  "sms.message.viewOriginal": "View Original Message",
  "sms.message.show": "Show Message Content",
  "sms.message.hide": "Hide Message Content",
  "sms.message.noContent": "No message content available.",

  // Errors & quota
  "sms.error.tryAgain": "Try Again",
  "sms.error.notAuthenticated": "User not authenticated",
  "sms.error.loadFailed": "Failed to load SMS analyses. Please try again.",
  "sms.error.notFound": "The requested SMS analysis could not be found.",
  "sms.error.loadNotifFailed": "Failed to load the requested SMS analysis.",
  "sms.error.deleteFailed": "Failed to delete SMS analysis. Please try again.",
  "sms.quota.title": "Daily Limit Reached",
  "sms.quota.body":
    "You've reached your daily limit of 25 SMS analyses. Your quota will reset tomorrow.",

  // ── Manual Text/SMS Analysis (TextMessageAnalysis) ───────────────────────
  "textAnalysis.page.title": "Manual Text/SMS Analysis",
  "textAnalysis.page.subtitle":
    "Analyze phone numbers and text messages for potential scams and security threats",

  // Loading
  "textAnalysis.loading.title": "Analyzing text message...",
  "textAnalysis.loading.subtitle":
    "Please wait while we check for potential scams",

  // Form
  "textAnalysis.form.heading": "Inputs",
  "textAnalysis.form.phoneLabel": "Phone Number",
  "textAnalysis.form.phonePlaceholder": "Enter phone number",
  "textAnalysis.form.invalidPhone": "Invalid phone number",
  "textAnalysis.form.messageLabel": "Message Content",
  "textAnalysis.form.messagePlaceholder":
    "Paste or type the text message content to analyze...",
  "textAnalysis.form.charCount": "{count}/1000 characters",
  "textAnalysis.form.contextLabel": "Context (optional)",
  "textAnalysis.form.contextPlaceholder":
    "Any other details or situations to add?",
  "textAnalysis.form.analyzing": "Analyzing...",
  "textAnalysis.form.analyzeBtn": "Analyze Content",
  "textAnalysis.form.clearBtn": "Clear",

  // Mobile form section header
  "textAnalysis.mobile.formSection": "Text Message Inputs",

  // Results
  "textAnalysis.results.heading": "Analysis Details",
  "textAnalysis.results.whatToDo": "What Should You Do?",
  "textAnalysis.results.explanation": "Explanation",
  "textAnalysis.results.scamType": "Scam Type:",
  "textAnalysis.results.completedOn": "Analysis completed on",
  "textAnalysis.results.noAnalysis": "No Analysis Available",
  "textAnalysis.results.noAnalysisDesc":
    "Provide inputs to see detailed results here.",

  // Mobile results
  "textAnalysis.mobile.viewResults": "View Analysis Results",
  "textAnalysis.mobile.backToForm": "Back to Form",
  "textAnalysis.mobile.noThreats": "NO THREATS DETECTED",
  "textAnalysis.mobile.riskDetectedSuffix": "RISK DETECTED",
  "textAnalysis.mobile.shareIndividual":
    "Available For Family or Extended Family Plans",
  "textAnalysis.mobile.shareFamily": "Share this alert with family members",
  "textAnalysis.mobile.share": "Share",
  "textAnalysis.mobile.newScan": "New Scan",
  "textAnalysis.mobile.noResults.title": "No Results Yet",
  "textAnalysis.mobile.noResults.desc":
    "Submit message details to see analysis results here.",
  "textAnalysis.mobile.goToForm": "Go to Form",

  // Phone analysis section
  "textAnalysis.phone.title": "Phone Number Analysis",
  "textAnalysis.phone.riskLevel": "Risk Level",
  "textAnalysis.phone.summary": "Summary",
  "textAnalysis.phone.analysis": "Analysis",
  "textAnalysis.phone.whatToDo": "What Should You Do?",
  "textAnalysis.phone.dbResults": "Database Check Results ({count})",
  "textAnalysis.phone.foundInDb": "Found in Database",
  "textAnalysis.phone.notFound": "Not Found",
  "textAnalysis.phone.reports": "Reports",
  "textAnalysis.phone.category": "Category",
  "textAnalysis.phone.lastReported": "Last Reported",
  "textAnalysis.phone.status": "Status",

  // Text/SMS analysis section
  "textAnalysis.textSection.title": "Text/SMS Analysis",
  "textAnalysis.textSection.riskLevel": "Risk Level",
  "textAnalysis.textSection.confidence": "Confidence",
  "textAnalysis.textSection.category": "Category",
  "textAnalysis.textSection.scamType": "Scam Type",
  "textAnalysis.textSection.riskScore": "Risk Score",
  "textAnalysis.textSection.analysis": "Analysis",
  "textAnalysis.textSection.explanation": "Explanation",
  "textAnalysis.textSection.whatToDo": "What Should You Do?",
  "textAnalysis.textSection.reasoning": "Reasoning",

  // Errors
  "textAnalysis.error.emptyInput":
    "Please enter a phone number and/or text message to analyze.",
  "textAnalysis.error.timeout":
    "Request timed out. Please check your connection and try again.",
  "textAnalysis.error.server":
    "Server error ({status}): {text}. Please try again.",
  "textAnalysis.error.network":
    "Network error. Please check your internet connection and try again.",
  "textAnalysis.error.generic": "Analysis failed. Please try again.",

  // ─── Text Conversation Analysis ───────────────────────────────────────────
  "textConv.page.title": "Text Conversation Analysis",
  "textConv.page.subtitle":
    "Check your text conversation for warning signs of scams. Enter your message exchanges in order to detect suspicious patterns like romance scams, investment fraud, and identity theft.",
  "textConv.mobile.subtitle":
    "Check your text conversation for warning signs of scams and suspicious patterns",
  "textConv.loading.title": "Analyzing conversation history...",
  "textConv.loading.subtitle":
    "Please wait while we check for potential scam patterns",
  "textConv.error.missingPhone": "Please enter a phone number.",
  "textConv.error.noMessages": "Please enter at least one message to analyze.",
  "textConv.error.timeout":
    "Request timed out. Please check your connection and try again.",
  "textConv.error.server": "Server error ({status}): {text}. Please try again.",
  "textConv.error.network":
    "Network error. Please check your internet connection and try again.",
  "textConv.error.generic": "Analysis failed. Please try again.",
  "textConv.tab.input": "Input",
  "textConv.tab.results": "Results",
  "textConv.form.heading": "Enter Conversation Details",
  "textConv.form.sectionTitle": "Message History",
  "textConv.form.subtitle":
    "Enter your text conversation to check for scam patterns",
  "textConv.form.conversationInputs": "Conversation Inputs",
  "textConv.form.senderInfo": "Sender Info",
  "textConv.form.messagesLabel": "Messages (click icons to mark sender)",
  "textConv.form.messagesTapLabel": "Messages (tap icons to mark sender)",
  "textConv.form.addMessage": "Add Another Message",
  "textConv.form.analyzeBtn": "Analyze Conversation",
  "textConv.form.clearAll": "Clear All",
  "textConv.form.fromThem": "From them",
  "textConv.form.fromYou": "From you",
  "textConv.form.them": "Them",
  "textConv.form.you": "You",
  "textConv.form.theirMessage": "Their message...",
  "textConv.form.yourMessage": "Your message...",
  "textConv.form.remove": "Remove",
  "textConv.form.invalid": "Invalid",
  "textConv.results.heading": "Scam Detection Results",
  "textConv.results.conversationHeading": "Conversation Analysis Results",
  "textConv.results.overallRisk": "Overall Risk Assessment",
  "textConv.results.riskType": "Risk Type",
  "textConv.results.detailedBreakdown": "Detailed Breakdown",
  "textConv.results.conversationPattern": "Conversation Pattern Analysis",
  "textConv.results.recommendations": "Recommendations",
  "textConv.results.detectedThreats": "Detected Threats",
  "textConv.results.riskIndicators": "Risk Indicators",
  "textConv.results.recommendationLabel": "Recommendation",
  "textConv.empty.readyTitle": "Ready to Analyze",
  "textConv.empty.readyDesc":
    "Enter your message conversation on the left to check for scam patterns and get safety recommendations.",
  "textConv.empty.noResultsTitle": "Ready to Check Messages",
  "textConv.empty.noResultsDesc":
    "Complete the Input tab with your conversation, then come back here to see the safety analysis.",
  "textConv.empty.noResultsMobile":
    "Submit your conversation to see analysis results here.",
  "textConv.risk.highRecommendation":
    "Exercise extreme caution and avoid responding to this message.",
  "textConv.risk.mediumRecommendation":
    "Proceed with caution and verify the sender before taking any action.",
  "textConv.risk.lowRecommendation":
    "This appears to be safe, but always stay vigilant.",
  "textConv.risk.unknownRecommendation":
    "Unable to determine safety due to insufficient information. Use your best judgment.",

  // ── Phone Number Scam Check ────────────────────────────────────────
  "phoneCheck.error.enterPhone": "Please enter a phone number",
  "phoneCheck.error.invalidPhone":
    "Please enter a valid phone number for the selected country",
  "phoneCheck.error.incompletePhone":
    "Please enter a complete phone number for the selected country",
  "phoneCheck.error.generic":
    "An error occurred while analyzing the phone number",

  // ── Image Upload ──────────────────────────────────────────────────
  "imageUpload.header": "Upload Image for Analysis",
  "imageUpload.quota.title": "Daily Limit Reached",
  "imageUpload.quota.body":
    "You've reached your daily limit of 25 image uploads. Your quota will reset tomorrow.",
  "imageUpload.paste.title": "Paste Screenshot Here",
  "imageUpload.paste.hint": "Take a screenshot, then press",
  "imageUpload.paste.helpTitle": "How to take a screenshot",
  "imageUpload.paste.howTo":
    "\ud83d\udcf8 How to capture and paste a screenshot:",
  "imageUpload.paste.winTab": "\ud83e\ude9f Windows",
  "imageUpload.paste.macTab": "\ud83c\udf4e Mac",
  "imageUpload.paste.win.step1label": "Step 1 - Capture:",
  "imageUpload.paste.win.step1press": "Press",
  "imageUpload.paste.win.step1detail":
    "Your screen will dim and a crosshair will appear",
  "imageUpload.paste.win.step2label": "Step 2 - Select:",
  "imageUpload.paste.win.step2action":
    "Click and drag to select the area you want to capture",
  "imageUpload.paste.win.step2detail":
    "The screenshot is now copied to your clipboard",
  "imageUpload.paste.win.step3label": "Step 3 - Paste:",
  "imageUpload.paste.win.step3action":
    "Make sure you're on this page, then press",
  "imageUpload.paste.win.step3detail":
    "Your screenshot will appear automatically",
  "imageUpload.paste.mac.step1label": "Step 1 - Capture:",
  "imageUpload.paste.mac.step1press": "Press",
  "imageUpload.paste.mac.step1detail": "A crosshair cursor will appear",
  "imageUpload.paste.mac.step2label": "Step 2 - Select:",
  "imageUpload.paste.mac.step2action": "Click and drag to select the area",
  "imageUpload.paste.mac.step2detail":
    "The screenshot is saved to your desktop",
  "imageUpload.paste.mac.step3label": "Step 3 - Copy:",
  "imageUpload.paste.mac.step3action":
    'Right-click the screenshot image and select "Copy" or "Save to Clipboard"',
  "imageUpload.paste.mac.step3detailA": "Or use",
  "imageUpload.paste.mac.step3detailB": "to copy directly to clipboard",
  "imageUpload.paste.mac.step4label": "Step 4 - Paste:",
  "imageUpload.paste.mac.step4action":
    "Make sure you're on this page, then press",
  "imageUpload.paste.mac.step4detail":
    "Your screenshot will appear automatically",
  "imageUpload.browse.title": "Click to Browse Files",
  "imageUpload.browse.hint1": "Or drag and drop an image file here",
  "imageUpload.browse.hint2": "JPEG, PNG, GIF, WebP, BMP, TIFF, SVG (Max 10MB)",
  "imageUpload.divider": "OR",
  "imageUpload.preview.alt": "Preview",
  "imageUpload.context.label": "Context (optional)",
  "imageUpload.context.placeholder":
    "Any other details or situations to add? (e.g. 'Received this image in a message from an unknown sender') The more context you provide, the better we can analyze the risk.",
  "imageUpload.analyzeBtn": "Analyze",
  "imageUpload.status.starting": "Starting upload...",
  "imageUpload.status.complete": "Upload complete! Analyzing...",
  "imageUpload.whatNext.title": "What happens next?",
  "imageUpload.whatNext.step1":
    "Your image is securely uploaded to our cloud storage",
  "imageUpload.whatNext.step2":
    "Security scan checks for malware and inappropriate content",
  "imageUpload.whatNext.step3":
    "AI analyzes the image for scam indicators using computer vision",
  "imageUpload.whatNext.step4":
    "You receive a detailed report with risk assessment and recommendations",
  "imageUpload.security.badge": "Your images are encrypted and securely stored",
  "imageUpload.error.selectFile": "Please select a file first",
  "imageUpload.error.invalidType":
    "Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP, BMP, TIFF, SVG)",
  "imageUpload.error.tooLarge": "File too large. Maximum size is 10MB",
  "imageUpload.error.quotaExceeded":
    "Daily upload limit reached (25/day). Your quota will reset tomorrow.",
  "imageUpload.error.uploadFailed": "Upload failed. Please try again.",
  "imageUpload.toast.pasted": "Screenshot pasted from clipboard!",

  // ── Notifications ────────────────────────────────────────────────
  "notification.title": "Notifications",
  "notification.subtitle":
    "Stay up to date with your account activity and email analysis results",
  "notification.loading": "Loading notifications...",
  "notification.empty": "No notifications",
  "notification.empty.subtitle": "You're all caught up! Nothing to show here.",
  "notification.markAllRead": "Mark All Read",
  "notification.markAllAsRead.tooltip": "Mark all as read",
  "notification.markAsRead": "Mark as read",
  "notification.dismiss": "Dismiss notification",
  "notification.viewAll": "View All Notifications",
  "notification.viewDetails": "View Details",
  "notification.count.one": "1 notification",
  "notification.count.many": "{count} notifications",
  "notification.stat.total": "Total",
  "notification.stat.unread": "Unread",
  "notification.stat.emailAnalysis": "Email Analysis",
  "notification.stat.status": "Status",
  "notification.sections.all.title": "All Notifications",
  "notification.sections.all.subtitle":
    "View all your notifications in one place",
  "notification.sections.unread.title": "Unread",
  "notification.sections.unread.subtitle": "Notifications you haven't read yet",
  "notification.sections.emailAnalysis.title": "Email Analysis",
  "notification.sections.emailAnalysis.subtitle":
    "Email safety analysis results and alerts",
  "notification.sections.system.title": "System",
  "notification.sections.system.subtitle": "System and account notifications",
  "notification.verificationFailed.message":
    "\u26a0\ufe0f The item may have been deleted. Would you like to delete this notification?",
  "notification.verificationFailed.confirmDelete": "Yes, Delete",
  "notification.verificationFailed.keepIt": "No, Keep It",
  "notification.unreadCount.one": "1 unread notification",
  "notification.unreadCount.many": "{count} unread notifications",
  "notification.tapToMarkAllRead": "Tap to mark all as read",
  "notification.markAll": "Mark All",
  "notification.allCaughtUp": "You're all caught up!",
  "notification.noItemsToShow": "No notifications to show.",
  "notification.checkForUpdates": "Check for Updates",

  // ── Account & Billing (AccountBilling.js) ─────────────────────────
  "ab.title": "Subscription & Payment",
  "ab.subtitle": "Manage your account, subscription, and payments in one place",
  "ab.loading": "Loading Subscription & Payment...",
  "ab.loading.subscription": "Loading subscription...",
  "ab.loading.processing": "Processing your subscription...",
  "ab.refreshTitle": "Refresh all data",
  "ab.error.unableToLoad": "Unable to load account information.",
  "ab.error.failedToLoad":
    "Failed to load account information. Please try again later.",
  "ab.stat.account": "Subscription",
  "ab.stat.myAccount": "My Account",
  "ab.stat.noPlan": "No Active Plan",
  "ab.stat.status": "Status",
  "ab.stat.users": "Users",
  "ab.stat.usersUnlimited": "{count} (unlimited)",
  "ab.stat.manageBtn": "Manage \u2192",
  "ab.stat.manageTitle": "Manage users",
  "ab.stat.memberSince": "Next Payment",
  "ab.status.active": "Active",
  "ab.status.inactive": "Inactive",
  "ab.status.lapsed": "Lapsed",
  "ab.status.noSub": "No Subscription",
  "ab.status.cancelling": "Cancelling",
  "ab.status.gracePeriod": "Grace Period",
  "ab.status.downgradePending": "Downgrade pending",
  "ab.badge.freeTrial": "Free Trial",
  "ab.badge.freeTrialTitle": "You are on a free trial",
  "ab.section.subscription.title": "Subscription",
  "ab.section.subscription.subtitle":
    "Manage your plan, features, and renewal settings",
  "ab.section.payment.title": "Payment Methods",
  "ab.section.payment.subtitle": "Manage your saved cards and payment options",
  "ab.section.history.title": "Billing History",
  "ab.section.history.subtitle": "View past transactions and payment activity",
  "ab.cycle.monthly": "Monthly",
  "ab.cycle.yearly": "Yearly",
  "ab.cycle.autoRenew": " \u2022 Auto-renew",
  "ab.status.cancelled": "Cancelled",
  "ab.status.expired": "Expired",
  "ab.plan.currentPlan": "Current Plan",
  "ab.downgrade.scheduled": "Downgrade scheduled",
  "ab.downgrade.switchNotice":
    "Your plan will switch to {name}{price} on {date}. You keep full access to your current plan until then.",
  "ab.downgrade.nextBillingDate": "your next billing date",
  "ab.downgrade.cancelling": "Cancelling...",
  "ab.downgrade.keepCurrentPlan": "Keep Current Plan",
  "ab.promo.validateError": "Failed to validate promo code. Please try again.",
  "ab.promo.scheduled.title": "Promo code scheduled",
  "ab.promo.scheduled.codePrefix": "Code",
  "ab.promo.scheduled.discountSuffix": "({pct}% off)",
  "ab.promo.scheduled.effectOn":
    "will take effect on {date}. Your current period is unaffected.",
  "ab.promo.scheduled.nextBillingDate": "your next billing date",
  "ab.promo.scheduled.cancelling": "Cancelling\u2026",
  "ab.promo.scheduled.remove": "Remove Scheduled Promo",
  "ab.promo.apply.toggle": "Have a promo code?",
  "ab.promo.apply.label": "Apply a promo code to your next billing cycle",
  "ab.promo.apply.placeholder": "Enter promo code",
  "ab.promo.apply.checking": "Checking\u2026",
  "ab.promo.apply.check": "Check",
  "ab.promo.apply.applying": "Applying...",
  "ab.promo.apply.applyBtn": "Apply Promo Code",
  "ab.promo.apply.cancelTitle": "Cancel",
  // eslint-disable-next-line no-template-curly-in-string
  "ab.promo.apply.newPrice":
    "New price: ${price}/{cycle} starting your next billing date.",
  "ab.promo.trial.noticeTitle": "Promo code saved for your trial",
  "ab.promo.trial.noticeBody":
    "Code {code}{discount} will be applied automatically when your trial ends and converts to a paid plan. You won't be charged during your trial.",
  "ab.promo.trial.remove": "Remove Saved Promo",
  "ab.promo.trial.toggle": "Have a promo code for when your trial ends?",
  "ab.promo.trial.label":
    "Save a promo code now — it's applied automatically when your trial converts to a paid plan",
  "ab.promo.trial.hint":
    "You won't be charged during your trial. When it ends, you'll need a payment method on file, and the discount will be applied to your first paid charge.",
  "ab.promo.trial.paymentNotice":
    "You don't have a payment method on file yet. You'll need to add one before your trial ends so your plan can continue with this promo applied.",
  "ab.promo.trial.applyBtn": "Save Promo Code",
  "ab.promo.trial.applying": "Saving\u2026",
  // eslint-disable-next-line no-template-curly-in-string
  "ab.promo.trial.newPrice":
    "After your trial, you'll pay ${price}/{cycle} (no charge during your trial).",
  "ab.promo.trial.savedNote":
    "Promo {code} is saved and will be applied to this charge.",

  // ── Trial Reminder Modal ──
  "trialReminder.title": "⏰ Trial Period Reminder",
  "trialReminder.messagePrefix": "Your trial period will end in",
  "trialReminder.day": "day",
  "trialReminder.days": "days",
  "trialReminder.dateLabel": "Trial ends on:",
  "trialReminder.ownerInfo":
    "To continue enjoying uninterrupted access to all features, please setup payment methods before your trial expires.",
  "trialReminder.memberInfo":
    "Please alert your account owner to setup payment methods before your trial expires to continue enjoying uninterrupted access to all features.",
  "trialReminder.btnPayments": "View Payment Methods",
  "trialReminder.btnRemind": "Remind Me Later",
  "trialReminder.btnAlert": "Alert the Account Owner",

  "ab.planDetails.toggle": "Plan Features & Benefits",
  "ab.planDetails.limitsHeading": "Plan Limits",
  "ab.planDetails.users": "Members Protected",
  "ab.planDetails.simsPerMonth": "Scam Simulations/month",
  "ab.planDetails.reportsPerMonth": "Security Reports/month",
  "ab.planDetails.featuresHeading": "Included Features",
  "ab.planDetails.scamDetection": "Real-time Scam Detection",
  "ab.planDetails.phishingSimulations": "Phishing & SMS Simulations",
  "ab.planDetails.scamAlerts": "Scam Alerts & Threat Warnings",
  "ab.planDetails.advancedAnalytics": "Scam Threat Analytics",
  "ab.planDetails.customTraining": "Personalized Training Scenarios",
  "ab.planDetails.prioritySupport": "Priority Customer Support",
  "ab.action.chooseDifferentPlan": "Choose Different Plan",
  "ab.action.changePlan": "Change Plan",
  "ab.action.addPaymentMethod": "Add Payment Method",
  "ab.action.closeError": "Close error",
  "ab.action.cancelSubscription": "Cancel Subscription",
  "ab.action.resubscribing": "Resubscribing...",
  "ab.action.resubscribe": "Resubscribe",
  "ab.action.converting": "Converting...",
  "ab.action.convertToFull": "Convert to Full Subscription",
  "ab.action.browsePlans": "Browse Plans",
  "ab.cancellationPending": "Access continues until end of billing period",
  "ab.noSubscription.title": "No Active Subscription",
  "ab.noSubscription.subtitle": "Choose a plan to get started with KinShield.",
  "ab.setupJourney.return": "Continue Setting Up Your Account",
  "ab.renewal.label.gracePeriodEnds": "Grace period ends",
  "ab.renewal.sublabel.addPayment":
    "Add a payment method to keep your subscription active",
  "ab.renewal.label.accessUntil": "Access until",
  "ab.renewal.sublabel.willNotRenew":
    "Subscription will not renew after trial ends",
  "ab.renewal.label.trialEnds": "Trial ends",
  "ab.renewal.sublabel.autoRenews": "Auto-renews {date}",
  "ab.renewal.label.nextRenewal": "Next renewal",
  "ab.renewal.sublabel.cancellationScheduled":
    "Cancellation scheduled \u2014 no further charges",
  "ab.renewal.date.endOfBillingPeriod": "End of billing period",
  "ab.renewal.sublabel.subscriptionCancelled":
    "Subscription cancelled \u2014 no further charges",
  "ab.renewal.sublabel.planSwitchesTo":
    "Plan switches to {name}{price} at renewal",
  "ab.access.untilEndOfTrial": "Full access to features until end of trial",
  "ab.access.until": "Full access to features until {date}",
  "ab.modal.planChange.titleDowngrade": "Downgrade Subscription Plan?",
  "ab.modal.planChange.titleChange": "Change Subscription Plan?",
  "ab.modal.planChange.messageDowngrade":
    "Please review the details below before confirming your downgrade.",
  "ab.modal.planChange.messageChange":
    "Are you sure you want to change your subscription plan?",
  "ab.modal.planChange.labelCurrent": "Current",
  "ab.modal.planChange.labelNew": "New",
  "ab.modal.planChange.noteCurrent": "Current plan stays active until {date}",
  "ab.modal.planChange.noteNewRate":
    "New plan and rate take effect at your next renewal",
  "ab.modal.planChange.noteDiscountWarning":
    "Your {pct}% discount may not apply to the new plan",
  "ab.modal.planChange.noteUpgradeImmediate":
    "Upgraded immediately \u2014 prorated charge may apply",
  "ab.modal.planChange.noteChangeImmediate":
    "Updated immediately \u2014 no additional charges",
  "ab.modal.planChange.noteDiscountMayNotCarry":
    "Your {pct}% discount may not carry over",
  "ab.modal.planChange.confirmDowngrade": "Confirm Downgrade",
  "ab.modal.planChange.confirmChange": "Change Plan",
  "ab.modal.planChange.cancelKeep": "Keep Current Plan",
  "ab.modal.cancel.title": "Cancel Subscription?",
  "ab.modal.cancel.messageDefault":
    "Are you sure you want to cancel your subscription?",
  "ab.modal.cancel.messageTrial":
    "Are you sure you want to cancel? Your trial access will continue until the trial period ends \u2014 you will not be charged.",
  "ab.modal.cancel.confirmText": "Cancel Subscription",
  "ab.modal.cancel.cancelText": "Keep Subscription",
  "ab.modal.cancel.reasonPlaceholder":
    "Help us improve: Why are you canceling? (optional)",
  "ab.modal.cancel.keepFeaturesUntil":
    "\u2705 You will keep full access to {plan} features until {date}",
  "ab.modal.cancel.keepTrialFeatures":
    "\u2705 You will keep full access to {plan} features until your trial ends on {date}",
  "ab.modal.cancel.noTrialCharges":
    "\ud83d\udcb3 No charges will be applied \u2014 your trial is free",
  "ab.modal.cancel.willLoseAccess":
    "\ud83deb48 After that date, you will lose access to all paid features including simulations, reports, API access, and additional user slots",
  "ab.modal.cancel.noBillingAfter":
    "\ud83d\udcb3 No further charges will occur after cancellation",
  "ab.modal.cancel.canResubscribe":
    "\ud83d\udd04 You can resubscribe at any time from this page",
  "ab.modal.cancel.noSubText":
    "You can resubscribe at any time from this page.",
  "ab.modal.convert.title": "Convert to Full Subscription?",
  "ab.modal.convert.message":
    "You are about to convert your free trial to a paid subscription.",
  "ab.modal.convert.keepFeatures":
    "\u2705 You will keep all features of your current {plan}",
  "ab.modal.convert.chargeInfo": `\ud83d\udcb3 Your payment method will be charged \${amount}/{period} starting immediately`,
  "ab.modal.convert.autoRenewEnabled":
    "\ud83d\udd04 Auto-renew will remain enabled",
  "ab.modal.convert.autoRenewDisabled":
    "\ud83d\udd04 Auto-renew will remain disabled",
  "ab.modal.convert.cancelAnytime":
    "\u2753 You can cancel anytime from this page",
  "ab.modal.convert.confirmText": "Convert to Paid",
  "ab.modal.convert.cancelText": "Keep Trial",
  "ab.daysLeft": "{n}d left",

  // ── Billing issue banner ───────────────────────────────────────────
  "ab.billing.title": "We couldn't process your payment",
  "ab.billing.failedOn": "Your most recent payment failed on {date}.",
  "ab.billing.failedNoDate": "Your most recent payment failed.",
  "ab.billing.retryOn": "We'll automatically retry on {date}.",
  "ab.billing.updateOrPayNow":
    "Update your payment method or pay now to keep your subscription active.",
  "ab.billing.payNow": "Pay now",
  "ab.billing.updatePaymentMethod": "Update payment method",
  "ab.grace.title": "Grace period active",
  "ab.grace.message.withDate":
    "Full access continues until {date}. Add a payment method to avoid service interruption.",
  "ab.grace.message.noDate":
    "Full access is temporarily available. Add a payment method to avoid service interruption.",
  "ab.grace.nonOwner.withDate":
    "Full access continues until {date}. You do not have permission to manage billing on this account. Please contact the account owner to add or update a payment method before the grace period ends.",
  "ab.grace.nonOwner.noDate":
    "Full access is temporarily available. You do not have permission to manage billing on this account. Please contact the account owner to add or update a payment method as soon as possible.",
  "ab.grace.cta.updatePayment": "Update Payment Method",

  // ── Misc missing keys ─────────────────────────────────────────────
  "ab.period.month": "month",
  "ab.period.year": "year",
  "ab.price.off": "{pct}% off",
  "ab.modal.planChange.users": "{n} users",
  "ab.downgrade.fallbackPlan": "a lower plan",
  "ab.badge.downgradePendingTitle": "Downgrade pending",
  "ab.modal.cancel.endFallback": "end of billing period",
  "selectedPlan.promo.remove": "Remove",

  // ── Payment Methods (PaymentMethods.js) ────────────────────────────
  "paymentMethods.addSuccess": "Payment method added successfully!",
  "paymentMethods.addFailed": "Failed to add payment method",
  "paymentMethods.deletedSuccess": "Payment method deleted successfully",
  "paymentMethods.newDefaultSet":
    "{brand} \u2022\u2022\u2022\u2022{last4} is now your default payment method",
  "paymentMethods.defaultUpdated": "Default payment method updated",
  "paymentMethods.loadFailed": "Failed to load payment methods",
  "paymentMethods.deleteFailed": "Failed to delete payment method",
  "paymentMethods.setDefaultFailed": "Failed to set default payment method",
  "paymentMethods.form.cardholderName": "Cardholder Name",
  "paymentMethods.form.nameOnCard": "Name on card",
  "paymentMethods.form.cardNumber": "Card Number",
  "paymentMethods.form.expiration": "Expiration Date",
  "paymentMethods.form.cvv": "CVV",
  "paymentMethods.form.zipCode": "ZIP Code",
  "paymentMethods.form.setDefault": "Set as default payment method",
  "paymentMethods.form.setDefaultRequired": "(required for first card)",
  "paymentMethods.form.adding": "Adding...",
  "paymentMethods.form.continue": "Continue",
  "paymentMethods.savedMethods": "Saved Payment Methods",
  "paymentMethods.addNewCard": "Add New Card",
  "paymentMethods.addNewTitle": "Add New Payment Method",
  "paymentMethods.activateInfo":
    "Please add a payment method to activate your plan.",
  "paymentMethods.noMethods": "No payment methods saved yet.",
  "paymentMethods.noMethodsHint": "Add a payment method to get started.",
  "paymentMethods.expires": "Expires {month}/{year}",
  "paymentMethods.default": "Default",
  "paymentMethods.setAsDefault": "Set as Default",
  "paymentMethods.remove": "Remove",
  "paymentMethods.activateSubscription": "Activate Subscription",
  "paymentMethods.autoPaySettings": "Auto-Pay Settings",
  "paymentMethods.autoPayLabel": "Enable automatic payments",
  "paymentMethods.autoPayHelp":
    "Automatically charge your default payment method when payments are due",
  "paymentMethods.delete.title": "Delete Payment Method",
  "paymentMethods.delete.message":
    "Are you sure you want to delete this payment method?",
  "paymentMethods.delete.details": "This action cannot be undone.",
  "paymentMethods.delete.confirm": "Delete",
  "paymentMethods.setupJourney.return": "Continue Setting Up Your Account",
  // ── PaymentMethods.js — keys used by the updated component ─────────
  "paymentMethods.loading": "Loading payment methods...",
  "paymentMethods.savedTitle": "Saved Payment Methods",
  "paymentMethods.addTitle": "Add New Payment Method",
  "paymentMethods.activatePlanInfo":
    "Please add a payment method to activate your plan.",
  "paymentMethods.noMethodsTitle": "No payment methods saved yet.",
  "paymentMethods.noMethodsSubtitle": "Add a payment method to get started.",
  "paymentMethods.form.namePlaceholder": "Name on card",
  "paymentMethods.form.expDate": "Expiration Date",
  "paymentMethods.form.cancel": "Cancel",
  "paymentMethods.card.expires": "Expires {month}/{year}",
  "paymentMethods.card.default": "Default",
  "paymentMethods.card.setDefault": "Set as Default",
  "paymentMethods.card.remove": "Remove",
  "paymentMethods.autoPayTitle": "Auto-Pay Settings",
  "paymentMethods.autoPayEnable": "Enable automatic payments",
  "paymentMethods.autoPayDescription":
    "Automatically charge your default payment method when payments are due",
  "paymentMethods.deleteModal.title": "Delete Payment Method",
  "paymentMethods.deleteModal.message":
    "Are you sure you want to delete this payment method?",
  "paymentMethods.deleteModal.details": "This action cannot be undone.",
  "paymentMethods.deleteModal.confirmText": "Delete",
  "paymentMethods.deleteModal.cancelText": "Cancel",
  "paymentMethods.toast.added": "Payment method added successfully!",
  "paymentMethods.toast.addFailed": "Failed to add payment method",
  "paymentMethods.toast.deleted": "Payment method deleted successfully",
  "paymentMethods.toast.defaultSet":
    "{brand} ••••{last4} is now your default payment method",
  "paymentMethods.toast.loadFailed": "Failed to load payment methods",
  "paymentMethods.toast.deleteFailed": "Failed to delete payment method",
  "paymentMethods.toast.defaultUpdated": "Default payment method updated",
  "paymentMethods.toast.defaultFailed": "Failed to set default payment method",
  "paymentMethods.stripeUnavailable":
    "Payments are temporarily unavailable. Our team has been notified. Please try again later or contact support.",

  // ── Payment History (PaymentHistory.js) ────────────────────────────
  "paymentHistory.loadFailed": "Failed to load payment history",
  "paymentHistory.reconcileFailed":
    "Failed to refresh billing history from Stripe",
  "paymentHistory.loading": "Loading payment history...",
  "paymentHistory.description.monthly": "Monthly Subscription",
  "paymentHistory.transactionHistory": "Transaction History",
  "paymentHistory.refresh.label": "Refresh Billing History",
  "paymentHistory.refresh.loading": "Refreshing...",
  "paymentHistory.refresh.title": "Fetch any missing payments from Stripe",
  "paymentHistory.filter.dateRange": "Filter by Date Range",
  "paymentHistory.filter.allTime": "All Time",
  "paymentHistory.filter.last30": "Last 30 days",
  "paymentHistory.filter.last3Months": "Last 3 months",
  "paymentHistory.filter.last6Months": "Last 6 months",
  "paymentHistory.filter.lastYear": "Last year",
  "paymentHistory.filter.status": "Filter by Status",
  "paymentHistory.filter.all": "All",
  "paymentHistory.filter.completed": "Completed",
  "paymentHistory.filter.processing": "Processing",
  "paymentHistory.filter.pending": "Pending",
  "paymentHistory.filter.failed": "Failed",
  "paymentHistory.table.date": "Date",
  "paymentHistory.table.description": "Description",
  "paymentHistory.table.amount": "Amount",
  "paymentHistory.table.amountShort": "Amt",
  "paymentHistory.table.method": "Method",
  "paymentHistory.table.status": "Status",
  "paymentHistory.noHistory": "No payment history available.",
  "paymentHistory.noMatch": "No payments match your filters.",
  "paymentHistory.reconcile.recovered": "{count} missing payment(s) recovered",
  "paymentHistory.reconcile.upToDate": "Billing history is already up to date",
  // ── PaymentHistory.js — keys used by the updated component ─────────
  "paymentHistory.title": "Transaction History",
  "paymentHistory.refreshLabel": "Refresh Billing History",
  "paymentHistory.refreshing": "Refreshing...",
  "paymentHistory.refreshTitle": "Fetch any missing payments from Stripe",
  "paymentHistory.filter.dateLabel": "Filter by Date Range",
  "paymentHistory.filter.statusLabel": "Filter by Status",
  "paymentHistory.filter.last90": "Last 3 months",
  "paymentHistory.filter.last180": "Last 6 months",
  "paymentHistory.filter.last365": "Last year",
  "paymentHistory.toast.loadFailed": "Failed to load payment history",
  "paymentHistory.toast.recovered": "{count} missing payment(s) recovered",
  "paymentHistory.toast.upToDate": "Billing history is already up to date",
  "paymentHistory.toast.refreshFailed":
    "Failed to refresh billing history from Stripe",

  // ── Subscription Actions (useSubscriptionActions.js) ──────────────────────
  "subscriptionActions.toast.selectPlanFirst": "Please select a plan first.",
  "subscriptionActions.toast.addPaymentToActivate":
    "Please add a payment method to activate your plan.",
  "subscriptionActions.toast.planChanged":
    "Successfully changed your plan to {plan}!",
  "subscriptionActions.toast.planActivated": "Successfully activated {plan}!",
  "subscriptionActions.toast.changePlanFailed":
    "Failed to change plan. Please try again.",
  "subscriptionActions.toast.activatePlanFailed":
    "Failed to activate plan. Please try again.",
  "subscriptionActions.toast.addPaymentToSetup":
    "Add a payment method to continue subscription setup.",
  "subscriptionActions.toast.addPaymentToConvert":
    "Please add a payment method to convert your trial.",
  "subscriptionActions.toast.trialConverted":
    "Successfully converted to full {plan} subscription!",
  "subscriptionActions.toast.trialConvertFailed":
    "Failed to convert trial. Please try again.",
  "subscriptionActions.toast.cancelled":
    "Subscription cancelled. You will continue to have full access until the end of your current billing period \u2014 no further charges will apply.",
  "subscriptionActions.toast.cancelFailed":
    "Failed to cancel subscription. Please try again.",
  "subscriptionActions.toast.reactivated":
    "Subscription reactivated! Your plan is active again.",
  "subscriptionActions.toast.downgradeCancelled":
    "Pending downgrade cancelled. You'll keep your current plan.",
  "subscriptionActions.toast.enterPromoCode": "Please enter a promo code.",
  "subscriptionActions.toast.promoScheduled":
    "Promo code scheduled. It takes effect at your next billing date.",
  "subscriptionActions.toast.promoScheduleFailed":
    "Failed to schedule promo code. Please try again.",
  "subscriptionActions.toast.promoCancelled": "Scheduled promo code cancelled.",
  "subscriptionActions.toast.promoCancelFailed":
    "Failed to cancel scheduled promo code. Please try again.",

  // ── Payment Overview (PaymentOverview.js) ──────────────────────────────────
  "paymentOverview.toast.loadFailed": "Failed to load payment information",

  // ── Service Plans (ServicePlans.js) ────────────────────────────────
  "servicePlans.loading": "Loading Service Plans...",
  "servicePlans.errorTitle": "Error Loading Plans",
  "servicePlans.tryAgain": "Try Again",
  "servicePlans.modalTitle": "Choose Your Service Plan",
  "servicePlans.pageTitle": "Choose Your Plan",
  "servicePlans.pageSubtitle":
    "Select the perfect plan for your security awareness needs",
  "servicePlans.featuresTitle": "Features Included:",
  "servicePlans.badge.popular": "Most Popular",
  "servicePlans.badge.current": "Current Plan",
  "servicePlans.badge.switching": "Switching",
  "servicePlans.badge.soon": "soon",
  "servicePlans.btn.current": "Current Plan",
  "servicePlans.btn.downgradePending": "Downgrade Pending",
  "servicePlans.btn.upgrade": "Upgrade",
  "servicePlans.btn.downgrade": "Downgrade",
  "servicePlans.btn.selected": "Selected",
  "servicePlans.btn.select": "Select Plan",
  "servicePlans.btn.back": "Back",
  "servicePlans.btn.goBack": "Go Back",
  "servicePlans.price.free": "Free",
  "servicePlans.price.perMonth": "/month",
  "servicePlans.price.perYear": "/year",
  "servicePlans.price.orPrefix": "or",
  "servicePlans.toast.upgraded": "Upgraded to",
  "servicePlans.toast.downgraded": "Downgraded to",
  "servicePlans.error.loadFailed": "Failed to load service plans",

  // Plan names and descriptions
  "servicePlans.plan.individual.name": "Individual",
  "servicePlans.plan.individual.desc":
    "Perfect for individuals with full featured security awareness training needs.",
  "servicePlans.plan.family.name": "Family",
  "servicePlans.plan.family.desc":
    "Ideal for families wanting to protect their household.",
  "servicePlans.plan.extended_family.name": "Extended Family",
  "servicePlans.plan.extended_family.desc":
    "Complete protection for larger families or small groups with advanced features and priority support.",
  "servicePlans.plan.smb-10.name": "Team (up to 10)",
  "servicePlans.plan.smb-10.desc":
    "Security awareness training and scam protection for small teams of up to 10 employees.",
  "servicePlans.plan.smb-25.name": "Team (up to 25)",
  "servicePlans.plan.smb-25.desc":
    "Security awareness training and scam protection for small teams of up to 25 employees.",
  "servicePlans.plan.smb-50.name": "Business (up to 50)",
  "servicePlans.plan.smb-50.desc":
    "Comprehensive protection and training for growing businesses of up to 50 employees.",
  "servicePlans.plan.smb-75.name": "Business (up to 75)",
  "servicePlans.plan.smb-75.desc":
    "Comprehensive protection and training for businesses of up to 75 employees.",
  "servicePlans.plan.smb-100.name": "Business Plus (up to 100)",
  "servicePlans.plan.smb-100.desc":
    "Full-featured security awareness program for organizations of up to 100 employees.",

  // Plan feature bullets
  "servicePlans.feature.covers1User": "Covers 1 user",
  "servicePlans.feature.realtimeScam":
    "Real-time scam analysis for emails, texts, and social media posts",
  "servicePlans.feature.screenshotScan":
    "Screenshot and image scanning to detect fraudulent content",
  "servicePlans.feature.phoneVerify":
    "Phone number verification to identify scam callers",
  "servicePlans.feature.textConvo":
    "Text conversation analysis to catch long-running romance and investment scams",
  "servicePlans.feature.bizCheck":
    "Business/Person Check to verify legitimacy before transactions",
  "servicePlans.feature.simPhishing":
    "Simulated phishing attacks via email and text",
  "servicePlans.feature.trendingAlerts":
    "Alerts about trending scams and current threats",
  "servicePlans.feature.trainings":
    "Interactive trainings, quizzes, and practical security tips",
  "servicePlans.feature.monthlyReports":
    "Comprehensive monthly reports on progress",
  "servicePlans.feature.freeTrial":
    "30-day free trial with full access to all features",
  "servicePlans.feature.covers3Users": "Covers up to 3 users",
  "servicePlans.feature.everythingIndividual": "Everything in Individual Plan",
  "servicePlans.feature.advancedSimCustom":
    "Advanced customization of simulation scenarios",
  "servicePlans.feature.advancedThreatSim":
    "Advanced threat simulations with customizable difficulty levels",
  "servicePlans.feature.realWorldScenarios":
    "Simulation scenarios based on real-world attacks",
  "servicePlans.feature.moreUsage": "More usage than Individual Plan",
  "servicePlans.feature.weeklyReports": "Weekly/Monthly simulation reports",
  "servicePlans.feature.referralBonus": "Referral bonus",
  "servicePlans.feature.covers6Users": "Covers up to 6 users",
  "servicePlans.feature.everythingFamily": "Everything in Family Plan",
  "servicePlans.feature.myScamShield":
    "Includes \u2018My Scam Shield\u2019 (future feature)",
  "servicePlans.feature.highestUsage": "Highest usage limits",
  "servicePlans.feature.prioritySupport": "Priority customer support",
  "servicePlans.feature.indepthReports":
    "In-depth weekly/monthly simulation reports",
  "servicePlans.feature.earlyAccess":
    "Early access to new features and updates",
  "servicePlans.feature.coversUpTo10Employees": "Covers up to 10 employees",
  "servicePlans.feature.coversUpTo25Employees": "Covers up to 25 employees",
  "servicePlans.feature.coversUpTo50Employees": "Covers up to 50 employees",
  "servicePlans.feature.coversUpTo75Employees": "Covers up to 75 employees",
  "servicePlans.feature.coversUpTo100Employees": "Covers up to 100 employees",
  "servicePlans.feature.everythingTeam": "Everything in the Team plans",
  "servicePlans.feature.adminDashboard":
    "Centralized admin dashboard to manage your team",
  "servicePlans.feature.teamReports":
    "Team-wide phishing simulation and progress reports",
  "servicePlans.feature.auditLogs": "Audit logs for compliance and oversight",
  "servicePlans.feature.freeTrial14": "14-day free trial",

  // ── Choose Plans (ChoosePlans.js) ───────────────────────────────────
  "choosePlans.title": "Choose Your Plan",
  "choosePlans.description":
    "Select the plan that best protects you and your family and enjoy enhanced security protection.",
  "choosePlans.btn.start": "Start Strengthening Your Shield",

  "selectedPlan.title.new": "\ud83c\udfaf Selected Plan",
  "selectedPlan.title.change": "\ud83d\udd04 Plan Change Preview",
  "selectedPlan.alert.youHaveSelected": "You have selected a plan to activate:",
  "selectedPlan.alert.downgrade": "\u2b07\ufe0f Plan Downgrade:",
  "selectedPlan.alert.upgrade": "\u2b06\ufe0f Plan Upgrade:",
  "selectedPlan.alert.change": "\ud83d\udd04 Plan Change:",
  "selectedPlan.alert.downgradeNote":
    "Your current plan stays active until the end of your billing period. The new plan and pricing take effect at your next renewal.",
  "selectedPlan.table.currentPlan": "Current Plan",
  "selectedPlan.table.newPlan": "New Plan",
  "selectedPlan.table.current": "Current",
  "selectedPlan.table.new": "New",
  "selectedPlan.table.planName": "Plan Name",
  "selectedPlan.table.price": "Price",
  "selectedPlan.table.maxUsers": "Max Users",
  "selectedPlan.table.free": "Free",
  "selectedPlan.price.afterTrial": " after free trial ends",
  "selectedPlan.promo.toggle": "\ud83c\udff7\ufe0f Have a promo code?",
  "selectedPlan.promo.label": "Do you have a Promo Code?",
  "selectedPlan.promo.removeTitle": "Remove promo code",
  "selectedPlan.promo.placeholder": "Enter promo code (e.g., SPRING40)",
  "selectedPlan.promo.checking": "Checking...",
  "selectedPlan.promo.apply": "Apply",
  "selectedPlan.promo.cancelTitle": "Cancel",
  "selectedPlan.trial.unavailablePaidActive":
    "Trial not available - you have an active paid subscription",
  "selectedPlan.trial.unavailableAlreadyUsed":
    "Trial not available - you've already used your free trial",
  "selectedPlan.trial.endDate": "Trial will end on {date}",
  "selectedPlan.trial.notExtended": " (same as current trial - not extended)",
  "selectedPlan.trial.cancelRiskFree": "Cancel risk free before trial ends",
  "selectedPlan.trial.availabilityNote": "(30-day trial period available)",
  "selectedPlan.toggle.fullSubscription": "Full Subscription",
  "selectedPlan.toggle.continueWithTrial": "Continue with Trial Period",
  "selectedPlan.toggle.startWithTrial": "Start with Trial Period",
  "selectedPlan.toggle.autoRenewLabel": "Auto-renew?",
  "selectedPlan.toggle.autoRenewMessage":
    "Subscription will automatically renew",
  "selectedPlan.btn.activating": "\ud83d\udd04 Activating...",
  "selectedPlan.btn.changingPlan": "\ud83d\udd04 Changing Plan...",
  "selectedPlan.btn.activatePlan": "\ud83d\ude80 Activate Plan",
  "selectedPlan.btn.subscribePlan": "\ud83d\ude80 Subscribe to New Plan",
  "selectedPlan.btn.chooseDifferent": "\ud83d\udd04 Choose Different Plan",
  "selectedPlan.btn.cancelSelection": "\u274c Cancel Selection",
  "selectedPlan.btn.cancelChange": "\u274c Cancel Change",
  "selectedPlan.details.subscriptionDetails":
    "\ud83d\udccb Subscription Details",
  "selectedPlan.details.downgradeDetails": "\ud83d\udccb Downgrade Details",
  "selectedPlan.details.upgradeDetails": "\ud83d\udccb Upgrade Details",
  "selectedPlan.details.planChangeDetails": "\ud83d\udccb Plan Change Details",
  "selectedPlan.details.activatedImmediately":
    "Activated immediately upon confirmation",
  "selectedPlan.details.cancelAnytime":
    "Cancel anytime from your subscription settings",
  "selectedPlan.details.includesTrial": "Includes {days} days free trial",
  "selectedPlan.details.upgradedImmediately":
    "Upgraded immediately upon confirmation",
  "selectedPlan.details.updatedImmediately":
    "Updated immediately upon confirmation",
  "selectedPlan.details.proratedCharge":
    "You may be charged a prorated amount for the upgrade",
  "selectedPlan.details.noAdditionalCharges":
    "No additional charges will apply",
  "selectedPlan.details.discountMayNotApply":
    "Your current {pct}% discount may not apply to the new plan.",
  "selectedPlan.details.discountMayNotCarry":
    "Your current {pct}% discount may not carry over unless the same promotion applies. Use the promo code field above if you have one.",
  "selectedPlan.details.promoApplied": "Promo code applied \u2705",
  "selectedPlan.details.usePromoHint":
    " Use the promo code field above if you have one.",
  "selectedPlan.downgrade.fullAccessNote":
    "Full access to all current features including {users} user slots",
  "selectedPlan.downgrade.keepUntil": "Now \u2014 {date}",
  "selectedPlan.downgrade.afterDate": "After {date}",
  "selectedPlan.free": "Free",
  "selectedPlan.afterTrial": "after free trial ends",
  "selectedPlan.details.includesDiscount": "(includes discount)",
  "selectedPlan.downgrade.keepYourPlan": "Keep your",
  "selectedPlan.downgrade.plan": "plan",
  "selectedPlan.downgrade.at": "at",
  "selectedPlan.downgrade.switchTo": "Switch to",

  // ── Confirmation Modal (ConfirmationModal.js) ─────────────────────
  "confirmModal.reasonLabel": "Reason (optional)",
  "confirmModal.processing": "Processing...",

  // ── Member User Info (MemberUserInfo.js & sub-components) ──────────
  // Page header
  "usermgmt.title": "User Management",
  "usermgmt.subtitle": "Create, update, and manage member user information",
  "usermgmt.activityMonitoring.title": "User Activity Monitoring",
  "usermgmt.activityMonitoring.subtitle":
    "View member users\u2019 dashboard, analyses, and weekly reports",
  "usermgmt.activityMonitoring.selectPrompt":
    "Select a member from the list to view their activity",
  "usermgmt.activityMonitoring.label.dashboard": "Dashboard",
  "usermgmt.activityMonitoring.label.analyses": "Analysis",
  "usermgmt.activityMonitoring.label.reports": "Weekly Progress Reports",

  // Stat cards
  "usermgmt.stats.selectedUser": "Selected User",
  "usermgmt.stats.none": "None",
  "usermgmt.stats.totalUsers": "Total Users",
  "usermgmt.stats.yourRole": "Your Role",
  "usermgmt.stats.plan": "Plan",
  "usermgmt.stats.planActive": "Active",
  "usermgmt.stats.planNone": "None",

  // Banner
  "usermgmt.banner.viewingEditing": "Viewing / Editing  ",
  "usermgmt.banner.backToMyProfile": "Back to My Profile",

  // Roles
  "usermgmt.role.platformAdmin": "Platform Admin",
  "usermgmt.role.accountAdmin": "Account Admin",
  "usermgmt.role.groupAdmin": "Group Admin",
  "usermgmt.role.member": "Member",

  // Section titles & subtitles
  "usermgmt.section.list.title": "User List",
  "usermgmt.section.list.subtitle": "View and manage all users in your account",
  "usermgmt.section.create.title": "Create User",
  "usermgmt.section.create.subtitle": "Add a new member user to the system",
  "usermgmt.section.update.title": "Update User Info",
  "usermgmt.section.update.subtitle": "Modify existing user information",
  "usermgmt.section.details.title": "Additional Details",
  "usermgmt.section.details.subtitle":
    "Manage additional user profile information. This information is optional and can be used for segmentation, analytics, or personalized simulation purposes.",
  "usermgmt.section.resend.title": "Resend Verification",
  "usermgmt.section.resend.subtitle":
    "Resend the email verification link to member users whose verification email has expired.",

  // Toast messages
  "usermgmt.toast.activated": "Activated {firstName} {lastName}",
  "usermgmt.toast.deactivated": "Deactivated {firstName} {lastName}",
  "usermgmt.toast.activateSuccess": "User activated successfully",
  "usermgmt.toast.deactivateSuccess": "User deactivated successfully",
  "usermgmt.toast.phoneUpdated": "Phone number updated successfully!",
  "usermgmt.toast.phoneVerifyRefreshError":
    "Phone verification completed, but there was an error refreshing the data. Please refresh the page.",
  "usermgmt.toast.resendSuccess": "Verification email resent to {email}",
  "usermgmt.toast.resendFailed": "Failed to resend verification email.",

  // Error messages
  "usermgmt.error.noPermission":
    "You don't have permission to modify this user.",
  "usermgmt.error.authRequired":
    "Authentication required. Please log in again.",
  "usermgmt.error.updateStatusFailed":
    "Failed to update user status. Please try again.",
  "usermgmt.error.selectUserToDelete":
    "Please select at least one user to delete.",
  "usermgmt.error.ownerNotDeleted": "Account owners will not be deleted.",
  "usermgmt.error.deleteFailed": "Failed to delete users. Please try again.",
  "usermgmt.error.noUserSelected":
    "No user selected. Please select a user from the User List.",
  "usermgmt.error.loadDetailsFailed":
    "Failed to load user details. Please try again.",
  "usermgmt.error.loadUserFailed": "Failed to load user data.",
  "usermgmt.error.userNotFound": "User not found.",
  "usermgmt.error.refreshFailed": "Failed to refresh user data.",
  "usermgmt.error.uuidNotFound":
    "User UUID not found. Please try reloading the page.",
  "usermgmt.error.noSubscription":
    "Cannot create user: no active subscription found. Please activate a subscription plan.",
  "usermgmt.error.atLimit":
    "Cannot create user: subscription user limit reached. Please upgrade your plan.",
  "usermgmt.error.createFailed": "Failed to create user",
  "usermgmt.error.unauthorized": "Unauthorized to create new member user",
  "usermgmt.error.createError": "An error occurred while creating the user",
  "usermgmt.error.updateFailed": "Failed to update user",
  "usermgmt.error.updateError": "An error occurred while updating the user",
  "usermgmt.error.saveDetailsFailed": "Failed to save user details",
  "usermgmt.error.saveDetailsError":
    "An error occurred while saving user details",
  "usermgmt.error.loadUnverifiedFailed": "Failed to load unverified users.",

  // Validation
  "usermgmt.validation.firstNameRequired": "First name is required",
  "usermgmt.validation.lastNameRequired": "Last name is required",
  "usermgmt.validation.emailRequired": "Email is required",
  "usermgmt.validation.emailInvalid": "Please enter a valid email address",
  "usermgmt.validation.phoneRequired": "Phone number is required",
  "usermgmt.validation.phoneInvalid":
    "Phone number must be in E.164 format (e.g., +1234567890)",
  "usermgmt.validation.groupRequired": "Please select a group",

  // Create section subscription info
  "usermgmt.create.loadingSubscription": "Loading subscription information...",
  "usermgmt.create.noSubscription": "No active subscription!",
  "usermgmt.create.noSubscriptionMsg":
    "An active subscription plan is required to create users.",
  "usermgmt.create.activateSubscription": "Activate Subscription",
  "usermgmt.create.contactSupport": "or contact support to get started.",
  "usermgmt.create.atLimit": "User limit reached!",
  "usermgmt.create.atLimitMsg":
    "Your current plan allows up to {maxUsers} users.",
  "usermgmt.create.atLimitUpgrade":
    "Please upgrade your subscription to add more users, or contact support for assistance.",
  "usermgmt.create.upgradeSubscription": "Upgrade Subscription",
  "usermgmt.create.highestTier":
    "You are on the highest subscription tier. Contact support to discuss custom enterprise solutions for additional users.",
  "usermgmt.create.nearLimit": "Approaching user limit!",
  "usermgmt.create.nearLimitMsg":
    "You can create {remaining} more user(s) before reaching your plan's limit of {maxUsers} users.",
  "usermgmt.create.nearLimitConsider":
    "Consider upgrading your subscription plan to accommodate more users.",
  "usermgmt.create.viewSubscriptions": "View Subscriptions",
  "usermgmt.create.continueSetup": "Continue Setting Up Your Account",

  // Security warning boxes
  "usermgmt.warning.phoneConflict.title": "Phone Number Verification Conflict",
  "usermgmt.warning.phoneConflict.details":
    "This phone number is currently being used for verification by another user. Please try a different phone number or contact support if you believe this is an error.",
  "usermgmt.warning.emailRequired.title": "Email Verification Required",
  "usermgmt.warning.emailRequired.message":
    "Verification email sent to your new email address.",
  "usermgmt.warning.emailRequired.details":
    "Please check your inbox and click the verification link to complete the email change.",
  "usermgmt.warning.emailConflict.title": "Email Address Verification Conflict",
  "usermgmt.warning.emailConflict.details":
    "This email address is currently being used for verification by another user. Please try a different email address or contact support if you believe this is an error.",
  "usermgmt.warning.securityRequired.title": "Security Verification Required",
  "usermgmt.warning.securityRequired.message":
    "For your security, please change your email and phone number separately.",
  "usermgmt.warning.securityRequired.details":
    "Each change requires individual verification to ensure account security. Your values have been reverted to the original settings.",

  // Update success toasts
  "usermgmt.toast.updateSelf": "Your info has been updated successfully.",
  "usermgmt.toast.updateOther":
    "{firstName}'s info has been updated successfully.",
  "usermgmt.toast.detailsSelf": "Your details have been updated successfully.",
  "usermgmt.toast.detailsOther":
    "{firstName}'s details have been updated successfully.",

  // Phone change request toast
  "usermgmt.toast.phoneChangePending":
    "Phone change request sent! Please enter the verification code that was sent to your new phone number.",

  // Resend verification panel
  "usermgmt.resend.infoText":
    "If a member user's email verification link has expired, you can resend it here. This will reset their verification state and send a fresh email with a new 24-hour link.",
  "usermgmt.resend.loading": "Loading unverified users…",
  "usermgmt.resend.allVerified":
    "All member users have verified their email. No action required.",
  "usermgmt.resend.countFound": "{count} unverified member user{plural} found",
  "usermgmt.resend.group": "Group:",
  "usermgmt.resend.unverified": "Unverified",
  "usermgmt.resend.sending": "Sending…",
  "usermgmt.resend.resendEmail": "Resend Email",
  "usermgmt.resend.refreshList": "Refresh List",

  // Delete confirmation modal
  "usermgmt.modal.deleteTitle": "Delete Users",
  "usermgmt.modal.deleteMessage":
    "Are you sure you want to delete {count} user(s)?",
  "usermgmt.modal.deleteSingleMessage":
    "Are you sure you want to delete this user?",
  "usermgmt.modal.deleteDetails":
    "This action cannot be undone. User data will be permanently removed from the system.",

  // Deactivate confirmation modal
  "usermgmt.modal.deactivateTitle": "Deactivate User",
  "usermgmt.modal.deactivateMessage":
    "Are you sure you want to deactivate this user?",
  "usermgmt.modal.deactivateDetails":
    "The user will lose access to the system. You can reactivate them later.",
  "usermgmt.modal.deactivateConfirm": "Deactivate",

  // UserForm
  "usermgmt.form.basicInfo": "Basic Information",
  "usermgmt.form.noSelectedUser":
    "No selected user. Please ensure you have selected a user from the User List.",
  "usermgmt.form.firstName": "First Name",
  "usermgmt.form.lastName": "Last Name",
  "usermgmt.form.emailAddress": "Email Address",
  "usermgmt.form.phoneNumber": "Phone Number",
  "usermgmt.form.verifyPhoneChange": "Verify Phone Change",
  "usermgmt.form.assignGroup": "Assign to Group",
  "usermgmt.form.loadingGroups": "Loading groups...",
  "usermgmt.form.errorLoadingGroups": "Error loading groups:",
  "usermgmt.form.selectGroup": "Select a group...",
  "usermgmt.form.groupRequired": "Please select a group for the new user",
  "usermgmt.form.creating": "Creating...",
  "usermgmt.form.updating": "Updating...",
  "usermgmt.form.createUser": "Create User",
  "usermgmt.form.updateUser": "Update User",
  "usermgmt.form.changePending": "Change pending verification",
  "usermgmt.form.verified": "Verified",
  "usermgmt.form.notVerified": "Not verified",
  "common.retrying": "Retrying...",

  // UserDetails
  "usermgmt.details.city": "City",
  "usermgmt.details.cityPlaceholder": "Enter your city",
  "usermgmt.details.state": "State",
  "usermgmt.details.statePlaceholder": "Enter your state",
  "usermgmt.details.country": "Country",
  "usermgmt.details.countryPlaceholder": "Enter your country",
  "usermgmt.details.ageRange": "Age Range",
  "usermgmt.details.ageRangePlaceholder": "Enter your age range",
  "usermgmt.details.selectAgeRange": "Select age range",
  "usermgmt.details.profession": "Profession",
  "usermgmt.details.professionPlaceholder": "Enter your profession",
  "usermgmt.details.hobbies": "Hobbies",
  "usermgmt.details.hobbiesPlaceholder": "Enter your hobby",
  "usermgmt.details.additionalInfo": "Additional Info",
  "usermgmt.details.additionalInfoPlaceholder":
    "Enter any additional information about yourself",
  "usermgmt.details.saving": "Saving...",
  "usermgmt.details.saveDetails": "Save Details",

  // UserCard — actions dropdown
  "usermgmt.card.actionsBtn": "Actions",
  "usermgmt.card.configSimProfile": "Configure Simulation Profile",
  "usermgmt.card.monitorActivity": "Monitor Activity",

  // UserCard
  "usermgmt.card.selectUserTitle": "Select User",
  "usermgmt.card.selectForDeletion": "Select user for deletion",
  "usermgmt.card.accountAdmin": "Account Admin",
  "usermgmt.card.accountAdminBadge": "Account Admin",
  "usermgmt.card.unverified": "Unverified",
  "usermgmt.card.active": "Active",
  "usermgmt.card.inactive": "Inactive",
  "usermgmt.card.deactivateTooltip":
    "Deactivated users retain login access but cannot use application features",
  "usermgmt.card.activateTooltip": "Activate user",
  "usermgmt.card.deactivateTitle": "Deactivate User",
  "usermgmt.card.deactivate": "Deactivate",
  "usermgmt.card.activate": "Activate",
  "usermgmt.card.viewProfile": "Edit Profile",
  "usermgmt.card.edit": "Edit",
  "usermgmt.card.delete": "Delete",
  "usermgmt.card.group": "Group:",

  // UserListControls
  "usermgmt.controls.selectUserHelp":
    "Select a user to view and modify their information, personal details, simulation settings, and more.",
  "usermgmt.controls.deactivatedNote":
    "Note: Deactivated users retain login access but cannot use application features.",
  "usermgmt.controls.filterLabel": "Filter Users:",
  "usermgmt.controls.filterAll": "All Users",
  "usermgmt.controls.filterActive": "Active Users",
  "usermgmt.controls.filterInactive": "Inactive Users",
  "usermgmt.controls.filterUnverified": "Unverified Users",
  "usermgmt.controls.refreshTitle": "Refresh user list",
  "usermgmt.controls.userListLabel": "User List",

  // BulkActions
  "usermgmt.bulk.title": "Bulk Actions ({count} selected)",
  "usermgmt.bulk.deleteSelected": "Delete Selected",
  "usermgmt.bulk.ownerNote":
    "Note: Account owners cannot be deleted and will be excluded.",

  // UserList
  "usermgmt.list.loading": "Loading users...",
  "usermgmt.list.noUsers": "No users found.",

  // PhoneChangeVerificationPopup
  "usermgmt.phone.title": "Verify Phone Number Change",
  "usermgmt.phone.greeting": "Hello, {firstName}!",
  "usermgmt.phone.prompt":
    "Please verify your phone number change by entering the verification code sent to:",
  "usermgmt.phone.fallbackPhone": "your new phone number",
  "usermgmt.phone.codeLabel": "Verification Code",
  "usermgmt.phone.codePlaceholder": "Enter 6-digit code",
  "usermgmt.phone.verifying": "Verifying...",
  "usermgmt.phone.verifyBtn": "Verify Phone Change",
  "usermgmt.phone.sending": "Sending...",
  "usermgmt.phone.resendCode": "Resend Code",
  "usermgmt.phone.cancelChange": "Cancel Phone Change",
  "usermgmt.phone.note":
    'Note: If you don\'t receive the SMS, please check that your phone can receive text messages and try the "Resend Code" button.',
  "usermgmt.phone.attempts": "Attempts: {current} / {max}",
  "usermgmt.phone.noPendingChange":
    "No pending phone change found. Please start a new phone change request from your account settings.",
  "usermgmt.phone.verificationExpired":
    "Your phone verification has expired. Please start a new phone change request from your account settings.",
  "usermgmt.phone.loadStatusFailed":
    "Failed to load verification status. Please try again.",
  "usermgmt.phone.enterCode": "Please enter the verification code.",
  "usermgmt.phone.invalidCode": "Invalid verification code. Please try again.",
  "usermgmt.phone.noPendingChangeShort":
    "No pending phone change found. Please start a new phone change request.",
  "usermgmt.phone.verificationFailed": "Verification failed. Please try again.",
  "usermgmt.phone.noPendingPhone":
    "No pending phone number found. Please start a new phone change request.",
  "usermgmt.phone.sendFailed":
    "Failed to send verification code. Please try again later.",
  "usermgmt.phone.resendFailed":
    "Failed to resend verification code. Please try again.",
  "usermgmt.phone.cancelFailed":
    "Failed to cancel phone change. Please try again.",
  "usermgmt.phone.cancelSuccess": "Phone change request has been cancelled.",
  "usermgmt.phone.codeSent":
    "A new verification code has been sent to your pending phone number.",
  "usermgmt.phone.verifySuccess":
    "Phone change verification completed successfully! Your phone number has been updated.",

  // PhoneChangeVerificationPopup — alias/renamed keys used in updated component
  "usermgmt.phone.modalTitle": "Verify Phone Number Change",
  "usermgmt.phone.hello": "Hello, {firstName}!",
  "usermgmt.phone.userFallback": "there",
  "usermgmt.phone.instructions":
    "Please verify your phone number change by entering the verification code sent to:",
  "usermgmt.phone.yourNewPhone": "your new phone number",
  "usermgmt.phone.cancelBtn": "Cancel Phone Change",
  "usermgmt.phone.smsNote":
    'Note: If you don\'t receive the SMS, please check that your phone can receive text messages and try the "Resend Code" button.',
  "usermgmt.phone.sendCodeFailed":
    "Failed to send verification code. Please try again later.",
  "usermgmt.phone.verifyFailed": "Verification failed. Please try again.",

  // UserList
  "usermgmt.list.noUsersFound": "No users found.",
  "usermgmt.list.noMembers": "No members found.",

  // User list column headers (MemberUserInfo_v2)
  "usermgmt.list.col.name": "Name",
  "usermgmt.list.col.email": "Email",
  "usermgmt.list.col.phone": "Phone",
  "usermgmt.list.col.role": "Role",
  "usermgmt.list.col.status": "Status",
  "usermgmt.list.col.actions": "Actions",

  // UserCard
  "usermgmt.card.activateTitle": "Activate User",

  // BulkActions
  "usermgmt.bulk.ownersNote":
    "Note: Account owners cannot be deleted and will be excluded.",

  // Roster panel (left sidebar — search + add member button)
  "usermgmt.roster.backToList": "Back to User List",
  "usermgmt.roster.searchPlaceholder": "Search members...",
  "usermgmt.roster.addMember": "Add Member",

  // Detail panel (right panel header)
  "usermgmt.detail.nowViewing": "Now viewing",

  // Tabs
  "usermgmt.tab.profile": "Profile",
  "usermgmt.tab.additionalDetails": "Additional Details",
  "usermgmt.tab.activity": "Activities",
  "usermgmt.tab.detailTabsLabel": "Member detail tabs",
  "usermgmt.tab.profileTabsLabel": "Profile tabs",

  // Actions
  "usermgmt.action.saveChanges": "Save Changes",

  // Detail panel
  "usermgmt.detail.emptyState":
    "Select a member from the list to view and edit their profile.",

  // My Profile (non-admin solo view)
  "usermgmt.myProfile.title": "My Profile",
  "usermgmt.myProfile.subtitle":
    "Manage your personal information and preferences",

  // Activate modal
  "usermgmt.modal.activateTitle": "Activate User",
  "usermgmt.modal.activateMessage":
    "Are you sure you want to activate this user?",
  "usermgmt.modal.activateDetails":
    "Activating this user will restore their access to the platform.",
  "usermgmt.modal.activateConfirm": "Activate",

  // Delete success toast
  "usermgmt.toast.deleteSuccess": "User deleted successfully.",

  // UserCard consent chips
  "usermgmt.card.selected": "selected",
  "usermgmt.card.selectedAriaLabel": "Selected",
  "usermgmt.card.dashboard": "Dashboard",
  "usermgmt.card.dashboardSharedTitle": "Dashboard: Shared with owner",
  "usermgmt.card.dashboardPrivateTitle": "Dashboard: Private",
  "usermgmt.card.analysis": "Analysis",
  "usermgmt.card.analysisSharedTitle": "Analysis: Shared with owner",
  "usermgmt.card.analysisPrivateTitle": "Analysis: Private",
  "usermgmt.card.reports": "Reports",
  "usermgmt.card.reportsSharedTitle": "Reports: Shared with owner",
  "usermgmt.card.reportsPrivateTitle": "Reports: Private",
  "usermgmt.card.resendVerificationTitle":
    "Resend verification email to {email}",
  "usermgmt.card.deleteTitle": "Delete {firstName} {lastName}",

  // Roster / left panel
  "usermgmt.roster.listLabel": "Member roster",
  "usermgmt.roster.newMemberInfoBtn": "What to expect after adding a member",

  // Filters
  "usermgmt.controls.filterGroupLabel": "Filter users by status",

  // Add Member slide-in sheet
  "usermgmt.sheet.addMemberTitle": "Add Member",
  "usermgmt.sheet.slotsUsed": "slots used",
  "usermgmt.sheet.seatsUsed": "seats used",
  "usermgmt.sheet.seatsAvailable": "available",

  // Activity tab
  "usermgmt.activity.thisMember": "this member",
  "usermgmt.activity.intro":
    "Review {name}'s Dashboard, scam analyses, and weekly progress reports.",
  "usermgmt.activity.dashboard": "Dashboard Access",
  "usermgmt.activity.consentGranted": "Consent granted",
  "usermgmt.activity.consentNotGranted": "Consent not granted",
  "usermgmt.activity.viewDashboardTitle": "View {name}'s dashboard",
  "usermgmt.activity.noAccessTitle": "{name} has not granted dashboard access",
  "usermgmt.activity.viewDashboard": "View Dashboard",
  "usermgmt.activity.noAccess": "No Access Granted",
  "usermgmt.activity.analyses": "Analyses",
  "usermgmt.activity.analysesDetail": "Analysis Access",
  "usermgmt.activity.viewAnalysesTitle": "View {name}'s analyses",
  "usermgmt.activity.viewAnalyses": "View Analyses",
  "usermgmt.activity.reports": "Weekly Progress Reports Access",
  "usermgmt.activity.readOnly": "Read-only",
  "usermgmt.activity.viewReportsTitle": "View {name}'s reports",
  "usermgmt.activity.viewReports": "View Reports",
  "usermgmt.activity.backToUserMgmt": "Back to User Management",

  // Member analyses panel
  "usermgmt.analyses.backToActivities": "Back to Activities",
  "usermgmt.analyses.panelTitle": "{name}'s Saved Analyses",
  "usermgmt.analyses.readOnly": "Read-Only",
  "usermgmt.analyses.tabEmails": "Forwarded Emails",
  "usermgmt.analyses.tabTexts": "Forwarded Texts",
  "usermgmt.analyses.tabSocialMedia": "Social Media",
  "usermgmt.analyses.tabImages": "Uploaded Images",
  "usermgmt.analyses.loading": "Loading {type}…",
  "usermgmt.analyses.retry": "Retry",
  "usermgmt.analyses.empty": "No saved {type} found for {name}.",
  "usermgmt.analyses.loadFailed": "Failed to load analyses. Please try again.",
  "usermgmt.analyses.pageOf": "Page {page} of {total}",
  "usermgmt.analyses.previous": "Previous",
  "usermgmt.analyses.next": "Next",
  "usermgmt.analyses.untitled": "Untitled Analysis",
  "usermgmt.analyses.scamDetected": "Scam Detected",
  "usermgmt.analyses.appearsSafe": "Appears Safe",
  "usermgmt.analyses.detailTitle.email": "Forwarded Email Analysis",
  "usermgmt.analyses.detailTitle.sms": "Forwarded Text Analysis",
  "usermgmt.analyses.detailTitle.image": "Uploaded Image Analysis",
  "usermgmt.analyses.detailTitle.socialMedia": "Social Media Analysis",
  "usermgmt.analyses.verdict.analysis": "Analysis",
  "usermgmt.analyses.verdict.riskFactors": "Risk Factors",
  "usermgmt.analyses.verdict.recommendations": "Recommendations",
  "usermgmt.analyses.verdict.submittedContent": "Submitted Content",
  "usermgmt.analyses.verdict.risk": "{level} Risk",
  "usermgmt.analyses.smsScamDetected": "SMS Scam Detected",
  "usermgmt.analyses.smsAnalyzed": "Forwarded SMS Analyzed",
  "usermgmt.analyses.emailScamDetected": "Email Scam Detected",
  "usermgmt.analyses.emailAnalyzed": "Forwarded Email Analyzed",
  "usermgmt.analyses.imageScamDetected": "Image Scam Detected",
  "usermgmt.analyses.imageAnalyzed": "Uploaded Image Analyzed",
  "usermgmt.analyses.socialMediaScamDetected": "Social Media Scam Detected",
  "usermgmt.analyses.socialMediaAnalyzed": "Social Media Analyzed",

  // NewMemberInfoModal
  "usermgmt.newMember.title": "Member Created",
  "usermgmt.newMember.subtitle": "Account set up for",
  "usermgmt.newMember.closeAriaLabel": "Close",
  "usermgmt.newMember.intro":
    "Here is what the new member needs to do before they can log in. If they ask you for help, use the steps below to guide them.",
  "usermgmt.newMember.stepsAriaLabel": "Member onboarding steps",
  "usermgmt.newMember.step1.title": "Check email for a verification link",
  "usermgmt.newMember.step1.body.sentTo": "A verification email was sent to",
  "usermgmt.newMember.step1.body.checkInbox": ". The member should check their",
  "usermgmt.newMember.step1.body.inbox": "inbox",
  "usermgmt.newMember.step1.body.alsoCheck": "\u2014 and also their",
  "usermgmt.newMember.step1.body.spamJunk": "spam / junk",
  "usermgmt.newMember.step1.body.suffix":
    "folder if they don't see it within a couple of minutes.",
  "usermgmt.newMember.step2.title": "Click the verification link",
  "usermgmt.newMember.step2.body":
    "Inside the email there is a single-click verification link. The member must click it \u2014 it confirms their email address and automatically takes them to the password setup page.",
  "usermgmt.newMember.step3.title": "Set up a password",
  "usermgmt.newMember.step3.body":
    "On the password setup page the member chooses a strong password. This is the password they will use every time they log in. The link is single-use and will expire, so they should do this promptly.",
  "usermgmt.newMember.step4.title": "Log in and start using the app",
  "usermgmt.newMember.step4.body":
    "Once the password is set, the member can log in with their email address and the password they just created. No further action is needed from you.",
  "usermgmt.newMember.tip.title": "If the member never receives the email",
  "usermgmt.newMember.tip.body.goto": "Go to the",
  "usermgmt.newMember.tip.body.userList": "User List",
  "usermgmt.newMember.tip.body.findMember":
    ", find the member (they will show as",
  "usermgmt.newMember.tip.body.unverified": "unverified",
  "usermgmt.newMember.tip.body.openMenu":
    "), open their actions menu, and choose",
  "usermgmt.newMember.tip.body.resendAction": "Resend Verification Email",
  "usermgmt.newMember.tip.body.howMany":
    "You can do this as many times as needed.",
  "usermgmt.newMember.gotIt": "Got it",

  // Member dashboard panel
  "usermgmt.dashboard.panelTitle": "{name}'s Dashboard",

  // Member reports panel
  "usermgmt.reports.panelTitle": "{name}'s Reports",
  "usermgmt.reports.loading": "Loading reports…",
  "usermgmt.reports.loadFailed": "Failed to load reports. Please try again.",
  "usermgmt.reports.empty": "No reports found for {name}.",
  "usermgmt.reports.colType": "Type",
  "usermgmt.reports.colPeriod": "Period",
  "usermgmt.reports.colScore": "Score",
  "usermgmt.reports.colCreated": "Created",
  "usermgmt.reports.typePersonal": "Personal",
  "usermgmt.reports.backToList": "Back to Reports",
  "usermgmt.reports.detailLoadFailed":
    "Failed to load report. Please try again.",

  // ── Cyber-Insurance Readiness report ───────────────────────────────
  "ci.pageTitle": "Cyber-Insurance Readiness",
  "ci.pageSubtitle":
    "A shareable, underwriting-ready snapshot of your organization's human-layer security posture.",

  // List view
  "ci.list.title": "Generated Reports",
  "ci.list.subtitle":
    "Account-level readiness reports you can share with your insurer or broker during underwriting and renewals.",
  "ci.list.newReport": "+ New Report",
  "ci.list.emptyTitle": "No reports yet.",
  "ci.list.emptyBody":
    "Generate your first cyber-insurance readiness report to see how your organization measures up on the human-layer controls underwriters care about most.",
  "ci.list.generate": "Generate Report",
  "ci.list.colPeriod": "Period",
  "ci.list.colReadiness": "Readiness",
  "ci.list.colLevel": "Level",
  "ci.list.colSeats": "Seats",
  "ci.list.colGenerated": "Generated",
  "ci.action.view": "View",
  "ci.action.pdf": "PDF",
  "ci.action.delete": "Delete",
  "ci.action.deleting": "Deleting…",

  // Generate view
  "ci.back": "← Back to reports",
  "ci.generate.title": "New Cyber-Insurance Readiness Report",
  "ci.generate.intro":
    "KinShield automatically measures your human-layer controls (MFA, security-awareness training, phishing-simulation results, and threat engagement) across all enrolled seats. Confirm the administrative controls below to complete the picture underwriters expect.",
  "ci.generate.period": "Reporting period",
  "ci.generate.attestTitle": "Administrative control attestations",
  "ci.generate.attestIntro":
    "These controls aren't measured by KinShield. Check the ones your organization has in place; add notes for context. Honest answers make the report more useful to your insurer.",
  "ci.generate.notesPlaceholder": "Optional notes",
  "ci.generate.cancel": "Cancel",
  "ci.generate.generating": "Generating…",
  "ci.generate.submit": "Generate Report",

  // Reporting-period options
  "ci.period.30": "Last 30 days",
  "ci.period.60": "Last 60 days",
  "ci.period.90": "Last 90 days",
  "ci.period.180": "Last 180 days",
  "ci.period.365": "Last 12 months",

  // Administrative control attestations
  "ci.attest.edr.label": "Endpoint Detection & Response / Antivirus",
  "ci.attest.edr.help":
    "EDR or antivirus is deployed across company endpoints.",
  "ci.attest.backups.label": "Regular, Tested Backups",
  "ci.attest.backups.help":
    "Critical data is backed up regularly and restores are tested.",
  "ci.attest.patching.label": "Patch & Vulnerability Management",
  "ci.attest.patching.help":
    "Operating systems and software are patched on a timely basis.",
  "ci.attest.email_filtering.label": "Email Security / Spam Filtering",
  "ci.attest.email_filtering.help":
    "Inbound email is filtered for spam, malware, and phishing.",
  "ci.attest.incident_response.label": "Incident Response Plan",
  "ci.attest.incident_response.help":
    "A documented incident response plan exists and is reviewed.",
  "ci.attest.access_control.label": "Least-Privilege Access Controls",
  "ci.attest.access_control.help":
    "Access is granted on a least-privilege basis and reviewed.",

  // Detail view
  "ci.detail.companyFallback": "Company",
  "ci.detail.enrolledSeats": "enrolled seats",
  "ci.detail.employees": "employees",
  "ci.detail.controlsTitle": "Human-Layer Security Controls",
  "ci.detail.noData": "NO DATA",
  "ci.control.mfa": "Multi-Factor Authentication",
  "ci.control.training": "Security Awareness Training",
  "ci.control.phishing": "Phishing Simulation Results",
  "ci.control.threats": "Threat Detection & Engagement",
  "ci.metric.mfaCoverage": "MFA coverage",
  "ci.metric.seatsWithMfa": "Seats with MFA",
  "ci.metric.membersTrained": "Members trained",
  "ci.metric.completionRate": "Completion rate",
  "ci.metric.trainingsCompleted": "Trainings completed",
  "ci.metric.simulationsDelivered": "Simulations delivered",
  "ci.metric.clickRate": "Click rate",
  "ci.metric.reportRate": "Report rate",
  "ci.metric.analysesPerformed": "Analyses performed",
  "ci.metric.scamsDetected": "Scams / threats detected",
  "ci.metric.seatsOf": "of",
  "ci.detail.attestTitle": "Administrative Control Attestations",
  "ci.detail.colControl": "Control",
  "ci.detail.colAttested": "Attested",
  "ci.detail.colNotes": "Notes",
  "ci.yes": "Yes",
  "ci.no": "No",
  "ci.detail.recommendations": "Recommendations",
  "ci.detail.frameworkTitle": "Framework Mapping",
  "ci.detail.colFramework": "Framework",
  "ci.detail.colControlId": "Control",
  "ci.detail.colName": "Name",
  "ci.detail.colEvidence": "KinShield Evidence",
  "ci.detail.downloadPdf": "Download PDF",
  "ci.detail.preparing": "Preparing…",

  // Recommendation messages ({code} maps to backend Recommendation.code)
  "ci.rec.mfa":
    "Enforce multi-factor authentication for the remaining {seats} seat(s) — MFA is the single control most underwriters require for coverage.",
  "ci.rec.training":
    "Increase security-awareness training completion to at least 80% of seats; assign required modules with due dates to close the gap.",
  "ci.rec.phishingNoData":
    "Run phishing simulations across the team — click and reporting rates are a primary input to underwriting and demonstrate active risk reduction.",
  "ci.rec.phishingClickRate":
    "Reduce the phishing click rate (currently {rate}%) below 5% through targeted follow-up training for repeat clickers.",
  "ci.rec.attestation":
    "Complete the self-attestation section (EDR, backups, patching, incident-response plan) so the report is ready to submit to your broker.",
  "ci.rec.strong":
    "Strong posture across all measured controls — keep MFA enforced and maintain your training and simulation cadence to sustain favorable renewal terms.",

  // Readiness level labels
  "ci.level.EXCELLENT": "Excellent",
  "ci.level.STRONG": "Strong",
  "ci.level.MODERATE": "Moderate",
  "ci.level.NEEDS_IMPROVEMENT": "Needs Improvement",

  // Headline summary
  "ci.summary.default":
    "{company} has a cyber-insurance readiness score of {score}/100 ({level}), based on {seats} protected seats. MFA is enabled on {mfaPct}% of seats, {trainingPct}% have completed security-awareness training, and the phishing-simulation click rate is {clickRate}.",
  "ci.summary.clickNotMeasured": "not yet measured",

  // Framework mapping rows
  "ci.fw.nist_ac.name": "Identity Management & Access Control (MFA)",
  "ci.fw.nist_ac.evidence": "MFA coverage across all account seats",
  "ci.fw.nist_at.name": "Security Awareness & Training",
  "ci.fw.nist_at.evidence": "Security-awareness training completion per seat",
  "ci.fw.nist_cm.name": "Security Continuous Monitoring",
  "ci.fw.nist_cm.evidence":
    "Phishing-simulation results and scam-detection activity",
  "ci.fw.cis_6.name": "Access Control Management",
  "ci.fw.cis_6.evidence":
    "Multi-factor authentication enablement and method breakdown",
  "ci.fw.cis_14.name": "Security Awareness & Skills Training",
  "ci.fw.cis_14.evidence":
    "Training completion plus phishing-simulation click/report rates",

  // Toasts / confirmations
  "ci.toast.loadFailed": "Unable to load reports. Please try again.",
  "ci.toast.generated": "Cyber-insurance readiness report generated.",
  "ci.toast.generateFailed": "Unable to generate report. Please try again.",
  "ci.toast.openFailed": "Unable to open report.",
  "ci.toast.pdfUnavailable": "PDF is not available for this report yet.",
  "ci.toast.pdfFailed": "Unable to download the PDF.",
  "ci.confirm.delete": "Delete this report permanently? This cannot be undone.",
  "ci.toast.deleted": "Report deleted.",
  "ci.toast.deleteFailed": "Unable to delete the report.",

  // ── Security Settings ──────────────────────────────────────────────
  "security.mfa.title": "Multi-Factor Authentication",
  "security.mfa.demoUnavailable":
    "MFA settings are not available in Demo Mode.",
  "security.mfa.enabled": "Two-Factor Authentication Enabled",
  "security.mfa.methodLabel": "Method:",
  "security.mfa.phoneLabel": "Phone",
  "security.mfa.methodSms": "SMS",
  "security.mfa.methodEmail": "Email",
  "security.mfa.regenBackupCodes": "Regenerate Backup Codes",
  "security.mfa.disable": "Disable MFA",
  "security.mfa.disableConfirmTitle": "Disable Two-Factor Authentication?",
  "security.mfa.disableConfirmText": "Enter your password to confirm:",
  "security.mfa.passwordPlaceholder": "Password",
  "security.mfa.viewChangeMethods": "View or Change Authentication Method",
  "security.mfa.availableMethods": "Available Authentication Methods",
  "security.mfa.smsMethod": "SMS Text Message",
  "security.mfa.smsDescription": "Receive codes via SMS",
  "security.mfa.emailMethod": "Email",
  "security.mfa.emailDescription": "Receive codes via email",
  "security.mfa.activeBadge": "Active",
  "security.mfa.appMethod": "Authentication App",
  "security.mfa.appDescription": "Use authenticator app",
  "security.mfa.setupDescription":
    "Add an extra layer of security by requiring a verification code when signing in",
  "security.mfa.enabledSuccess":
    "Two-factor authentication enabled successfully!",
  "security.mfa.disabledSuccess":
    "Two-factor authentication disabled successfully",
  "security.mfa.enterPassword": "Please enter your password",
  "security.mfa.disableFailed": "Failed to disable MFA",
  "security.mfa.regenPasswordPrompt":
    "Enter your password to regenerate backup codes:",
  "security.mfa.regenSuccess": "Backup codes regenerated successfully",
  "security.mfa.regenFailed": "Failed to regenerate backup codes",
  "security.password.title": "Password Security",
  "security.password.change": "Change Password",

  // ── Notifications Settings ─────────────────────────────────────────
  "notifications.email.title": "Email Notifications",
  "notifications.email.securityTitle": "Security Announcements",
  "notifications.email.securityDesc":
    "Important security notifications, including password and MFA changes and new login or device alerts",
  "notifications.email.trainingTitle": "Training Reminders",
  "notifications.email.trainingDesc":
    "Simulation training updates and reminders",
  "notifications.email.weeklyTitle": "Weekly Reports",
  "notifications.email.weeklyDesc":
    "Weekly summary of your security training progress",
  "notifications.sms.title": "SMS Communications",
  "notifications.sms.loading": "Loading SMS preferences...",
  "notifications.sms.consentTitle": "SMS Consent Status",
  "notifications.sms.enabledFor": "SMS notifications are enabled for {phone}",
  "notifications.sms.notOptedIn": "You have not opted in to SMS notifications",
  "notifications.sms.noPhone":
    "No phone number found in your profile. Please add a phone number to enable SMS notifications.",
  "notifications.sms.enableTitle": "Enable SMS Notifications",
  "notifications.sms.enableDesc":
    "Opt in to receive security announcements and important notifications via SMS at {phone}. You can opt out at any time.",
  "notifications.sms.optInButton": "Opt In to SMS Notifications",
  "notifications.sms.optOutConfirm":
    "Opt out of SMS notifications? You will no longer receive any text messages from KinShield.",
  "notifications.sms.optInConfirm":
    "Consent to receive SMS notifications from KinShield? Standard message and data rates may apply. You can opt out at any time.",
  "notifications.sms.verifyTitle": "Verify Your Phone Number",
  "notifications.sms.verifyDesc":
    "A verification code has been sent to {phone}. Enter it below to activate SMS notifications.",
  "notifications.sms.verificationCodeLabel": "Verification Code:",
  "notifications.sms.verificationCodePlaceholder": "Enter 6-digit code",
  "notifications.sms.verifyButton": "Verify Code",
  "notifications.sms.verifying": "Verifying...",
  "notifications.sms.optOutTitle": "Opt Out of SMS Communications",
  "notifications.sms.optOutDesc":
    "Click the button below to stop receiving SMS messages from KinShield. You can opt back in by contacting support.",
  "notifications.sms.optOutButton": "Opt Out of SMS",
  "notifications.sms.processing": "Processing...",
  "notifications.sms.criticalTitle": "Critical Security Announcements",
  "notifications.sms.criticalActiveDesc":
    "Urgent security notifications via SMS",
  "notifications.sms.criticalInactiveDesc":
    "Opt in to SMS to receive critical security announcements",
  "notifications.sms.noPhoneError":
    "No phone number found. Please contact support.",
  "notifications.sms.noUserInfo":
    "User information not found. Please try refreshing the page.",
  "notifications.sms.optedOutSuccess":
    "You have successfully opted out of SMS notifications.",
  "notifications.sms.optOutFailed": "Failed to opt out. Please try again.",
  "notifications.sms.optOutError":
    "An error occurred while opting out. Please try again.",
  "notifications.sms.optInFailed":
    "Failed to process opt-in. Please try again.",
  "notifications.sms.optInError":
    "An error occurred while opting in. Please try again.",
  "notifications.sms.verifyRequired": "Please enter the verification code.",
  "notifications.sms.noUserInfoRefresh":
    "User information not found. Please refresh the page.",
  "notifications.sms.verifiedSuccess":
    "Phone number verified successfully! SMS notifications are now active.",
  "notifications.sms.verifyFailed":
    "Verification failed. Please check the code and try again.",
  "notifications.inapp.title": "In-App Notifications",
  "notifications.inapp.desktopTitle": "Desktop Notifications",
  "notifications.inapp.desktopDesc":
    "Show browser notifications when app is open",
  "notifications.announcements.title": "Announcement Preferences",
  "notifications.announcements.levelLabel":
    "Which pop-up announcements do you want to see?",
  "notifications.announcements.warningsOnly": "⚠️ Warnings & Errors only",
  "notifications.announcements.plusInfo": "📢 + Info messages",
  "notifications.announcements.plusSuccess": "✅ + Success messages",
  "notifications.announcements.showAll": "🔔 Show everything",
  "notifications.announcements.noteLabel": "Note:",
  "notifications.announcements.noteText":
    "Warnings and errors are always shown and cannot be turned off.",

  // ── Appearance Settings ────────────────────────────────────────────
  "appearance.landingPage.title": "Landing Page",
  "appearance.landingPage.label": "Initial Page After Login",
  "appearance.landingPage.dashboard": "📊 Dashboard",
  "appearance.landingPage.gettingStarted": "🚀 Getting Started",
  "appearance.landingPage.howDoI": "❓ How Do I...?",
  "appearance.theme.title": "Theme",
  "appearance.theme.colorLabel": "Color Theme",
  "appearance.theme.optLight": "Light",
  "appearance.theme.optDark": "Dark",
  "appearance.theme.optAuto": "Auto (System)",
  "appearance.compactMode.title": "Compact Mode",
  "appearance.compactMode.desc": "Show more content by reducing spacing",
  "appearance.accessibility.title": "Accessibility Widget",
  "appearance.accessibility.desc":
    "Show the accessibility toolbar at the bottom-left of the screen",
  "appearance.installApp.title": "Install App",
  "appearance.dashboard.title": "Dashboard Settings",
  "appearance.dashboard.viewLabel": "Dashboard View",
  "appearance.dashboard.detailed": "📊 Detailed View (Full Dashboard)",
  "appearance.dashboard.simple": "📱 Simple View (Senior-Friendly)",
  "appearance.dashboard.modeLabel": "Dashboard Mode (Detailed View Only)",
  "appearance.dashboard.riskMode": "🚨 Risk Mode (Focus on Threats)",
  "appearance.dashboard.shieldMode": "🛡️ Shield Mode (Focus on Protection)",
  "appearance.dashboard.showSettingsLabel": "Show View Settings Button",
  "appearance.dashboard.showSettingsDesc":
    "Display the View Settings button (⚙️) in the dashboard header for quick access to temporary display options",

  // ── Change Password Modal ──────────────────────────────────────────
  "changePassword.title": "Change Password",
  "changePassword.currentLabel": "Current Password",
  "changePassword.currentPlaceholder": "Enter your current password",
  "changePassword.newLabel": "New Password",
  "changePassword.newPlaceholder": "Enter your new password",
  "changePassword.confirmLabel": "Confirm New Password",
  "changePassword.confirmPlaceholder": "Confirm your new password",
  "changePassword.mismatch": "Passwords do not match",
  "changePassword.match": "✓ Passwords match",
  "changePassword.requirements.title": "Password Requirements:",
  "changePassword.requirements.length":
    "Minimum 12 characters (15+ recommended for better security) with no requirement for special characters, numbers, or upper/lowercase",
  "changePassword.requirements.passphrase":
    "Use a memorable passphrase instead of complex characters",
  "changePassword.requirements.avoid":
    "Avoid common words, sequential patterns, or repeated characters",
  "changePassword.requirements.example":
    'Example: "coffee-morning-sunshine-blue"',
  "changePassword.requirements.tip":
    "Strong passphrases are easier to remember and more secure than complex short passwords!",
  "changePassword.changing": "Changing...",
  "changePassword.submit": "Change Password",
  "changePassword.error.enterCurrent": "Please enter your current password.",
  "changePassword.error.enterNew": "Please enter a new password.",
  "changePassword.error.mismatch": "New passwords do not match.",
  "changePassword.error.samePassword":
    "New password must be different from current password.",
  "changePassword.error.wrongPassword": "Current password is incorrect.",
  "changePassword.error.failed": "Failed to change password. Please try again.",
  "changePassword.success": "Password changed successfully!",

  // ── MFA Setup Modal ────────────────────────────────────────────────
  "mfaSetup.title": "Enable Two-Factor Authentication",
  "mfaSetup.sendingCode": "Sending verification code...",
  "mfaSetup.tryAgain": "Try Again",
  "mfaSetup.codeSentTo": "A verification code has been sent to",
  "mfaSetup.emailHint":
    "Check your email and enter the 6-digit code to continue",
  "mfaSetup.smsHint": "Enter the 6-digit code to continue",
  "mfaSetup.verifyButton": "Verify Code",
  "mfaSetup.verifyingButton": "Verifying...",
  "mfaSetup.codeArrival": "Code can take up to 30 seconds to arrive",
  "mfaSetup.enabledMessage": "Two-factor authentication has been enabled!",
  "mfaSetup.saveCodesDesc": "Save these backup codes in a secure location",
  "mfaSetup.codesUsageDesc":
    "Each code can be used once if you lose access to your phone",
  "mfaSetup.copyCode": "Copy code",
  "mfaSetup.downloadCodes": "Download Codes",
  "mfaSetup.done": "Done",
  "mfaSetup.warningLabel": "Warning:",
  "mfaSetup.warningText":
    "Store these codes securely. You won't be able to see them again.",
  "mfaSetup.failedToSend": "Failed to send verification code",
  "mfaSetup.verifyEnterCode": "Please enter the verification code",
  "mfaSetup.verifyFailed": "Verification failed",

  // Deactivated account messages
  "deactivated.title": "Account Deactivated",
  "deactivated.heroMessage":
    "Your account has been deactivated. To access the app's functions, please contact your account owner.",
  "deactivated.accessDenied":
    "Your account has been deactivated. Please contact your administrator.",
  "deactivated.accessRestricted": "Access restricted",

  // Lapsed subscription messages (past grace period, inactive)
  "lapsed.title": "Subscription Inactive",
  "lapsed.owner.message":
    "Your subscription has lapsed. To restore access to the app's features, please renew your subscription. You can still manage your Subscription & Payment, User Profile, and Settings.",
  "lapsed.owner.cta": "Go to Subscription & Payment",
  "lapsed.nonOwner.message":
    "This account's subscription has lapsed. Please contact your account owner to renew the subscription and restore access to the app's features.",

  // ── URL Safety Analysis (Mobile) ────────────────────────────────────
  "urlsa.page.title": "Social Media Post Safety Analysis",
  "urlsa.page.subtitle":
    "Analyze any link to detect phishing, scams, and security threats",
  "urlsa.form.urlLabel": "Enter URL to Analyze",
  "urlsa.form.urlPlaceholder": "https://example.com or example.com",
  "urlsa.form.clearAriaLabel": "Clear input",
  "urlsa.form.contextLabel": "Context (optional)",
  "urlsa.form.contextPlaceholder":
    "Any other details or situations to add? How did you come across this URL? The more context you provide, the better we can analyze the risk.",
  "urlsa.form.analyzeBtn": "Analyze",
  "urlsa.form.clearBtn": "Clear",
  "urlsa.form.viewResults": "View Analysis Results",
  "urlsa.nav.backToInputs": "Back to Inputs",
  "urlsa.redirect.detected": "Redirect Detected!",
  "urlsa.redirect.summary":
    "A {platform} redirect was resolved — tap to see details.",
  "urlsa.redirect.body":
    "The URL you submitted is a {platform} redirect link. We followed it to the actual destination and analyzed that website instead.",
  "urlsa.redirect.youSubmitted": "You submitted:",
  "urlsa.redirect.actualDest": "Actual destination:",
  "urlsa.threat.scamDetected": "SCAM DETECTED",
  "urlsa.threat.noThreats": "NO THREATS DETECTED",
  "urlsa.threat.shareIndividual":
    "Available For Family or Extended Family Plans",
  "urlsa.threat.shareFamily": "Share this alert with family members",
  "urlsa.threat.share": "Share",
  "urlsa.results.whatToDo": "What Should You Do?",
  "urlsa.results.detailedAnalysis": "Detailed Analysis",
  "urlsa.disclaimer":
    "The URL safety analysis provided is for educational purposes only. For complete terms and limitations, please review our",

  // ── Image Analysis (Mobile) ──────────────────────────────────────────────
  "imgMobile.page.title": "Screenshot Analysis",
  "imgMobile.page.subtitle":
    "Upload screenshots to detect scams, phishing, and fraudulent content using AI-powered analysis",

  "imgMobile.upload.prompt": "Take a Picture or Choose Photo",
  "imgMobile.upload.formats": "JPEG, PNG, GIF, WebP, BMP, TIFF, SVG (Max 10MB)",
  "imgMobile.upload.contextLabel": "Context (optional)",
  "imgMobile.upload.contextPlaceholder":
    "Any other details or situations to add? (e.g. 'Received this image in a message from an unknown sender')",
  "imgMobile.upload.analyzeBtn": "Analyze",
  "imgMobile.upload.cancelBtn": "Cancel",
  "imgMobile.upload.startingUpload": "Starting upload...",
  "imgMobile.upload.complete": "Upload complete! Analyzing...",
  "imgMobile.upload.loadingTitle": "Uploading and analyzing image...",
  "imgMobile.upload.loadingSubtitle":
    "Please wait while we securely upload and analyze your screenshot",
  "imgMobile.upload.noFileError": "Please select a file first",
  "imgMobile.upload.invalidTypeError":
    "Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP, BMP, TIFF, SVG)",
  "imgMobile.upload.fileTooLargeError": "File too large. Maximum size is 10MB",
  "imgMobile.upload.failedError": "Upload failed. Please try again.",
  "imgMobile.upload.dailyLimitError":
    "Daily upload limit reached (25/day). Your quota will reset tomorrow.",
  "imgMobile.upload.timeoutMsg":
    "Analysis is taking longer than expected. Please refresh.",
  "imgMobile.upload.successToast": "Security checks complete!",

  "imgMobile.quota.title": "Daily Limit Reached",
  "imgMobile.quota.desc":
    "You've reached your daily limit of 25 image uploads. Resets tomorrow.",

  "imgMobile.history.title": "Analysis History",
  "imgMobile.history.loading": "Loading...",
  "imgMobile.history.empty": "No analyses yet. Upload your first image!",
  "imgMobile.history.showing": "Showing 10 of {total} analyses",
  "imgMobile.history.refreshSuccess": "Analysis history refreshed!",
  "imgMobile.history.refreshError": "Failed to refresh: ",
  "imgMobile.history.deleteError": "Failed to delete: ",

  "imgMobile.card.statusVirus": "Virus",
  "imgMobile.card.statusBlocked": "Blocked",
  "imgMobile.card.statusScam": "Scam",
  "imgMobile.card.statusSafe": "Safe",

  "imgMobile.detail.backToUpload": "Back to Upload",
  "imgMobile.detail.uploadAnother": "Upload Another",
  "imgMobile.detail.deleteBtn": "Delete",
  "imgMobile.detail.noAnalysisTitle": "No Analysis Selected",
  "imgMobile.detail.noAnalysisDesc":
    "Select an analysis from history to view details.",
  "imgMobile.detail.malwareDetected": "MALWARE DETECTED",
  "imgMobile.detail.securityFailed": "SECURITY CHECK FAILED",
  "imgMobile.detail.scamDetected": "SCAM DETECTED",
  "imgMobile.detail.noThreats": "NO THREATS DETECTED",
  "imgMobile.detail.shareIndividual":
    "Available For Family or Extended Family Plans",
  "imgMobile.detail.shareFamily": "Share this alert with family members",
  "imgMobile.detail.share": "Share",
  "imgMobile.detail.blockedTitle": "Image Blocked for Your Safety",
  "imgMobile.detail.malwareNotice":
    "This file was detected as containing malware and has been quarantined.",
  "imgMobile.detail.securityNotice":
    "This image failed our security screening and cannot be displayed.",
  "imgMobile.detail.blockedReason": "Reason:",
  "imgMobile.detail.scamTypeLabel": "Scam Type:",
  "imgMobile.detail.whatToDo": "What Should You Do?",
  "imgMobile.detail.explanationTitle": "Explanation",
  "imgMobile.detail.imagePreviewTitle": "Image Preview",
  "imgMobile.detail.analyzedOn": "Analysis completed on {date}",
  "imgMobile.detail.unknownType": "Unknown",

  "imgMobile.errors.unknown": "Unknown error",

  // ── Phone Number Scam Check (Mobile + Desktop) ────────────────────────────
  "phoneCheck.page.title": "Phone Number Lookup",
  "phoneCheck.page.subtitle":
    "Check if a phone number is associated with known scammers or fraudulent activities",

  "phoneCheck.form.phoneLabel": "Phone Number",
  "phoneCheck.form.phonePlaceholder": "Enter phone number",
  "phoneCheck.form.invalidPhone": "Invalid phone number",
  "phoneCheck.form.contextLabel": "Context (optional)",
  "phoneCheck.form.contextPlaceholder":
    "Any other details or situations to add? Is someone calling you or asking you to call this number? The more context you provide, the better we can analyze the risk.",
  "phoneCheck.form.analyzing": "Analyzing...",
  "phoneCheck.form.checkBtn": "Check Number",
  "phoneCheck.form.clearBtn": "Clear",

  "phoneCheck.mobile.viewResults": "View Analysis Results",
  "phoneCheck.mobile.backToInputs": "Back to Inputs",

  "phoneCheck.desktop.inputPanelTitle": "Phone Number Input",
  "phoneCheck.desktop.resultsPanelTitle": "Analysis Results",

  "phoneCheck.tips.heading": "Safety Tips:",
  "phoneCheck.tips.tip1":
    "Do not pay under pressure. Take a breath and assess the situation",
  "phoneCheck.tips.tip2":
    "Never give personal information to unsolicited callers",
  "phoneCheck.tips.tip3":
    "Be wary of callers claiming to be from government agencies",
  "phoneCheck.tips.tip4": "Verify caller identity through official channels",
  "phoneCheck.tips.tip5": "Do not change settings on your phone or computer",
  "phoneCheck.tips.tip6":
    "Do not engage with suspicious callers, regardless of their threats of jail, police, or fines",
  "phoneCheck.tips.tip7": "Do not call back unknown numbers",
  "phoneCheck.tips.howTitle": "How Phone Number Analysis Works",
  "phoneCheck.tips.howDesc":
    "Our phone number checker searches multiple scam databases and community reports to identify numbers associated with fraudulent activities, robocalls, and scams.",

  "phoneCheck.results.readyTitle": "Ready to Check",
  "phoneCheck.results.readyDesc":
    "Enter a phone number and click 'Check Number' to see analysis results.",
  "phoneCheck.results.shareIndividual":
    "Available For Family or Extended Family Plans",
  "phoneCheck.results.shareFamily": "Share this alert with family members",
  "phoneCheck.results.share": "Share",
  "phoneCheck.results.whatToDo": "What Should You Do?",
  "phoneCheck.results.explanation": "Explanation",
  "phoneCheck.results.warningImportant": "Important:",
  "phoneCheck.results.warningText":
    "DO NOT call this number if a pop-up or a text message asks you to call it. Instead, go to the official website of whatever the organization you are dealing with to find a number to call. See Safety Tips on the left panel.",
  "phoneCheck.results.dbResults": "Database Results",
  "phoneCheck.results.dbCategory": "Category:",
  "phoneCheck.results.dbSummary": "Summary:",
  "phoneCheck.results.dbReports": "Reports:",
  "phoneCheck.results.dbLastReported": "Last Reported:",
  "phoneCheck.results.dbNoSources":
    "No database sources returned for this number.",

  "phoneCheck.disclaimer":
    "The phone number analysis provided is for educational purposes only. For complete terms and limitations, please review our",

  // ── Analysis Feedback Bar ──────────────────────────────────────────────────
  "feedback.prompt": "Was this analysis helpful?",
  "feedback.helpful": "Helpful",
  "feedback.yesHelpful": "Yes, helpful",
  "feedback.notHelpful": "Not helpful",
  "feedback.thanks": "Thanks for your feedback!",
  "feedback.nudge": "Try adding context below to improve the result.",

  // ── Reanalyze With Context Panel ───────────────────────────────────────────
  "reanalyze.title": "Refine Analysis with Additional Context",
  "reanalyze.helperText":
    "If you have more context or details around the email/text you forwarded, please provide them to improve the analysis.",
  "reanalyze.addContext": "Add Context",
  "reanalyze.contextLabel": "Your Additional Context",
  "reanalyze.contextPlaceholder":
    "e.g., I don't have an account with this company, I wasn't expecting this email, I never placed this order...",
  "reanalyze.reanalyzing": "Re-analyzing...",
  "reanalyze.reanalyze": "Re-analyze",
  "reanalyze.cancel": "Cancel",
  "reanalyze.error.addContext":
    "Please add some context to help us refine the analysis.",
  "reanalyze.error.failed": "Failed to re-analyze. Please try again.",
  "reanalyze.success": "Your context has been processed. Analysis updated.",

  // ── URL Safety Analysis (Desktop) – additional keys ───────────────────────
  "urlsa.section.enterUrl": "Enter URL Details",
  "urlsa.section.results": "Analysis Results",
  "urlsa.empty.title": "Ready to Analyze",
  "urlsa.empty.description":
    "Enter a URL on the left to check for phishing, scams, and security threats.",
  "urlsa.share.threatType": "URL Safety",
  "urlsa.share.threatTitle": "Potentially Dangerous URL Detected",

  // ── Social Media Analysis (Desktop + Mobile + Details) ────────────────────
  "social.page.title": "Social Media Analysis",
  "social.page.subtitle":
    "Analyze social media businesses and interactions for potential scams and fraud indicators",
  "social.page.subtitleMobile":
    "Analyze businesses and websites for potential scams",
  "social.tab.inputs": "Inputs",
  "social.tab.history": "Analysis History",
  "social.disclaimer":
    "The social media analysis provided is for educational purposes only. For complete terms and limitations, please review our",
  "social.section.inputs": "Analysis Inputs",
  "social.section.results": "Analysis Results",
  "social.viewResults": "View Analysis Results",
  "social.history.recentTitle": "Recent Analyses",
  "social.history.loading": "Loading history...",
  "social.history.empty": "No recent analyses. Submit a form to get started.",
  "social.nav.backToForm": "Back to Form",
  "social.nav.newAnalysis": "New Analysis",
  "social.nav.startAnalysis": "Start Analysis",
  "social.empty.title": "No Results Yet",
  "social.empty.description":
    "Submit an analysis or select a recent item to see results.",
  "social.detail.heading": "Analysis Details",
  "social.detail.analysisUnavailable": "Analysis Unavailable",
  "social.detail.insufficientData": "Insufficient Data",
  "social.detail.potentialScam": "Potential Scam Detected",
  "social.detail.appearsLegitimate": "Appears Legitimate",
  "social.detail.statusUnknown": "Analysis Status Unknown",
  "social.detail.shareIndividual":
    "Available For Family or Extended Family Plans",
  "social.detail.shareFamily": "Share this alert with family members",
  "social.detail.share": "Share",
  "social.detail.limitedData": "Limited Data Available",
  "social.detail.moreAccurate":
    "For more accurate analysis, please provide additional information in the Case Input fields. The more details you enter, the better our analysis can be:",
  "social.detail.missingWebsite": "Complete business website URL",
  "social.detail.missingMerchandise": "Specific product/service type",
  "social.detail.missingContact": "Contact person information",
  "social.detail.missingInteraction": "How you discovered this business",
  "social.detail.missingPlatform": "Social media platform details",
  "social.detail.missingName": "Business or person name",
  "social.detail.fillMissing":
    "Fill in the missing fields above and resubmit for improved results.",
  "social.detail.comprehensiveInfo":
    "You've provided comprehensive information. The analysis may be limited by external data availability.",
  "social.detail.whatToDo": "What Should You Do?",
  "social.detail.explanation": "Explanation",
  "social.detail.additionalData": "Additional Data Needed",
  "social.detail.noCase": "No Case Selected",
  "social.detail.noCaseDesc":
    "Select a case from the history or submit a new analysis to view details here.",
  "social.share.threatType": "Social Media Scam",
  "social.form.error.nameOrUrl":
    "Please enter either a Business/Person Name or a Website/URL, or both",
  "social.form.error.notLoggedIn":
    "User not logged in. Please log in to perform analysis.",
  "social.form.error.notLoggedInDesktop":
    "You must be logged in to perform social media analysis.",
  "social.form.error.deleteFailed":
    "Failed to delete analysis. Please try again.",

  // ── Simulation Profile Settings ──────────────────────────────────────────────
  "sps.page.title": "Simulation Profile Settings",
  "sps.page.subtitle": "Configure how your practice phishing scenarios work",
  "sps.page.configuringFor": "Configuring profile for: {name}",
  "sps.loading": "Loading...",
  "sps.error.prefix": "Error: ",

  // Status section
  "sps.status.heading": "Simulation Status",
  "sps.status.label": "Simulation Status:",
  "sps.status.reasonLabel": "Reason:",
  "sps.status.demo.text": "DEMO MODE",
  "sps.status.demo.messagePart1":
    "Simulation schedules are disabled for demo accounts. No phishing emails or SMS messages will be sent.",
  "sps.status.demo.messagePart2":
    "You can still explore and adjust the simulation configuration below.",
  "sps.status.paused.text": "PAUSED",
  "sps.status.paused.noSub":
    "Simulations are paused because there is no active subscription. They will resume automatically when your subscription is active.",
  "sps.status.scheduling.text": "SCHEDULING",
  "sps.status.scheduling.message":
    "Your simulation profile is active. Schedules are generated automatically overnight and simulation messages will be sent soon.",
  "sps.status.active.text": "ACTIVE",
  "sps.status.active.message": "Simulations are currently running on schedule",
  "sps.status.paused.base": "Simulations are paused",
  "sps.status.paused.until": "Paused until {date}",
  "sps.status.paused.ended": "Pause period has ended - please resume",
  "sps.status.paused.days": "Paused for {days} days",
  "sps.status.paused.indefinitely": "Paused indefinitely",
  "sps.status.resumeDisabled": "Resume is disabled in demo mode",
  "sps.status.resumeTitle": "Resume simulations",
  "sps.status.resume": "Resume",
  "sps.status.pauseDisabled": "Pause is disabled in demo mode",
  "sps.status.pauseTitle": "Pause simulations",
  "sps.status.pause": "Pause",

  // Configuration / Presets
  "sps.config.heading": "Your Configuration",
  "sps.presets.heading": "How tough should your practice scenarios be?",
  "sps.presets.headingMobile": "Choose your difficulty",
  "sps.presets.subheading":
    "Pick a practice level below. You can always change it later.",
  "sps.presets.subheadingMobile": "Pick a level. You can change it later.",

  // Preset cards
  "sps.preset.easy.title": "Gentle",
  "sps.preset.easy.subtitle": "Good for beginners",
  "sps.preset.easy.desc": "Obvious scam patterns that are easy to spot",
  "sps.preset.easy.difficulty": "Basic (1/10)",
  "sps.preset.easy.frequency": "2\u00d7 per 10 days",
  "sps.preset.easy.channels": "Email only",
  "sps.preset.easy.timing": "Business hours",
  "sps.preset.easy.context": "General, Regional",

  "sps.preset.average.title": "Moderate",
  "sps.preset.average.subtitle": "Realistic scam scenarios",
  "sps.preset.average.desc": "Mix of obvious and tricky phishing attempts",
  "sps.preset.average.difficulty": "Intermediate (4/10)",
  "sps.preset.average.frequency": "5\u00d7 per 10 days",
  "sps.preset.average.channels": "Email, SMS",
  "sps.preset.average.timing": "Business hours",
  "sps.preset.average.context": "General, Regional, Events",

  "sps.preset.difficult.title": "Challenging",
  "sps.preset.difficult.subtitle": "Subtle & advanced tactics",
  "sps.preset.difficult.desc":
    "Sophisticated social engineering, hard to detect",
  "sps.preset.difficult.difficulty": "Expert (7/10)",
  "sps.preset.difficult.frequency": "8\u00d7 per 10 days",
  "sps.preset.difficult.channels": "Email, SMS, Voice",
  "sps.preset.difficult.timing": "Any time",
  "sps.preset.difficult.context": "All categories",

  "sps.preset.custom.title": "Custom",
  "sps.preset.custom.subtitle": "Full control",
  "sps.preset.custom.desc":
    "You choose every detail of your simulation settings",
  "sps.preset.custom.descMobile":
    "Choose every detail of your simulation settings",

  "sps.preset.locked": "Requires Family plan",
  "sps.preset.currentBadge": "Current Settings",
  "sps.preset.currentBadgeMobile": "Current",
  "sps.preset.viewDetails": "View current settings details \u2192",

  // Dashboard
  "sps.dashboard.heading": "Simulation Settings",
  "sps.dashboard.back": "Back",
  "sps.dashboard.profileSuffix": " Profile",
  "sps.dashboard.tagPreset": "Preset",
  "sps.dashboard.tagCustom": "Custom",
  "sps.dashboard.tagActive": "Active",
  "sps.dashboard.hintPreset":
    "These settings are configured by the {preset} preset.",
  "sps.dashboard.hintPresetMobile": "Configured by {preset} preset.",
  "sps.dashboard.switchToCustom": "Switch to Custom",
  "sps.dashboard.switchToCustomSuffix": " to edit individually.",
  "sps.dashboard.hintEditable": "Tap any badge to change your settings.",
  "sps.dashboard.saveCustom": "Use Custom Settings",
  "sps.dashboard.savePreset": "Use {preset} Preset",

  // Badge row labels
  "sps.badge.difficulty": "Difficulty",
  "sps.badge.frequency": "Frequency",
  "sps.badge.channels": "Channels",
  "sps.badge.timing": "Timing",
  "sps.badge.context": "Context",

  // Sophistication tiers
  "sps.tier.basic": "Basic",
  "sps.tier.intermediate": "Intermediate",
  "sps.tier.advanced": "Advanced",
  "sps.tier.expert": "Expert",
  "sps.tier.master": "Master",

  // Frequency tiers
  "sps.freqTier.low": "Low",
  "sps.freqTier.medium": "Medium",
  "sps.freqTier.high": "High",
  "sps.freqTier.veryHigh": "Very High",

  // Channel labels
  "sps.channel.email": "Email",
  "sps.channel.sms": "SMS",
  "sps.channel.qr": "QR Code",
  "sps.channel.voice": "Voice Call",
  "sps.channel.social": "Social Media",

  // Timing labels
  "sps.timing.day": "Day Time",
  "sps.timing.evening": "Evening",
  "sps.timing.any": "Any Time",

  // Context labels
  "sps.context.general": "General",
  "sps.context.events": "Events",
  "sps.context.regional": "Regional",
  "sps.context.financial": "Financial",
  "sps.context.emergencies": "Emergencies",
  "sps.context.personalized": "Personalized",

  // Setup journey
  "sps.setupJourney.continue": "Continue Setting Up Your Account",

  // Pause Modal
  "sps.pauseModal.title": "Pause Simulations",
  "sps.pauseModal.description":
    "Pausing simulations will stop all scheduled simulation messages from being sent. You can resume at any time.",
  "sps.pauseModal.optionIndefiniteTitle": "Pause indefinitely",
  "sps.pauseModal.optionIndefiniteDesc":
    "Simulations will remain paused until you manually resume them",
  "sps.pauseModal.optionUntilTitle": "Pause until specific date",
  "sps.pauseModal.optionUntilDesc":
    "Simulations will automatically resume on the selected date",
  "sps.pauseModal.optionDaysTitle": "Pause for number of days",
  "sps.pauseModal.optionDaysDesc":
    "Simulations will resume after the specified number of days",
  "sps.pauseModal.days": "days",
  "sps.pauseModal.reasonLabel": "Reason for pausing (optional)",
  "sps.pauseModal.reasonPlaceholder":
    "E.g., Holiday break, maintenance period, training session...",
  "sps.pauseModal.reasonCounter": "{n}/255 characters",
  "sps.pauseModal.cancel": "Cancel",
  "sps.pauseModal.confirm": "Pause Simulations",

  // ── Social Media Analysis — Form labels & placeholders ────────────────────
  "social.form.label.businessInfo": "Business Info*",
  "social.form.label.businessName": "Business Name",
  "social.form.label.websiteUrl": "Website/URL",
  "social.form.label.platform": "Where did you find this post or business?",
  "social.form.label.merchandise": "What product or service offered?",
  "social.form.label.interaction": "How did you find this post or business?",
  "social.form.label.contactPerson": "Contact Person",
  "social.form.label.contactPhone": "Contact Phone",
  "social.form.label.context": "Context (optional)",
  "social.form.placeholder.businessName": "Enter business name",
  "social.form.placeholder.websiteUrl":
    "Enter website URL (e.g., example.com or www.example.com)",
  "social.form.placeholder.selectPlatform": "Select Platform",
  "social.form.placeholder.selectType": "Select Type",
  "social.form.placeholder.selectMethod": "Select Method",
  "social.form.placeholder.contactPerson": "Contact person name",
  "social.form.placeholder.phone": "Enter phone number",
  "social.form.hint.phone":
    "Select country and enter phone number. Defaults to US (+1)",
  "social.form.placeholder.context": "Any other details or situations to add?",
  "social.form.button.analyzing": "Analyzing...",
  "social.form.button.analyze": "Analyze",
  "social.form.button.clear": "Clear",

  // ── Social Media Analysis — History panel ────────────────────────────────
  "social.history.heading": "Analysis History",
  "social.history.caseCount": "{count} cases",
  "social.history.loadingCases": "Loading cases...",
  "social.history.noResults": "No cases found",
  "social.history.submitHint":
    "Submit an analysis to see your case history here.",
  "social.history.deleteTitle": "Delete this analysis",
  "social.history.delete": "Delete",
  "social.history.unknownBusiness": "Unknown Business",
  "social.history.unknown": "Unknown",
  "social.history.previous": "Previous",
  "social.history.pageOf": "Page {page} of {total}",
  "social.history.next": "Next",
  "social.history.hide": "Hide History",
  "social.history.show": "Show History",
  "social.history.hideTitle": "Hide Case History",
  "social.history.showTitle": "Show Case History",

  // ── Social Media Analysis — Details panel ────────────────────────────────
  "social.detail.unknownBusiness": "Unknown Business",

  // ── ADA Accessibility Widget ───────────────────────────────────────────────
  "ada.title": "Accessibility",
  "ada.panel.ariaLabel": "Accessibility settings",
  "ada.close.title": "Close",
  "ada.close.ariaLabel": "Close accessibility panel",
  "ada.close.label": "Close",
  "ada.card.biggerText": "Bigger Text",
  "ada.card.contrast": "Contrast",
  "ada.card.textSpacing": "Text Spacing",
  "ada.card.lineHeight": "Line Height",
  "ada.fontScale.ariaLabel": "Font size scale",
  "ada.resetAll": "Reset All",
  "ada.resetAll.ariaLabel": "Reset all accessibility settings",
  "ada.footer.brand": "Accessibility \u00b7 KinShield",
  "ada.fab.open": "Open accessibility settings",
  "ada.fab.close": "Close accessibility settings",
  "ada.fab.label": "Accessibility",
  "ada.biggerText.title": "Current: {label} \u2014 click to increase",
  "ada.biggerText.ariaLabel":
    "Bigger text, current size {label}, click to increase",
  "ada.setFontSize.ariaLabel": "Set font size to {label}",
  "ada.contrast.title": "Current: {theme} \u2014 click to toggle",
  "ada.contrast.ariaLabel":
    "Contrast, currently {theme} theme, click to toggle",
  "ada.contrast.dark": "Dark",
  "ada.contrast.light": "Light",
  "ada.textSpacing.title": "Text spacing: {label} \u2014 click to increase",
  "ada.textSpacing.ariaLabel":
    "Text spacing, currently {label}, click to increase",
  "ada.setSpacing.ariaLabel": "Set text spacing to {label}",
  "ada.lineHeight.cardTitle": "Line height: {label} \u2014 click to increase",
  "ada.lineHeight.cardAriaLabel":
    "Line height, currently {label}, click to increase",
  "ada.setLineHeight.ariaLabel": "Set line height to {label}",
  "ada.fontSizeBadge.ariaLabel": "Font size: {label}",
  "ada.spacing.label.default": "Default",
  "ada.spacing.label.wide": "Wide",
  "ada.spacing.label.wider": "Wider",
  "ada.spacing.label.widest": "Widest",
  "ada.lineHeight.label.default": "Default",
  "ada.lineHeight.label.relaxed": "Relaxed",
  "ada.lineHeight.label.loose": "Loose",
  "ada.lineHeight.label.veryLoose": "Very loose",
  "ada.dismiss.tooltip": "Hide accessibility widget",
  "ada.dismiss.ariaLabel": "Hide accessibility widget",
  "ada.dismiss.label": "Hide",
  "ada.dismiss.preview":
    "Hides this widget. To restore it, go to Settings \u2192 Appearance & Display.",
  "ada.dismissed.message":
    "Accessibility widget hidden. To restore it, go to Settings \u2192 Appearance & Display.",

  // ── ChatWidget / Scout chatbot ─────────────────────────────────────
  "chat.fab.label": "Scout",
  "chat.fab.close": "Close chat",
  "chat.fab.open": "Chat with Scout \u2014 your Digital Personal Guard",
  "chat.fab.ariaOpen": "Open Scout, Digital Personal Guard",
  "chat.window.ariaLabel": "Scout \u2014 Digital Personal Guard",
  "chat.header.name": "Scout",
  "chat.header.subtitle": "Digital Personal Guard",
  "chat.header.status.analyzing": "Analyzing...",
  "chat.header.status.ready": "On duty \u2014 Ready to protect",
  "chat.header.newConversation": "New conversation",
  "chat.tts.enable": "Turn on read aloud",
  "chat.tts.disable": "Turn off read aloud",
  "chat.tts.ariaEnable": "Enable text-to-speech",
  "chat.tts.ariaDisable": "Disable text-to-speech",
  "chat.tts.readAloud": "Read aloud",
  "chat.tts.stop": "Stop reading",
  "chat.tts.ariaReadAloud": "Read message aloud",
  "chat.welcome.desc":
    "I\u2019m Scout, your Digital Personal Guard \u2014 think of me as your personal security expert. Always on duty, always watching out for you.",
  "chat.starter.email": "How do I check if an email is a scam?",
  "chat.starter.training": "What security training is available?",
  "chat.starter.alerts": "Show me the latest scam alerts",
  "chat.starter.help": "What can you help me with?",
  "chat.error.connection":
    "I\u2019m sorry, I\u2019m having trouble connecting right now. Please try again in a moment.",
  "chat.nudge.dismiss": "Dismiss",
  "chat.input.placeholder": "Ask me anything...",
  "chat.send.title": "Send message",
  "chat.footer.powered": "Powered by KinShield AI",

  // ── DPG Personality — greetings, nudges, reactions ──────────────────────
  "dpg.greeting.morning.0":
    "Good morning, {name}! Your Digital Personal Guard is on duty. \u2600\ufe0f",
  "dpg.greeting.morning.1":
    "Rise and shine, {name}! I\u2019ve been watching over your accounts overnight. All clear!",
  "dpg.greeting.morning.2":
    "Morning, {name}! Ready to start the day safely? Let me know if you need anything.",
  "dpg.greeting.afternoon.0":
    "Good afternoon, {name}! I\u2019m here if you need a security hand.",
  "dpg.greeting.afternoon.1":
    "Afternoon check-in \u2014 everything\u2019s looking secure on my end!",
  "dpg.greeting.afternoon.2":
    "Hi, {name}! Staying safe this afternoon? I\u2019ve got your back.",
  "dpg.greeting.evening.0":
    "Good evening, {name}! Winding down? I\u2019ll keep watch while you relax. \ud83c\udf19",
  "dpg.greeting.evening.1":
    "Evening, {name}! A great time to review any suspicious messages from today.",
  "dpg.greeting.evening.2":
    "Hi, {name}! Before you call it a day, want me to do a quick safety scan?",
  "dpg.nudge.dashboard.0":
    "Welcome back! Your safety score is looking good. Want me to run a quick security check?",
  "dpg.nudge.dashboard.1":
    "I\u2019ve been keeping watch while you were away. Everything looks secure! \ud83d\udee1\ufe0f",
  "dpg.nudge.dashboard.2":
    "Pro tip: Check your safety score regularly \u2014 I\u2019ll alert you if anything changes.",
  "dpg.nudge.email.0":
    "I see you\u2019re checking an email. Paste it in and I\u2019ll scan it for phishing red flags!",
  "dpg.nudge.email.1":
    "Remember: Legitimate companies never ask for passwords via email. I\u2019m here if it looks suspicious.",
  "dpg.nudge.text.0":
    "Got a suspicious text? I can analyze it for common scam patterns like fake delivery notices or bank alerts.",
  "dpg.nudge.social.0":
    "Social media scams are on the rise! Let me help you verify if that message or profile is legit.",
  "dpg.nudge.url.0":
    "Checking a link? Smart move! Paste the URL and I\u2019ll tell you if it\u2019s safe before you click.",
  "dpg.nudge.training.0":
    "Great choice investing in your security knowledge! I\u2019ll be here if you have questions about any lesson.",
  "dpg.nudge.alerts.0":
    "Stay informed! These are the latest scam alerts in your area. Tap any to learn how to protect yourself.",
  "dpg.nudge.settings.0":
    "Reviewing your settings? Make sure two-factor authentication is enabled \u2014 it\u2019s your best defense!",
  "dpg.nudge.settings.1":
    "Did you know there\u2019s an Accessibility panel in the bottom-left corner? You can adjust font size, letter spacing, line height, and contrast \u2014 no account change needed!",
  "dpg.nudge.plan.0":
    "Looking at plans? Higher tiers unlock advanced threat monitoring. Happy to explain the differences!",
  "dpg.nudge.idle.0":
    "Everything quiet on the digital front. I\u2019m keeping watch! \ud83d\udee1\ufe0f",
  "dpg.nudge.idle.1":
    "Did you know? Over 3.4 billion phishing emails are sent daily. Got a suspicious one? Forward it to me and I\u2019ll check it for phishing red flags!",
  "dpg.nudge.idle.2":
    "I\u2019ve been standing guard. Let me know if anything seems off \u2014 I\u2019m always on duty.",
  "dpg.nudge.idle.3":
    "Quick tip: Never share verification codes with anyone, even if they claim to be from your bank!",
  "dpg.nudge.idle.4":
    "Your digital safety is my #1 priority. Anything I can help you with?",
  "dpg.nudge.idle.5":
    "Fun fact: I can detect over 50 types of scam patterns. Try me!",
  "dpg.nudge.idle.6":
    "Remember to update your passwords regularly. I can guide you through best practices!",
  "dpg.reaction.scam.0":
    "\ud83d\udea8 Heads up! That looks suspicious. Let me break down the red flags I found.",
  "dpg.reaction.scam.1":
    "\u26a0\ufe0f My sensors are tingling! This has several scam indicators. Let\u2019s take a closer look.",
  "dpg.reaction.scam.2":
    "\ud83d\uded1 Hold on \u2014 I\u2019m detecting some serious red flags here. Don\u2019t click anything yet!",
  "dpg.reaction.safe.0":
    "\u2705 All clear! This looks legitimate. Stay vigilant though!",
  "dpg.reaction.safe.1":
    "\ud83d\udc4d Looks safe to me! No red flags detected. Nice work checking!",
  "dpg.reaction.safe.2":
    "\ud83d\udee1\ufe0f Good news \u2014 this appears to be genuine. Smart of you to verify first!",
  "dpg.reaction.returned.0":
    "Welcome back! I\u2019ve been standing guard. \ud83d\udee1\ufe0f",
  "dpg.reaction.returned.1":
    "Hey, you\u2019re back! Everything\u2019s been quiet while you were gone.",
  "dpg.reaction.returned.2":
    "Good to see you again! Ready to help whenever you need me.",
  "dpg.reaction.first.0":
    "Welcome to KinShield! I\u2019m your Digital Personal Guard. Think of me as your personal security expert \u2014 always on duty, always watching out for you. \ud83d\udee1\ufe0f",
  "dpg.reaction.inactive.0":
    "Still here! Just keeping watch. Let me know if you need anything.",
  "dpg.reaction.inactive.1":
    "I haven\u2019t gone anywhere \u2014 your Digital Personal Guard never sleeps!",

  // ── Help Guide Modal UI ─────────────────────────────────────────────────
  "helpGuide.modal.closeGuide": "Close guide",
  "helpGuide.modal.stepLabel": "Step",
  "helpGuide.modal.of": "of",
  "helpGuide.modal.allSet": "You're All Set!",
  "helpGuide.modal.complete": "Complete",
  "helpGuide.modal.back": "Back",
  "helpGuide.modal.next": "Next",
  "helpGuide.modal.finish": "Finish",
  "helpGuide.modal.goThereNow": "Go There Now",
  "helpGuide.modal.copy": " Copy",
  "helpGuide.modal.copied": " Copied!",
  "helpGuide.modal.copyToClipboard": "Copy to clipboard",

  // Help Guide Button
  "helpGuide.button.how": "How?",
  "helpGuide.button.showMeHow": "Show me how",

  // ── Help Guide 1 — Forward a Suspicious Email ────────────────────────────
  "helpGuide.1.title": "How to Forward a Suspicious Email",
  "helpGuide.1.subtitle":
    "Pick your email app below and follow the steps \u2014 your analysis will appear on this page.",
  "helpGuide.1.finalStep":
    "Once you send the email, come back here and <strong>tap Refresh</strong> \u2014 your threat analysis usually appears within 60 seconds.",
  "helpGuide.1.actionLabel": "Got it!",
  "helpGuide.1.p0.name": "Gmail",
  "helpGuide.1.p0.s0.instruction":
    "Open the suspicious email in Gmail. <strong>Don\u2019t click any links</strong> inside it.",
  "helpGuide.1.p0.s0.detail":
    "If you\u2019re on your phone, open the Gmail app. On a computer, go to mail.google.com.",
  "helpGuide.1.p0.s1.instruction":
    "Tap the <strong>\u22ee three-dot menu</strong> (top-right of the email) and select <strong>\u201cForward\u201d</strong>.",
  "helpGuide.1.p0.s1.detail":
    "On mobile, you may need to scroll to the bottom of the email and tap the Forward arrow instead.",
  "helpGuide.1.p0.s2.instruction":
    "In the <strong>\u201cTo\u201d</strong> field, enter:",
  "helpGuide.1.p0.s2.detail":
    "Don\u2019t change the subject line or the email body \u2014 KinShield needs the original content to analyze it accurately.",
  "helpGuide.1.p0.s3.instruction":
    "Tap <strong>Send</strong>. Your analysis will appear on this page within about 60 seconds.",
  "helpGuide.1.p0.s3.detail":
    "You\u2019ll also receive a notification when it\u2019s ready. Tap <strong>Refresh</strong> on this page if you don\u2019t see it right away.",
  "helpGuide.1.p1.name": "Outlook",
  "helpGuide.1.p1.s0.instruction":
    "Open the suspicious email in Outlook. <strong>Don\u2019t click any links</strong> inside it.",
  "helpGuide.1.p1.s0.detail":
    "Works the same in Outlook desktop, web (outlook.com), or the mobile app.",
  "helpGuide.1.p1.s1.instruction":
    "Click the <strong>Forward</strong> button (arrow icon) in the toolbar, or right-click the email and choose <strong>\u201cForward\u201d</strong>.",
  "helpGuide.1.p1.s1.detail":
    "On mobile, tap the reply arrow at the bottom and choose Forward.",
  "helpGuide.1.p1.s2.instruction":
    "In the <strong>\u201cTo\u201d</strong> field, enter:",
  "helpGuide.1.p1.s2.detail":
    "Leave the subject and body unchanged so KinShield receives the original message.",
  "helpGuide.1.p1.s3.instruction":
    "Click <strong>Send</strong>, then come back to this page.",
  "helpGuide.1.p1.s3.detail":
    "Your analysis will appear here within about 60 seconds. Tap Refresh if needed.",
  "helpGuide.1.p2.name": "Apple Mail",
  "helpGuide.1.p2.s0.instruction":
    "Open the suspicious email in the <strong>Mail app</strong> on your iPhone, iPad, or Mac.",
  "helpGuide.1.p2.s0.detail":
    "Don\u2019t tap on any links or download any attachments.",
  "helpGuide.1.p2.s1.instruction":
    "Tap the <strong>Reply/Forward arrow</strong> (\u21a9 icon at the bottom), then select <strong>Forward</strong>.",
  "helpGuide.1.p2.s1.detail":
    "On Mac, you can also go to Message \u2192 Forward in the menu bar, or press \u21e7\u2318F.",
  "helpGuide.1.p2.s2.instruction":
    "In the <strong>\u201cTo\u201d</strong> field, type:",
  "helpGuide.1.p2.s2.detail":
    "Keep the original email content intact \u2014 don\u2019t modify the subject or body.",
  "helpGuide.1.p2.s3.instruction":
    "Tap the <strong>Send</strong> button (\u2191 arrow). Your analysis will appear here shortly.",
  "helpGuide.1.p2.s3.detail":
    "Usually ready within 60 seconds. Tap Refresh on this page if you don\u2019t see it.",
  "helpGuide.1.p3.name": "Yahoo Mail",
  "helpGuide.1.p3.s0.instruction":
    "Open the suspicious email in Yahoo Mail. <strong>Don\u2019t click any links</strong>.",
  "helpGuide.1.p3.s1.instruction":
    "Click the <strong>Forward</strong> button in the toolbar (or the \u201c...\u201d menu \u2192 Forward on mobile).",
  "helpGuide.1.p3.s2.instruction":
    "In the <strong>\u201cTo\u201d</strong> field, enter:",
  "helpGuide.1.p3.s2.detail":
    "Don\u2019t change the subject line or email content.",
  "helpGuide.1.p3.s3.instruction":
    "Click <strong>Send</strong>. Your analysis will appear on this page within about a minute.",

  // ── Help Guide 2 — Forward a Suspicious Text ─────────────────────────────
  "helpGuide.2.title": "How to Forward a Suspicious Text",
  "helpGuide.2.subtitle":
    "Forward the text to KinShield \u2014 your analysis will appear on this page.",
  "helpGuide.2.finalStep":
    "Once you send the text, come back here and <strong>tap Refresh</strong> \u2014 your threat analysis usually appears within 60 seconds.",
  "helpGuide.2.actionLabel": "Got it!",
  "helpGuide.2.p0.name": "iPhone (iOS)",
  "helpGuide.2.p0.s0.instruction":
    "Open the <strong>Messages</strong> app and find the suspicious text.",
  "helpGuide.2.p0.s0.detail": "Don\u2019t tap on any links in the message.",
  "helpGuide.2.p0.s1.instruction":
    "<strong>Long-press</strong> on the message bubble, not on any link that may be there in the message, then tap <strong>More\u2026</strong>",
  "helpGuide.2.p0.s1.detail":
    "You\u2019ll see checkboxes appear next to messages. Make sure the suspicious one is selected.",
  "helpGuide.2.p0.s2.instruction":
    'Tap the <strong>Forward arrow</strong> <img src="/icons/ios-forward-arrow.svg" alt="Forward arrow" style="width:1.2em;height:1.2em;vertical-align:middle;margin:0 0.15em;" /> at the bottom-right.',
  "helpGuide.2.p0.s3.instruction":
    "In the <strong>\u201cTo\u201d</strong> field, enter the KinShield number:",
  "helpGuide.2.p0.s3.detail":
    "The message content will be forwarded automatically.",
  "helpGuide.2.p0.s4.instruction":
    "Tap <strong>Send</strong> (the \u2191 arrow). Your analysis will appear on this page shortly.",
  "helpGuide.2.p0.s4.detail":
    "Usually ready within about a minute. Tap Refresh on this page if you don\u2019t see it.",
  "helpGuide.2.p1.name": "Android",
  "helpGuide.2.p1.s0.instruction":
    "Open your <strong>Messages</strong> app and find the suspicious text.",
  "helpGuide.2.p1.s0.detail": "Don\u2019t tap on any links in the message.",
  "helpGuide.2.p1.s1.instruction":
    "<strong>Long-press</strong> on the message bubble until it\u2019s selected.",
  "helpGuide.2.p1.s1.detail":
    "You may see a highlight or checkmark appear on the message.",
  "helpGuide.2.p1.s2.instruction":
    "Tap the <strong>\u22ee menu</strong> (or the share/forward icon) and choose <strong>\u201cForward\u201d</strong>.",
  "helpGuide.2.p1.s2.detail":
    "The exact wording may vary by phone brand (Samsung, Pixel, etc.) but the option is always there.",
  "helpGuide.2.p1.s3.instruction":
    "In the <strong>\u201cTo\u201d</strong> field, enter:",
  "helpGuide.2.p1.s4.instruction":
    "Tap <strong>Send</strong>, then come back to this page.",
  "helpGuide.2.p1.s4.detail":
    "Your analysis will appear here within about a minute. Tap Refresh if needed.",

  // ── Help Guide 3 — Text Conversation Analysis ────────────────────────────
  "helpGuide.3.title": "How to Analyze a Conversation",
  "helpGuide.3.subtitle":
    "Paste a conversation thread so KinShield can detect manipulation, fraud, and suspicious patterns.",
  "helpGuide.3.finalStep":
    "Paste your conversation into the text area on this page and tap <strong>Analyze</strong> to get started.",
  "helpGuide.3.actionLabel": "Got it!",
  "helpGuide.3.p0.name": "All Devices",
  "helpGuide.3.p0.s0.instruction":
    "Go to your messaging app and <strong>copy the conversation</strong> you want analyzed.",
  "helpGuide.3.p0.s0.detail":
    "Select all the messages in the thread \u2014 the more context KinShield has, the better the analysis. Include both sides of the conversation if possible.",
  "helpGuide.3.p0.s1.instruction":
    "<strong>Paste the conversation</strong> into the text area on this page.",
  "helpGuide.3.p0.s1.detail":
    "You can also type or describe the conversation if copy-paste isn\u2019t possible. Include details like who said what and when.",
  "helpGuide.3.p0.s2.instruction":
    "Tap <strong>\u201cAnalyze\u201d</strong> and wait a moment for your results.",
  "helpGuide.3.p0.s2.detail":
    "KinShield will look for manipulation tactics, fraud patterns, social engineering, and romance scam indicators.",

  // ── Help Guide 4 — Social Media Post / URL Check ─────────────────────────
  "helpGuide.4.title": "How to Check a Social Media Post or Link",
  "helpGuide.4.subtitle":
    "Copy the link from a social media post and paste it below, or take a screenshot for ads without links.",
  "helpGuide.4.finalStep":
    "Paste the link into the URL field on this page and tap <strong>Analyze</strong>. If you can\u2019t copy a link, use <strong>Screenshot Analysis</strong> instead.",
  "helpGuide.4.actionLabel": "Got it!",
  "helpGuide.4.p0.name": "Copy the Link",
  "helpGuide.4.p0.s0.instruction":
    "Find the suspicious post on Facebook, Instagram, X/Twitter, LinkedIn, or any social platform.",
  "helpGuide.4.p0.s0.detail": "Don\u2019t click any links in the post yet.",
  "helpGuide.4.p0.s1.instruction":
    "Tap the <strong>share button</strong> or <strong>\u22ef menu</strong> on the post and choose <strong>\u201cCopy Link\u201d</strong>.",
  "helpGuide.4.p0.s1.detail":
    "On most platforms: tap the share icon or \u22ef \u2192 Copy Link. On X/Twitter: tap the share arrow \u2192 Copy Link.",
  "helpGuide.4.p0.s2.instruction":
    "<strong>Paste the link</strong> into the URL field on this page.",
  "helpGuide.4.p0.s3.instruction":
    "Tap <strong>\u201cAnalyze\u201d</strong> to run the analysis.",
  "helpGuide.4.p0.s3.detail":
    "KinShield will examine the URL destination, the domain reputation, and the content for known scam patterns.",
  "helpGuide.4.p1.name": "Sponsored Ad?",
  "helpGuide.4.p1.s0.instruction":
    "Sponsored ads (marked <strong>\u201cSponsored\u201d</strong>) usually <strong>don\u2019t have a Share button or a copyable link</strong>.",
  "helpGuide.4.p1.s0.detail":
    "Clicking the ad opens pop-ups or lead forms <em>inside</em> the app \u2014 the browser URL stays as facebook.com or linkedin.com. There\u2019s no real URL to copy.",
  "helpGuide.4.p1.s1.instruction":
    "<strong>Don\u2019t fill out any forms or click \u201cDownload\u201d / \u201cContinue\u201d</strong> inside the ad. That\u2019s exactly how scam ads steal your information.",
  "helpGuide.4.p1.s1.detail":
    "Legitimate companies don\u2019t need your personal info just to let you read a report. If it asks for work email, name, and job title before showing content \u2014 be cautious.",
  "helpGuide.4.p1.s2.instruction":
    "<strong>Take screenshots</strong> of the ad and any pop-ups it shows, then use KinShield\u2019s <strong>Screenshot Analysis</strong> tool to check them.",
  "helpGuide.4.p1.s3.instruction":
    "<strong>Verify the company independently:</strong> Tap the company name on the ad to check if it\u2019s a verified page, then search for the company and offer on Google \u2014 not through the ad.",
  "helpGuide.4.p1.s3.detail":
    "If the report or offer is real, you\u2019ll find it on the company\u2019s actual website without giving your info to an ad form.",
  "helpGuide.4.p1.finalStep":
    "Take screenshots of the ad and any forms it shows, then use <strong>Screenshot Analysis</strong> to check them.",
  "helpGuide.4.p1.actionLabel": "Open Screenshot Analysis",
  "helpGuide.4.p2.name": "Screenshot Instead",
  "helpGuide.4.p2.s0.instruction":
    "Can\u2019t copy a link? No problem \u2014 take a screenshot and use KinShield\u2019s <strong>Screenshot Analysis</strong> tool instead.",
  "helpGuide.4.p2.s0.detail":
    "Screenshot Analysis uses AI to detect scam indicators in images \u2014 perfect for ads, pop-ups, or posts you can\u2019t copy a link from.",
  "helpGuide.4.p2.finalStep":
    "Take a screenshot of the suspicious content, then open <strong>Screenshot Analysis</strong> to upload and check it.",
  "helpGuide.4.p2.actionLabel": "Open Screenshot Analysis",

  // ── Help Guide 5 — Phone Number Lookup ──────────────────────────────────
  "helpGuide.5.title": "How to Look Up a Phone Number",
  "helpGuide.5.subtitle":
    "Enter any phone number to see if it\u2019s associated with known scams.",
  "helpGuide.5.finalStep":
    "Enter the phone number in the field on this page and tap <strong>Check Number</strong> to see the results.",
  "helpGuide.5.actionLabel": "Got it!",
  "helpGuide.5.p0.name": "All Devices",
  "helpGuide.5.p0.s0.instruction":
    "Enter the <strong>phone number</strong> in the input field on this page.",
  "helpGuide.5.p0.s0.detail":
    "Include the country code if it\u2019s an international number (e.g., +1 for US/Canada). The number can be from a missed call, text, voicemail, or any source.",
  "helpGuide.5.p0.s1.instruction":
    "Tap <strong>\u201cCheck Number\u201d</strong>.",
  "helpGuide.5.p0.s1.detail":
    "KinShield will search scam databases and provide a risk assessment for the number.",
  "helpGuide.5.p0.s2.instruction":
    "Review the results \u2014 KinShield will show the <strong>risk level</strong>, <strong>carrier info</strong>, and any <strong>reported scam activity</strong>.",
  "helpGuide.5.p0.s2.detail":
    "If the number is flagged, you\u2019ll see details about the type of scam and how many people have reported it.",

  // ── Help Guide 6 — Screenshot / Image Analysis ───────────────────────────
  "helpGuide.6.title": "How to Analyze a Screenshot or Image",
  "helpGuide.6.subtitle":
    "Upload an image of a suspicious message, invoice, pop-up, or QR code and KinShield will scan it.",
  "helpGuide.6.finalStep":
    "Upload your image using the <strong>Upload</strong> button on this page, then tap <strong>Analyze</strong> to get your results.",
  "helpGuide.6.actionLabel": "Got it!",
  "helpGuide.6.p0.name": "iPhone / iPad",
  "helpGuide.6.p0.s0.instruction":
    "Tap the <strong>\u201cTake a Picture or Choose Photo\u201d</strong> area on this page. Your device will offer three options:",
  "helpGuide.6.p0.s0.detail":
    "<strong>Take a Screenshot</strong> \u2014 captures your current screen.<br/><strong>Take Photo</strong> \u2014 opens your camera so you can photograph a letter, invoice, or anything on another screen.<br/><strong>Choose from Photos</strong> \u2014 pick an image already saved in your library.",
  "helpGuide.6.p0.s1.instruction":
    "Select or capture the suspicious content using whichever method works best for your situation.",
  "helpGuide.6.p0.s1.detail":
    "Supported formats: PNG, JPG, HEIC, GIF, WebP, BMP, TIFF, SVG. Max size 10 MB.",
  "helpGuide.6.p0.s2.instruction":
    "Optionally add <strong>Context</strong> to describe the situation, then tap <strong>\u201cAnalyze\u201d</strong>.",
  "helpGuide.6.p0.s2.detail":
    "KinShield AI will examine the image for fake invoices, tech support scams, QR code fraud, and more.",
  "helpGuide.6.p1.name": "Android",
  "helpGuide.6.p1.s0.instruction":
    "Tap the <strong>\u201cTake a Picture or Choose Photo\u201d</strong> area on this page. Your device will offer three options:",
  "helpGuide.6.p1.s0.detail":
    "<strong>Take a Screenshot</strong> \u2014 captures your current screen.<br/><strong>Take Photo</strong> \u2014 opens your camera to photograph a document, letter, or another screen.<br/><strong>Choose from Gallery/Files</strong> \u2014 select an image already saved on your device.",
  "helpGuide.6.p1.s1.instruction":
    "Select or capture the suspicious content using whichever method works best for your situation.",
  "helpGuide.6.p1.s1.detail":
    "Supported formats: PNG, JPG, GIF, WebP, BMP, TIFF, SVG. Max size 10 MB.",
  "helpGuide.6.p1.s2.instruction":
    "Optionally add <strong>Context</strong> to describe the situation, then tap <strong>\u201cAnalyze\u201d</strong>.",
  "helpGuide.6.p1.s2.detail":
    "AI analysis usually takes 10\u201330 seconds depending on the image.",
  "helpGuide.6.p2.name": "Computer",
  "helpGuide.6.p2.s0.instruction":
    "Follow the directions on this page \u2014 you can either <strong>take a screenshot</strong> or <strong>upload an existing image file</strong> from your computer.",
  "helpGuide.6.p2.s0.detail":
    "The page will guide you through uploading and analyzing the image. Supported formats: PNG, JPG. Max size 10 MB.",

  // ── Help Guide 7 — Business / Person Check ───────────────────────────────
  "helpGuide.7.title": "How to Evaluate a Business or Person",
  "helpGuide.7.subtitle":
    "Enter details about someone you\u2019re interacting with to get a risk assessment.",
  "helpGuide.7.finalStep":
    "Enter the details in the form on this page and tap <strong>Analyze</strong> to get your risk assessment.",
  "helpGuide.7.actionLabel": "Got it!",
  "helpGuide.7.p0.name": "All Devices",
  "helpGuide.7.p0.s0.instruction":
    "In the input form on this page, enter what you know: a <strong>business name</strong>, <strong>website</strong>, <strong>person\u2019s name</strong>, or a description of the interaction.",
  "helpGuide.7.p0.s0.detail":
    "At least <strong>one</strong> field in the Business Info section is required \u2014 either a <strong>Business/Person Name</strong> or a <strong>Website/URL</strong> (or both). The more details you provide beyond that, the better the assessment. Include things like: what they\u2019re offering, how they contacted you, any pressure tactics, etc.",
  "helpGuide.7.p0.s1.instruction":
    "Tap <strong>\u201cAnalyze\u201d</strong> to run the evaluation.",
  "helpGuide.7.p0.s1.detail":
    "KinShield checks for red flags like fake businesses, impersonation patterns, and known fraud schemes.",
  "helpGuide.7.p0.s2.instruction":
    "Review the results \u2014 KinShield will show the <strong>risk level</strong> and <strong>specific red flags</strong> found.",
  "helpGuide.7.p0.s2.detail":
    "You can also check your previous analyses in the History panel on the right (or the Recent tab on mobile).",

  // ── Help Guide 8 — Simulation Status / Pause Controls ───────────────────
  "helpGuide.8.title": "Simulation Status & Pause Controls",
  "helpGuide.8.subtitle":
    "Pause or resume your phishing simulation exercises at any time.",
  "helpGuide.8.finalStep":
    "Use the <strong>Simulation Status</strong> section to pause or resume your training exercises whenever you need a break.",
  "helpGuide.8.actionLabel": "Got it!",
  "helpGuide.8.p0.name": "All Devices",
  "helpGuide.8.p0.s0.instruction":
    "Look for the <strong>Simulation Status</strong> section at the top of this page.",
  "helpGuide.8.p0.s0.detail":
    "This shows whether your simulations are currently active or paused, along with any pause schedule details.",
  "helpGuide.8.p0.s1.instruction":
    "Tap <strong>\u201cPause Simulations\u201d</strong> to temporarily stop receiving phishing exercises.",
  "helpGuide.8.p0.s1.detail":
    "You can pause indefinitely, for a set number of days, or until a specific date.",
  "helpGuide.8.p0.s2.instruction":
    "Tap <strong>\u201cResume Simulations\u201d</strong> to start receiving exercises again.",
  "helpGuide.8.p0.s2.detail":
    "Resuming takes effect immediately. Your simulation settings will remain unchanged.",

  // ── Help Guide 11 — Manual Email Analysis ────────────────────────────────
  "helpGuide.11.title": "How to Manually Analyze an Email",
  "helpGuide.11.subtitle":
    "Copy the key details from a suspicious email and paste them into the form on this page.",
  "helpGuide.11.finalStep":
    "Copy the sender address, subject, and body from the suspicious email into the fields on this page, then tap <strong>Analyze Email</strong>.",
  "helpGuide.11.actionLabel": "Got it!",
  "helpGuide.11.p0.name": "All Devices",
  "helpGuide.11.p0.s0.instruction":
    "Open the suspicious email in your email app. <strong>Don\u2019t click any links</strong> inside it.",
  "helpGuide.11.p0.s0.detail":
    "You\u2019ll need to copy three things: the sender\u2019s email address, the subject line, and the email body.",
  "helpGuide.11.p0.s1.instruction":
    "Find the <strong>sender\u2019s email address</strong> (usually shown near the top of the email) and copy it.",
  "helpGuide.11.p0.s1.detail":
    'On mobile, tap the sender\u2019s name to reveal the full email address. It might say something like "John Smith &lt;fakejohn@scamdomain.com&gt;" \u2014 you want the part inside the angle brackets.',
  "helpGuide.11.p0.s2.instruction":
    "Paste the sender\u2019s email address into the <strong>\u201cSender Email Address\u201d</strong> field on this page.",
  "helpGuide.11.p0.s3.instruction":
    "Copy the <strong>subject line</strong> from the email and paste it into the <strong>\u201cEmail Subject\u201d</strong> field.",
  "helpGuide.11.p0.s3.detail":
    "The subject line is usually displayed at the top of the email in bold text.",
  "helpGuide.11.p0.s4.instruction":
    "Select and copy the <strong>entire email body</strong> (the main message content), then paste it into the <strong>\u201cEmail Body\u201d</strong> field.",
  "helpGuide.11.p0.s4.detail":
    "Include everything \u2014 greetings, links, phone numbers, signatures. The more content KinShield has, the better the analysis.",
  "helpGuide.11.p0.s5.instruction":
    "Tap <strong>\u201cAnalyze Email\u201d</strong> and wait a moment for your results.",
  "helpGuide.11.p0.s5.detail":
    "KinShield AI will examine the sender, subject, and body for phishing indicators, scam patterns, and social engineering tactics.",

  // ── Help Guide 12 — Manual Text/SMS Analysis ─────────────────────────────
  "helpGuide.12.title": "How to Manually Analyze a Text Message",
  "helpGuide.12.subtitle":
    "Enter the sender\u2019s phone number and paste the message content into the form on this page.",
  "helpGuide.12.finalStep":
    "Enter the phone number (or short code) and paste the message into the fields on this page, then tap <strong>Analyze Content</strong>.",
  "helpGuide.12.actionLabel": "Got it!",
  "helpGuide.12.p0.name": "All Devices",
  "helpGuide.12.p0.s0.instruction":
    "Open the suspicious text message in your messaging app. <strong>Don\u2019t tap any links</strong> in the message.",
  "helpGuide.12.p0.s1.instruction":
    "Find the <strong>sender\u2019s phone number</strong> or <strong>SMS short code</strong> (shown at the top of the conversation).",
  "helpGuide.12.p0.s1.detail":
    "If the sender shows a name instead of a number, tap on it to reveal the actual phone number. Short codes are typically 5\u20136 digit numbers like 33513.",
  "helpGuide.12.p0.s2.instruction":
    "Enter the phone number into the <strong>\u201cPhone Number\u201d</strong> field on this page \u2014 or if it\u2019s a short code, use the <strong>\u201cSMS Short Code\u201d</strong> field instead.",
  "helpGuide.12.p0.s2.detail":
    "Include the country code for phone numbers (e.g., +1 for US/Canada). You only need to fill in one \u2014 phone number OR short code.",
  "helpGuide.12.p0.s3.instruction":
    "Go back to the message, <strong>copy the message text</strong>, and paste it into the <strong>\u201cMessage Content\u201d</strong> field.",
  "helpGuide.12.p0.s3.detail":
    "Long-press on the message bubble to select and copy it. Include the full message \u2014 links, phone numbers, and all.",
  "helpGuide.12.p0.s4.instruction":
    "Tap <strong>\u201cAnalyze Content\u201d</strong> and wait a moment for your results.",
  "helpGuide.12.p0.s4.detail":
    "KinShield will check the phone number against scam databases and analyze the message text for fraud patterns.",

  // ── Help Guide 13 — Verify Caller Identity ───────────────────────────────
  "helpGuide.13.title": "How to Verify Caller Identity",
  "helpGuide.13.subtitle":
    "Never rely on what a caller tells you \u2014 always verify through an independent source you trust.",
  "helpGuide.13.finalStep":
    "Always <strong>hang up</strong> and find the real number yourself through an independent, trusted source.",
  "helpGuide.13.actionLabel": "Got it!",
  "helpGuide.13.p0.name": "All Devices",
  "helpGuide.13.p0.s0.instruction":
    "Search for the organization\u2019s <strong>official website</strong> and call the number listed there.",
  "helpGuide.13.p0.s0.detail":
    "Type the company or agency name directly into your browser \u2014 don\u2019t click links from emails, texts, or pop-ups. Be cautious of the top search results, as scammers can pay for <strong>sponsored ads</strong> that mimic official sites. Look for non-sponsored, organic search results instead.",
  "helpGuide.13.p0.s1.instruction":
    "Use the phone number printed on the <strong>back of your credit or debit card</strong>.",
  "helpGuide.13.p0.s1.detail":
    "This is the safest way to reach your bank or card issuer. Scammers cannot change what\u2019s physically printed on your card.",
  "helpGuide.13.p0.s2.instruction":
    "Look up the number on <strong>official physical documents</strong> \u2014 bills, statements, letters, or insurance cards.",
  "helpGuide.13.p0.s2.detail":
    "Government agencies and businesses print their real contact numbers on the correspondence they mail to you.",
  "helpGuide.13.p0.s3.instruction":
    "<strong>Never</strong> trust the phone number displayed on your screen from a pop-up, text message, or email.",
  "helpGuide.13.p0.s3.detail":
    "Scammers can spoof caller ID and create fake pop-ups that display fraudulent support numbers.",
  "helpGuide.13.p0.s4.instruction":
    "<strong>Never</strong> use a phone number the caller gives you \u2014 they can route it right back to themselves.",
  "helpGuide.13.p0.s4.detail":
    "If someone asks you to call them back at a number they provide, hang up and find the real number yourself.",

  // ── Help Guide 14 — Configure Simulation Settings ────────────────────────
  "helpGuide.14.title": "How to Configure Your Simulation Settings",
  "helpGuide.14.subtitle":
    "Choose a preset profile or create a custom configuration to match your training goals.",
  "helpGuide.14.finalStep":
    "Choose a preset from the cards, or select <strong>Custom</strong> to fine-tune each parameter. Don\u2019t forget to save your changes!",
  "helpGuide.14.actionLabel": "Got it!",
  "helpGuide.14.p0.name": "All Devices",
  "helpGuide.14.p0.s0.instruction":
    "Start by selecting a <strong>preset profile</strong> \u2014 Gentle, Moderate, or Challenging.",
  "helpGuide.14.p0.s0.detail":
    "Each preset automatically configures difficulty, frequency, channels, timing, and context for you. Tap a card to select it.",
  "helpGuide.14.p0.s1.instruction":
    "Review your settings in the <strong>Simulation Settings</strong> panel.",
  "helpGuide.14.p0.s1.detail":
    "After selecting a preset, you\u2019ll see all configured parameters displayed as badges \u2014 difficulty tier, frequency, communication channels, timing, and context.",
  "helpGuide.14.p0.s2.instruction":
    "Select <strong>Custom</strong> to manually configure every parameter.",
  "helpGuide.14.p0.s2.detail":
    "In Custom mode, tap any badge to toggle channels, change timing, adjust difficulty or frequency tiers, and select context categories. Unselected options appear with dashed borders. Requires an Extended Family subscription.",
  "helpGuide.14.p0.s3.instruction":
    "Tap <strong>\u201cUse [Preset] Preset\u201d</strong> or <strong>\u201cUse Custom Settings\u201d</strong> to save.",
  "helpGuide.14.p0.s3.detail":
    "Your changes won\u2019t take effect until you save. The Active badge appears next to the profile name when your current view matches the saved settings.",

  // ── Help Guide 15 — Manage a Member User ─────────────────────────────────
  "helpGuide.15.title": "How to Manage a Member User",
  "helpGuide.15.subtitle":
    "Select a user from the list and use the navigation menu to manage their account.",
  "helpGuide.15.finalStep":
    "Start by selecting a user card below, then use the navigation menu on the left to manage their account.",
  "helpGuide.15.actionLabel": "Got it!",
  "helpGuide.15.p0.name": "All Devices",
  "helpGuide.15.p0.s0.instruction":
    "Click on a <strong>user card</strong> in the list below to select them.",
  "helpGuide.15.p0.s0.detail":
    "A checkbox will appear on the selected card. You can only manage one user at a time.",
  "helpGuide.15.p0.s1.instruction":
    "Navigate to <strong>Update User Info</strong> in the side menu to edit their basic information.",
  "helpGuide.15.p0.s1.detail":
    "Here you can change the user\u2019s name, email address, or phone number. Email changes will trigger a verification email.",
  "helpGuide.15.p0.s2.instruction":
    "Go to <strong>Add Additional Details</strong> to manage extra profile information.",
  "helpGuide.15.p0.s2.detail":
    "Additional details like location, age, and interests are used to personalize the user\u2019s simulation experience.",
  "helpGuide.15.p0.s3.instruction":
    "Visit <strong>Simulation Profile Settings</strong> (under the Simulations section in the main menu) to configure their training profile.",
  "helpGuide.15.p0.s3.detail":
    "There you can set sophistication level, communication channels, contact frequency, and scenario context for the selected user.",

  // ── Contact Support page ───────────────────────────────────────────
  "contactSupport.title": "Contact Support",
  "contactSupport.subtitle":
    "We're here to help! Choose the best way to reach us or submit a support ticket below.",

  // Contact method cards
  "contactSupport.email.title": "Email Support",
  "contactSupport.email.info": "support@kinprotect.com",
  "contactSupport.email.description":
    "Send us an email anytime. We typically respond within 24 hours.",
  "contactSupport.email.button": "Send Email",

  "contactSupport.phone.title": "Phone Support",
  "contactSupport.phone.info": "1-301-237-7843",
  "contactSupport.phone.description":
    "Speak directly with our support team during business hours.",
  "contactSupport.phone.button": "Call Now",

  "contactSupport.chat.title": "Live Chat",
  "contactSupport.chat.info": "Available during business hours",
  "contactSupport.chat.description":
    "Get instant answers to your questions with our live chat support.",

  "contactSupport.helpCenter.title": "Help Center",
  "contactSupport.helpCenter.info": "Self-service resources",
  "contactSupport.helpCenter.description":
    "Browse our comprehensive knowledge base and FAQs for quick answers.",

  // Support hours
  "contactSupport.hours.title": "Support Hours",
  "contactSupport.hours.monFri": "Monday - Friday:",
  "contactSupport.hours.monFriTime": "8:00 AM - 8:00 PM EST",
  "contactSupport.hours.saturday": "Saturday:",
  "contactSupport.hours.saturdayTime": "10:00 AM - 6:00 PM EST",
  "contactSupport.hours.sunday": "Sunday:",
  "contactSupport.hours.sundayClosed": "Closed",
  "contactSupport.hours.note":
    "Email support is available 24/7 with responses within 24 hours",

  // Support ticket form
  "contactSupport.ticket.title": "Submit a Support Ticket",
  "contactSupport.ticket.name": "Name",
  "contactSupport.ticket.email": "Email",
  "contactSupport.ticket.priority": "Priority",
  "contactSupport.ticket.priority.low": "Low",
  "contactSupport.ticket.priority.medium": "Medium",
  "contactSupport.ticket.priority.high": "High",
  "contactSupport.ticket.priority.critical": "Critical",
  "contactSupport.ticket.message": "Message",
  "contactSupport.ticket.namePlaceholder": "Your name",
  "contactSupport.ticket.emailPlaceholder": "your@email.com",
  "contactSupport.ticket.messagePlaceholder": "Describe your issue...",
  "contactSupport.ticket.submit": "Submit",

  // Validation messages
  "contactSupport.validation.nameRequired": "Please enter your name.",
  "contactSupport.validation.nameTooLong":
    "Name must not exceed 100 characters.",
  "contactSupport.validation.emailRequired": "Please enter your email address.",
  "contactSupport.validation.emailInvalid":
    "Please enter a valid email address.",
  "contactSupport.validation.messageRequired": "Please enter your message.",
  "contactSupport.validation.messageTooLong":
    "Message must not exceed 5000 characters.",

  // Status messages
  "contactSupport.success.message":
    "Thank you for contacting us! We've received your message and will respond within 24 hours.",
  "contactSupport.error.submit":
    "An error occurred while submitting your request. Please try again or email us directly at support@kinprotect.com",

  // Additional resources
  "contactSupport.resources.title": "Before You Contact Us",
  "contactSupport.resources.account.title": "Check Your Account Status",
  "contactSupport.resources.account.desc":
    "Many common issues can be resolved by checking your account settings and subscription status.",
  "contactSupport.resources.alerts.title": "Review Recent Alerts",
  "contactSupport.resources.alerts.desc":
    "If you're experiencing issues with the service, check if there are any system-wide alerts or maintenance notices.",
  "contactSupport.resources.troubleshoot.title": "Try Basic Troubleshooting",
  "contactSupport.resources.troubleshoot.desc":
    "Clear your browser cache, try a different browser, or check your internet connection before contacting support.",
  "contactSupport.resources.prepare.title": "Prepare Account Information",
  "contactSupport.resources.prepare.desc":
    "Have your account email and any relevant error messages ready to help us assist you faster.",

  // ── Contact Us Modal (ContactUsModal.js) ─────────────────────────────────
  "contactUs.toast.nameRequired": "Please enter your name",
  "contactUs.toast.nameTooLong": "Name must not exceed 100 characters",
  "contactUs.toast.emailRequired": "Please enter your email",
  "contactUs.toast.emailInvalid": "Please enter a valid email address",
  "contactUs.toast.emailTooLong": "Email must not exceed 255 characters",
  "contactUs.toast.subjectRequired": "Please enter a subject",
  "contactUs.toast.subjectTooLong": "Subject must not exceed 200 characters",
  "contactUs.toast.messageRequired": "Please enter your message",
  "contactUs.toast.messageTooLong": "Message must not exceed 5000 characters",
  "contactUs.toast.success":
    "Thank you for contacting us! We\u2019ll get back to you soon.",

  // ── Side Panel (SidePanel.js) ───────────────────────────────────────────────
  "sidePanel.toast.verificationEmailFailed":
    "Failed to send verification email. Please try again.",
  "sidePanel.toast.adminDashboardFailed":
    "Failed to access Admin Dashboard. Please try again.",
  "sidePanel.toast.generateBlogsFailed":
    "Failed to access Generate Blogs. Please try again.",

  // ── Shield Reports (ShieldReports.js, ShieldReportDetail.js) ────────────────
  "shieldReports.toast.reportGenerated": "New report generated successfully!",
  "shieldReportDetail.toast.emailSent": "Report email sent successfully!",
  "shieldReportDetail.toast.emailFailed":
    "Failed to send email. Please try again.",

  // ── Image Analysis (ImageAnalysisSimple.js) ────────────────────────────
  "imageAnalysis.toast.uploadSuccess": "Upload successful!",
  "imageAnalysis.toast.checksComplete": "Security Checks complete!",
  "imageAnalysis.toast.historyRefreshed": "Analysis history refreshed!",
  "imageAnalysis.toast.refreshFailed": "Failed to refresh: {error}",

  // ── URL Safety Analysis (URLSafetyAnalysis.js) ─────────────────────────
  "urlSafety.toast.enterUrl": "Please enter a URL to analyze",
  "urlSafety.toast.loginRequired": "You must be logged in to analyze URLs",
  "urlSafety.toast.safe": "URL appears to be safe",
  "urlSafety.toast.dangerous": "Warning: This URL may be dangerous",
  "urlSafety.toast.failed": "Failed to analyze URL",

  // ── Simulation Heuristics (SimulationHeuristics.js) ─────────────────────
  "simulation.toast.emailSent": "Message sent successfully to {email}",
  "simulation.toast.emailFailed": "Failed to send message: {error}",
  "simulation.toast.smsSent": "SMS sent successfully to {phone}",
  "simulation.toast.smsFailed": "Failed to send SMS: {error}",
  "simulation.toast.error": "Error: {error}",

  // ── Simulation Profile (useSimulationProfileState.js) ────────────────────
  "simProfile.toast.profileSaved": "Simulation profile saved successfully!",
  "simProfile.toast.profileSaveFailed": "Failed to save simulation profile.",
  "simProfile.toast.profileSaveError":
    "An error occurred while saving the simulation profile.",
  "simProfile.toast.noUserSelected": "No user selected",
  "simProfile.toast.scheduleResumed":
    "Simulation schedule resumed successfully",
  "simProfile.toast.resumeFailed":
    "Failed to resume schedule. Please try again.",
  "simProfile.toast.schedulePaused": "Simulation schedule paused successfully",
  "simProfile.toast.pauseFailed": "Failed to pause schedule. Please try again.",

  // ── Admin: Scam Reports (AdminScamReports.js) ────────────────────────
  "adminScamReports.toast.loadFailed": "Failed to load scam reports",
  "adminScamReports.toast.deleted": "Report #{id} deleted",
  "adminScamReports.toast.deleteFailed": "Failed to delete report",

  // ── Admin: User Sessions (UserSessionsAdmin.js) ──────────────────────
  "userSessions.toast.sessionLoadFailed": "Failed to load session data",
  "userSessions.toast.activeUsersLoadFailed": "Failed to load active users",

  // ── Admin: System Errors (SystemErrors.js) ───────────────────────────
  "systemErrors.toast.summaryLoadFailed": "Failed to load error summary",
  "systemErrors.toast.listLoadFailed": "Failed to load error list",
  "systemErrors.toast.dashboardRefreshed": "Dashboard refreshed",
  "systemErrors.toast.detailLoadFailed": "Failed to load error details",
  "systemErrors.toast.acknowledged": "Error acknowledged",
  "systemErrors.toast.acknowledgeFailed": "Failed to acknowledge error",
  "systemErrors.toast.resolved": "Error resolved",
  "systemErrors.toast.resolveFailed": "Failed to resolve error",

  // ── Admin: Promotions (PromotionsAdmin.js) ────────────────────────────
  "promotionsAdmin.toast.synced": "Synced {total} items from Stripe",

  // ── Admin: FCC Cache (FccCacheAdmin.js) ────────────────────────────────
  "fccCache.toast.loadFailed": "Failed to load FCC cache statistics",
  "fccCache.toast.purged": "Purged {count} stale cache entries",

  // ── Admin: Demo Users (DemoAdminPanel.js) ─────────────────────────────
  "demoAdmin.toast.loadFailed": "Failed to load demo users",
  "demoAdmin.toast.approved": "Demo user approved",
  "demoAdmin.toast.approveFailed": "Failed to approve user",
  "demoAdmin.toast.revoked": "Demo user revoked",
  "demoAdmin.toast.revokeFailed": "Failed to revoke user",
  "demoAdmin.toast.usageReset": "Usage counter reset to 0",
  "demoAdmin.toast.usageResetFailed": "Failed to reset usage",
  "demoAdmin.toast.deleted": "Demo user deleted",
  "demoAdmin.toast.deleteFailed": "Failed to delete user",
  "demoAdmin.toast.noUsersSelected": "No users selected",
  "demoAdmin.toast.bulkApprovalFailed": "Bulk approval failed",

  // ── Admin: Full Access Demo (FullAccessDemoAdmin.js) ────────────────────
  "fullAccessDemo.toast.loadFailed": "Failed to load event demo seats",
  "fullAccessDemo.toast.deactivated": "Seat deactivated",
  "fullAccessDemo.toast.deactivateFailed": "Failed to deactivate seat",
  "fullAccessDemo.toast.reactivated": "Seat reactivated",
  "fullAccessDemo.toast.reactivateFailed": "Failed to reactivate seat",
  "fullAccessDemo.toast.selectNewExpiry": "Select a new expiry date/time",
  "fullAccessDemo.toast.extended": "Seat extended",
  "fullAccessDemo.toast.extendFailed": "Failed to extend seat",
  "fullAccessDemo.toast.passwordUpdated": "Password updated",
  "fullAccessDemo.toast.passwordFailed": "Failed to reset password",
  "fullAccessDemo.toast.expiryRequired": "Expiry date/time is required",
  "fullAccessDemo.toast.expiryAfterStart": "Expiry must be after start",
  "fullAccessDemo.toast.createFailed": "Failed to create demo user",

  // Test Users admin panel (browser extension reviewer, future test users)
  "Test Users": "Test Users",
  "testUsers.toast.loadFailed": "Failed to load test users",
  "testUsers.toast.deactivated": "Test user deactivated",
  "testUsers.toast.deactivateFailed": "Failed to deactivate test user",
  "testUsers.toast.reactivated": "Test user reactivated",
  "testUsers.toast.reactivateFailed": "Failed to reactivate test user",
  "testUsers.toast.selectExpiry": "Select a new expiry date/time",
  "testUsers.toast.extended": "Test user extended",
  "testUsers.toast.extendFailed": "Failed to extend test user",
  "testUsers.toast.passwordUpdated": "Password updated",
  "testUsers.toast.passwordResetFailed": "Failed to reset password",
  "testUsers.toast.expiryRequired": "Expiry date/time is required",
  "testUsers.toast.expiryAfterStart": "Expiry must be after start",
  "testUsers.toast.createFailed": "Failed to create test user",
  "testUsers.toast.deleted": "Test user permanently deleted",
  "testUsers.toast.deleteFailed": "Failed to delete test user",
};

export default en;
